import { User, Tenant, Recipe, InventoryItem, Supply, StockReport, OrderList, OrderListItem, Shift, RoutineDefinition, RoutineAssignment, TrainingProgram, Category, MeasurementUnit, BusinessProfile, Lead, SubscriptionSummary, ChargeSummary, UpcomingInvoice, PaymentEvent, TenantStatusInfo, SupportTicket, TicketMessage, PricePlan, TenantSummary, StaffNotification } from '../types';
import { FleetRegisterLinkResponseSchema, type FleetRegisterLinkResponse } from '@platform/shared-types';
import type { SocialAccount, SocialChannel, SocialChannelsResponse, SocialConnectionView, SocialPolicyResponse, SocialPost, SocialAppKeysResponse, SocialAppKeysProviderView, SocialAppKeysTestResponse } from '@platform/shared-types';
import { db, DEMO_TENANT_ID } from './mockDb';
import { loadingService } from './loadingService';
import { cognitoLogin, cognitoCompleteNewPassword, cognitoSendMFACode, cognitoGetUserAttributes } from './cognitoService';
import { visiblePackages } from './packageTiers';

declare const process: { env: Record<string, string | undefined> } | undefined;

// ---------------------------------------------------------------------------
// CONFIGURATION
// ---------------------------------------------------------------------------

// Determine stage from hostname so we can default to per-service domains after retiring the monolith API Gateway
const detectStage = (): 'prod' | 'staging' | 'dev' => {
    // Check for explicit stage override (set at build time for native apps)
    try {
        const viteStage = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_STAGE : '';
        if (viteStage === 'prod' || viteStage === 'staging' || viteStage === 'dev') return viteStage;
    } catch (_) { /* ignore */ }

    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const protocol = typeof window !== 'undefined' ? window.location.protocol : '';

    // Capacitor/native apps: Multiple ways to detect native app environment
    const capacitorObj = (window as any).Capacitor;
    const isCapacitorProtocol = protocol === 'capacitor:';
    const isCapacitorObj = !!capacitorObj;
    const isNativePlatform = capacitorObj?.isNativePlatform?.() === true;
    const platformIsNative = ['ios', 'android'].includes(capacitorObj?.getPlatform?.() || '');

    // Also check if hostname is empty or localhost with Capacitor present (common in native apps)
    const isLikelyNative = isCapacitorObj && (hostname === '' || hostname === 'localhost');

    const isCapacitor = isCapacitorProtocol || isNativePlatform || platformIsNative || isLikelyNative;

    // Debug logging for iOS troubleshooting
    console.log('[API] Stage detection:', {
        hostname, protocol, isCapacitor,
        isCapacitorProtocol, isCapacitorObj, isNativePlatform, platformIsNative, isLikelyNative,
        platform: capacitorObj?.getPlatform?.()
    });

    if (isCapacitor) {
        // Native apps should use production by default
        console.log('[API] Detected Capacitor native app - using prod stage');
        return 'prod';
    }

    if (hostname.includes('dev.')) return 'dev';
    if (hostname.includes('staging.')) return 'staging';
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'dev';
    return 'prod';
};

// Export for debugging
export const getDebugInfo = () => {
    const stage = detectStage();
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const protocol = typeof window !== 'undefined' ? window.location.protocol : '';
    const capacitorObj = (window as any).Capacitor;
    return {
        stage,
        hostname,
        protocol,
        isCapacitor: !!capacitorObj,
        platform: capacitorObj?.getPlatform?.() || 'web',
        routinesApi: serviceDomain('routines', stage),
        uploadsApi: serviceDomain('uploads', stage),
    };
};

// ---------------------------------------------------------------------------
// MULTI-PRODUCT API DOMAIN CONFIGURATION
// ---------------------------------------------------------------------------
// Product config is injected at build time by Vite from buildProductConfig.ts
declare const __PRODUCT_CONFIG__: { id: string; domain: { api: string; apiDev: string } } | undefined;

type ProductId = 'cafemaster' | 'agenticpos' | 'agenticrealestate';

// Get current product ID.
// super-admin-ui serves ONE bundle across 3 product CloudFronts, so hostname
// detection wins — otherwise every non-special service would hit cafemaster APIs.
const getCurrentProductId = (): ProductId => {
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname.toLowerCase();
        if (hostname.includes('rubytill')) return 'agenticpos';
        if (hostname.includes('terrakron')) return 'agenticrealestate';
        if (hostname.includes('cafemenumaster') || hostname.includes('coffeemasterpro')) return 'cafemaster';
    }
    if (typeof __PRODUCT_CONFIG__ !== 'undefined' && __PRODUCT_CONFIG__?.id) {
        return __PRODUCT_CONFIG__.id as ProductId;
    }
    return 'cafemaster';
};

// API domain patterns per product and stage
const apiDomainPatterns: Record<ProductId, Record<'prod' | 'staging' | 'dev', string>> = {
    cafemaster: {
        prod: 'coffeemasterpro.com',
        staging: 'staging.coffeemasterpro.com',
        dev: 'dev.coffeemasterpro.com'
    },
    agenticpos: {
        prod: 'rubytill.com',
        staging: 'stage.rubytill.com',
        dev: 'dev.rubytill.com'
    },
    agenticrealestate: {
        prod: 'terrakron.com',
        staging: 'stage.terrakron.com',
        dev: 'dev.terrakron.com'
    }
};

const serviceDomain = (serviceKey: string, stage: 'prod' | 'staging' | 'dev'): string => {
    const productId = getCurrentProductId();
    const baseDomain = apiDomainPatterns[productId][stage];

    // For prod, use {service}.api.{domain}
    // For dev/staging, use {service}.api.{baseDomain}
    if (stage === 'prod') {
        return `https://${serviceKey}.api.${baseDomain}`;
    }
    return `https://${serviceKey}.api.${baseDomain}`;
};

// Determine API domain based on current hostname; defaults to accounts service to keep legacy fallbacks working
const getApiDomain = (): string => serviceDomain('accounts', detectStage());

const API_BASE_RESOLVED = (() => {
    // Check Vite environment variable first
    try {
        const viteBase = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_API_BASE : '';
        if (viteBase) return viteBase;
    } catch (_) { /* ignore */ }

    // Check process environment variable
    const envBase = (typeof process !== 'undefined' && process.env)
        ? (process.env.REACT_APP_API_BASE || process.env.API_BASE)
        : '';
    if (envBase) return envBase;

    // Auto-detect from hostname
    return getApiDomain();
})();

export const API_BASE = API_BASE_RESOLVED;

// Resolve per-service API base by environment override or by explicit stage-domain mapping
const resolveServiceBase = (serviceKey: string): string => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

    // Local development: use Vite proxy paths to avoid CORS issues
    // Each service gets its own proxy path that maps to its API Gateway URL
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        const proxyPaths: Record<string, string> = {
            'leads': '/leads-api',
            'tenants': '/tenants-api',
            'affiliates': '/affiliates-api',
            'devops': '/devops-api',
            'auth': '/auth-api',
            'users': '/users-api',
            'email': '/email-api',
            'media': '/media-api',
            'uploads': '/uploads-api',
            'creator': '/creator-api',
            'inventory': '/inventory-api',
            'scheduling': '/scheduling-api',
            'routines': '/routines-api',
            'training': '/training-api',
            'recipes': '/recipes-api',
            'location-billing': '/location-billing-api',
            'locations': '/locations-api',
            'pos-auth': '/pos-auth-api',
            'accounts': '/api',
        };
        if (proxyPaths[serviceKey]) {
            console.log(`[API] Localhost detected - using ${proxyPaths[serviceKey]} proxy path for ${serviceKey}`);
            return proxyPaths[serviceKey];
        }
    }

    const upper = serviceKey.toUpperCase();
    const envCandidates: Array<() => string | undefined> = [
        () => {
            try {
                return (typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.[`VITE_${upper}_API_BASE`] : undefined) ||
                    (typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.[`VITE_${upper}`] : undefined);
            } catch (_) {
                return undefined;
            }
        },
        () => (typeof process !== 'undefined' && process?.env)
            ? (process.env[`VITE_${upper}_API_BASE`] || process.env[`REACT_APP_${upper}_API_BASE`] || process.env[`${upper}_API_BASE`])
            : undefined,
    ];

    for (const getter of envCandidates) {
        const val = getter();
        if (val) return val;
    }

    // Default to per-service domains by stage (e.g., https://accounts.api.dev.coffeemasterpro.com)
    const stage = detectStage();
    return serviceDomain(serviceKey, stage);
};

const AUTH_API_BASE = resolveServiceBase('auth');
const ACCOUNTS_API_BASE = resolveServiceBase('accounts');
const LEADS_API_BASE = resolveServiceBase('leads');
const EMAIL_API_BASE = resolveServiceBase('email');
const RECIPES_API_BASE = resolveServiceBase('recipes');
const INVENTORY_API_BASE = resolveServiceBase('inventory');
const SCHEDULING_API_BASE = resolveServiceBase('scheduling');

// New microservices (split from accounts-service)
const USERS_API_BASE = resolveServiceBase('users');
const TENANTS_API_BASE = resolveServiceBase('tenants');
const ROUTINES_API_BASE = resolveServiceBase('routines');
const TRAINING_API_BASE = resolveServiceBase('training');
const UPLOADS_API_BASE = resolveServiceBase('uploads');
const CREATOR_API_BASE = resolveServiceBase('creator');
const AFFILIATES_API_BASE = resolveServiceBase('affiliates');
const MEDIA_API_BASE = resolveServiceBase('media');

// Runtime resolver for affiliates API - always checks hostname at call time
const getAffiliatesApiBase = (): string => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        console.log('[API] Runtime: Using /affiliates-api proxy for localhost');
        return '/affiliates-api';
    }
    return AFFILIATES_API_BASE;
};

// Determine Super Admin API domain based on current hostname
const getSuperAdminApiDomain = (): string => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

    // Multi-product detection by hostname base domain
    // Pattern: admin.api.<root> — use api subdomain to avoid conflict with UI on admin.<root>
    const isRubytill = hostname.includes('rubytill');
    const isTerrakron = hostname.includes('terrakron');
    const isDev = hostname.includes('dev.') || hostname.includes('stage.');

    // VITE_STAGE takes priority (set at build time for native apps)
    try {
        const viteStage = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_STAGE : '';
        if (viteStage === 'prod') {
            if (isRubytill) return 'https://admin.api.rubytill.com';
            if (isTerrakron) return 'https://admin.api.terrakron.com';
            return 'https://admin.coffeemasterpro.com';
        }
        if (viteStage === 'dev') {
            if (isRubytill) return 'https://admin.api.dev.rubytill.com';
            if (isTerrakron) return 'https://admin.api.dev.terrakron.com';
            return 'https://admin.dev.coffeemasterpro.com';
        }
    } catch (_) { /* ignore */ }

    // Check for Capacitor/native app (only if no explicit VITE_STAGE)
    const capacitorObj = (window as any).Capacitor;
    const isCapacitorNative = capacitorObj?.isNativePlatform?.() === true ||
        ['ios', 'android'].includes(capacitorObj?.getPlatform?.() || '');

    if (isCapacitorNative) {
        return 'https://admin.coffeemasterpro.com';
    }

    // Rubytill (agenticpos)
    if (isRubytill) {
        if (isDev) return 'https://admin.api.dev.rubytill.com';
        return 'https://admin.api.rubytill.com';
    }
    // Terrakron (agenticrealestate)
    if (isTerrakron) {
        if (isDev) return 'https://admin.api.dev.terrakron.com';
        return 'https://admin.api.terrakron.com';
    }

    // Cafemaster: coffeemasterpro.com or cafemenumaster.com → admin.coffeemasterpro.com
    if (hostname === 'coffeemasterpro.com' || hostname === 'www.coffeemasterpro.com' ||
        hostname === 'cafemenumaster.com' || hostname === 'www.cafemenumaster.com') {
        return 'https://admin.coffeemasterpro.com';
    }
    if (hostname.includes('dev.')) {
        return 'https://admin.dev.coffeemasterpro.com';
    }
    if (hostname.includes('staging.coffeemasterpro.com')) {
        return 'https://admin.staging.coffeemasterpro.com';
    }

    // Local development: use Vite proxy
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return '/api';
    }

    return 'https://admin.coffeemasterpro.com';
};

const SUPERADMIN_API_BASE_RESOLVED = (() => {
    // Check Vite environment variable first
    try {
        const viteBase = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_SUPERADMIN_API_BASE : '';
        if (viteBase) return viteBase;
    } catch (_) { /* ignore */ }

    // Check process environment variable
    const envBase = (typeof process !== 'undefined' && process.env)
        ? (process.env.REACT_APP_SUPERADMIN_API_BASE || process.env.SUPERADMIN_API_BASE)
        : '';
    if (envBase) return envBase;

    // Auto-detect from hostname
    return getSuperAdminApiDomain();
})();

export const SUPERADMIN_API_BASE = SUPERADMIN_API_BASE_RESOLVED;

// Super Admin Extension API (overflow functions like support tickets)
// Uses custom domains following *.api.{stage}.coffeemasterpro.com pattern
const getSuperAdminExtApiDomain = (): string => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isRubytill = hostname.includes('rubytill');
    const isTerrakron = hostname.includes('terrakron');
    const isDev = hostname.includes('dev.') || hostname.includes('stage.');

    try {
        const viteStage = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_STAGE : '';
        if (viteStage === 'prod') {
            if (isRubytill) return 'https://support.api.rubytill.com';
            if (isTerrakron) return 'https://support.api.terrakron.com';
            return 'https://support.api.coffeemasterpro.com';
        }
        if (viteStage === 'dev') {
            if (isRubytill) return 'https://support.api.dev.rubytill.com';
            if (isTerrakron) return 'https://support.api.dev.terrakron.com';
            return 'https://support.api.dev.coffeemasterpro.com';
        }
    } catch (_) { /* ignore */ }

    const capacitorObj = (window as any).Capacitor;
    const isCapacitorNative = capacitorObj?.isNativePlatform?.() === true ||
        ['ios', 'android'].includes(capacitorObj?.getPlatform?.() || '');
    if (isCapacitorNative) return 'https://support.api.coffeemasterpro.com';

    if (isRubytill) return isDev ? 'https://support.api.dev.rubytill.com' : 'https://support.api.rubytill.com';
    if (isTerrakron) return isDev ? 'https://support.api.dev.terrakron.com' : 'https://support.api.terrakron.com';

    if (hostname === 'coffeemasterpro.com' || hostname === 'www.coffeemasterpro.com' ||
        hostname === 'cafemenumaster.com' || hostname === 'www.cafemenumaster.com') {
        return 'https://support.api.coffeemasterpro.com';
    }
    if (hostname === 'localhost' || hostname === '127.0.0.1') return '/support-api';
    if (hostname.includes('dev.')) return 'https://support.api.dev.coffeemasterpro.com';
    if (hostname.includes('staging.coffeemasterpro.com')) return 'https://support.api.stage.coffeemasterpro.com';

    return 'https://support.api.coffeemasterpro.com';
};

const SUPERADMIN_EXT_API_BASE = (() => {
    // Check Vite environment variable first
    try {
        const viteBase = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_SUPERADMIN_EXT_API_BASE : '';
        if (viteBase) return viteBase;
    } catch (_) { /* ignore */ }
    
    // Check process environment variable
    const envBase = (typeof process !== 'undefined' && process.env)
        ? (process.env.SUPERADMIN_EXT_API_BASE)
        : '';
    if (envBase) return envBase;
    
    // Auto-detect from hostname
    return getSuperAdminExtApiDomain();
})();

// Super Admin Extension 2 API (POS config, registers, gateways, EMV)
const getSuperAdminExt2ApiDomain = (): string => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isRubytill = hostname.includes('rubytill');
    const isTerrakron = hostname.includes('terrakron');
    const isDev = hostname.includes('dev.') || hostname.includes('stage.');

    try {
        const viteStage = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_STAGE : '';
        if (viteStage === 'prod') {
            if (isRubytill) return 'https://super-admin-ext2.api.rubytill.com';
            if (isTerrakron) return 'https://super-admin-ext2.api.terrakron.com';
            return 'https://super-admin-ext2.api.coffeemasterpro.com';
        }
        if (viteStage === 'dev') {
            if (isRubytill) return 'https://super-admin-ext2.api.dev.rubytill.com';
            if (isTerrakron) return 'https://super-admin-ext2.api.dev.terrakron.com';
            return 'https://super-admin-ext2.api.dev.coffeemasterpro.com';
        }
    } catch (_) { /* ignore */ }

    const capacitorObj = (window as any).Capacitor;
    const isCapacitorNative = capacitorObj?.isNativePlatform?.() === true ||
        ['ios', 'android'].includes(capacitorObj?.getPlatform?.() || '');
    if (isCapacitorNative) return 'https://super-admin-ext2.api.coffeemasterpro.com';

    if (isRubytill) return isDev ? 'https://super-admin-ext2.api.dev.rubytill.com' : 'https://super-admin-ext2.api.rubytill.com';
    if (isTerrakron) return isDev ? 'https://super-admin-ext2.api.dev.terrakron.com' : 'https://super-admin-ext2.api.terrakron.com';

    if (hostname === 'coffeemasterpro.com' || hostname === 'www.coffeemasterpro.com' ||
        hostname === 'cafemenumaster.com' || hostname === 'www.cafemenumaster.com') {
        return 'https://super-admin-ext2.api.coffeemasterpro.com';
    }
    if (hostname === 'localhost' || hostname === '127.0.0.1') return '/super-admin-ext2-api';
    if (hostname.includes('dev.')) return 'https://super-admin-ext2.api.dev.coffeemasterpro.com';
    if (hostname.includes('staging.coffeemasterpro.com')) return 'https://super-admin-ext2.api.stage.coffeemasterpro.com';
    return 'https://super-admin-ext2.api.coffeemasterpro.com';
};

const SUPERADMIN_EXT2_API_BASE = (() => {
    try {
        const viteBase = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_SUPERADMIN_EXT2_API_BASE : '';
        if (viteBase) return viteBase;
    } catch (_) { /* ignore */ }
    const envBase = (typeof process !== 'undefined' && process.env)
        ? (process.env.SUPERADMIN_EXT2_API_BASE)
        : '';
    if (envBase) return envBase;
    return getSuperAdminExt2ApiDomain();
})();
// ---------------------------------------------------------------------------

// DevOps Service API (deployment dashboard)
// devops-service is deployed PER-PRODUCT in the product's own region.
// AgenticPOS/RubyTill runs its own devops backend in us-west-1 behind
// devops.api.dev.rubytill.com with its own DynamoDB registry + webhook-status
// tables. (The old shared cafemaster/us-east-2 backend is abandoned.)
//
// URL routing: resolve the devops custom domain from the current product's
// hostname so RubyTill admin reads the RubyTill/us-west-1 backend.
const getDevopsApiDomain = (): string => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return '/devops-api';
    }
    const isDev = hostname.includes('dev.') || hostname.includes('staging.') || hostname.includes('stage.');
    if (hostname.includes('rubytill')) {
        return isDev ? 'https://devops.api.dev.rubytill.com' : 'https://devops.api.rubytill.com';
    }
    if (hostname.includes('terrakron')) {
        // TODO: terrakron-branded devops custom domain not yet set up; falls back to cafemaster URL
        return isDev ? 'https://devops.api.dev.coffeemasterpro.com' : 'https://devops.api.coffeemasterpro.com';
    }
    return isDev
        ? 'https://devops.api.dev.coffeemasterpro.com'
        : 'https://devops.api.coffeemasterpro.com';
};

const DEVOPS_API_BASE = (() => {
    try {
        const viteBase = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_DEVOPS_API_BASE : '';
        if (viteBase) return viteBase;
    } catch (_) { /* ignore */ }
    return getDevopsApiDomain();
})();

// Locations Service API
const LOCATIONS_API_BASE = (() => {
    try {
        const viteBase = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_LOCATIONS_API_BASE : '';
        if (viteBase) return viteBase;
    } catch (_) { /* ignore */ }
    return serviceDomain('locations', detectStage());
})();

// Social publishing (social-publish-service) — super-admin mirror routes /social/admin/tenant/{tenantId}/...
const SOCIAL_API_BASE = (() => {
    try {
        const viteBase = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_SOCIAL_API_BASE : '';
        if (viteBase) return viteBase;
    } catch (_) { /* ignore */ }
    return serviceDomain('social', detectStage());
})();

// Location Billing Service API
const LOCATION_BILLING_API_BASE = (() => {
    try {
        const viteBase = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_LOCATION_BILLING_API_BASE : '';
        if (viteBase) return viteBase;
    } catch (_) { /* ignore */ }
    return serviceDomain('location-billing', detectStage());
})();
// Billing service — routed on the SHARED `platform` cluster by base path
// (https://platform.api.<base>/billing), per the URL & domain architecture rule.
// NOT a per-service custom domain.
const BILLING_SERVICE_API_BASE = (() => {
    try {
        const viteBase = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_BILLING_API_BASE : '';
        if (viteBase) return viteBase;
    } catch (_) { /* ignore */ }
    return `${serviceDomain('platform', detectStage())}/billing`;
})();
// Phase H: marketplace owner onboarding service base.
const MARKETPLACE_OWNER_API_BASE = (() => {
    try {
        const viteBase = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_MARKETPLACE_OWNER_API_BASE : '';
        if (viteBase) return viteBase;
    } catch (_) { /* ignore */ }
    return serviceDomain('marketplace-owner', detectStage());
})();
// ---------------------------------------------------------------------------

// Store JWT token
let jwtToken: string | null = null;
let tokenExpiry: number | null = null; // Track when the token expires
let backgroundRefreshTimer: ReturnType<typeof setTimeout> | null = null;

// Schedule a background refresh ~5 minutes before the token expires
const scheduleBackgroundRefresh = () => {
    if (backgroundRefreshTimer) clearTimeout(backgroundRefreshTimer);
    if (!tokenExpiry) return;
    const msUntilRefresh = tokenExpiry - Date.now() - 5 * 60 * 1000; // 5 min before expiry
    if (msUntilRefresh <= 0) return;
    backgroundRefreshTimer = setTimeout(async () => {
        console.log('[Auth] Background token refresh triggered');
        const ok = await refreshTokenIfNeeded();
        if (ok) {
            console.log('[Auth] Background refresh succeeded');
            scheduleBackgroundRefresh();
        } else {
            console.warn('[Auth] Background refresh failed — session will expire on next request');
        }
    }, msUntilRefresh);
};

// When tab becomes visible or gets focus, immediately check & refresh token.
// Browsers heavily throttle setTimeout in background tabs (Chrome delays by minutes),
// so the scheduled refresh timer may not fire. This catches that case.
const handleTabVisible = async () => {
    if (!(jwtToken || localStorage.getItem('jwtToken'))) return;
    if (isTokenExpired()) {
        console.log('[Auth] Tab visible — token expired, refreshing...');
        const ok = await refreshTokenIfNeeded();
        if (ok) {
            scheduleBackgroundRefresh();
        } else {
            console.warn('[Auth] Tab visible refresh failed — session expired');
            clearTokens();
            window.dispatchEvent(new CustomEvent('session-expired'));
        }
    } else {
        // Re-schedule in case the timer was throttled
        scheduleBackgroundRefresh();
    }
};

if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) handleTabVisible();
    });
    window.addEventListener('focus', handleTabVisible);
}

const clearTokens = () => {
    jwtToken = null;
    tokenExpiry = null;
    if (backgroundRefreshTimer) { clearTimeout(backgroundRefreshTimer); backgroundRefreshTimer = null; }
    try {
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tokenExpiry');
    } catch (_) {
        /* ignore */
    }
};

// Helper to decode JWT and check expiry
const decodeJwtExpiry = (token: string): number | null => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = JSON.parse(atob(parts[1]));
        return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
    } catch (_) {
        return null;
    }
};

// Check if token is expired or about to expire (5 min buffer)
const isTokenExpired = (): boolean => {
    if (!tokenExpiry) {
        // Try to get from stored token
        const storedToken = localStorage.getItem('jwtToken');
        if (storedToken) {
            tokenExpiry = decodeJwtExpiry(storedToken);
        }
    }
    if (!tokenExpiry) return true;
    return Date.now() > (tokenExpiry - 5 * 60 * 1000); // 5 minute buffer
};

// Refresh lock — prevents concurrent refresh attempts from racing
let refreshPromise: Promise<boolean> | null = null;

// Refresh token using Cognito (serialized — only one refresh at a time)
const refreshTokenIfNeeded = async (): Promise<boolean> => {
    if (!isTokenExpired()) return true; // Token still valid

    // If a refresh is already in flight, wait for it
    if (refreshPromise) {
        console.log('[Auth] Refresh already in progress, waiting...');
        return refreshPromise;
    }

    refreshPromise = (async () => {
        console.log('[Auth] Token expired or about to expire, attempting refresh...');

        // Try Cognito session refresh first (most reliable)
        try {
            const { cognitoGetSession } = await import('./cognitoService');
            const session = await cognitoGetSession();
            if (session && session.idToken) {
                jwtToken = session.idToken;
                tokenExpiry = decodeJwtExpiry(session.idToken);
                localStorage.setItem('jwtToken', session.idToken);
                if (session.refreshToken) {
                    localStorage.setItem('refreshToken', session.refreshToken);
                }
                if (tokenExpiry) {
                    localStorage.setItem('tokenExpiry', tokenExpiry.toString());
                }
                console.log('[Auth] Token refreshed successfully via Cognito session');
                scheduleBackgroundRefresh();
                return true;
            }
        } catch (err) {
            console.warn('[Auth] Cognito session refresh failed, trying refresh token...', err);
        }

        // Fallback to manual refresh token
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            console.warn('[Auth] No refresh token available');
            return false;
        }

        try {
            const { cognitoRefreshToken } = await import('./cognitoService');
            const result = await cognitoRefreshToken(refreshToken);
            if (result && result.idToken) {
                jwtToken = result.idToken;
                tokenExpiry = decodeJwtExpiry(result.idToken);
                localStorage.setItem('jwtToken', result.idToken);
                if (tokenExpiry) {
                    localStorage.setItem('tokenExpiry', tokenExpiry.toString());
                }
                console.log('[Auth] Token refreshed successfully via refresh token');
                scheduleBackgroundRefresh();
                return true;
            }
        } catch (err) {
            console.error('[Auth] Token refresh failed:', err);
        }

        return false;
    })().finally(() => { refreshPromise = null; });

    return refreshPromise;
};

/**
 * Attempt to refresh tokens and return the fresh JWT.
 * Other API services should call this on 401 before dispatching session-expired.
 * Returns the new token string on success, or null if refresh failed.
 */
export const tryRefreshToken = async (): Promise<string | null> => {
    const ok = await refreshTokenIfNeeded();
    if (ok) {
        return jwtToken || localStorage.getItem('jwtToken');
    }
    clearTokens();
    window.dispatchEvent(new CustomEvent('session-expired'));
    return null;
};

const isDemoMode = () => {
    try {
        return typeof localStorage !== 'undefined' && localStorage.getItem('demoMode') === 'true';
    } catch (_) {
        return false;
    }
};

const handleResponse = async (res: Response) => {
    const raw = await res.json().catch(() => ({}));
    // Some of our APIs still come back in the lambda-proxy envelope. Unwrap
    // before reading `error`/`message`, or a refusal reads as `undefined`.
    const data: any = (raw && typeof raw.body === 'string') ? parseBody(raw) : raw;
    if (!res.ok) {
        console.error('[API Error]', { status: res.status, statusText: res.statusText, url: res.url, data });
        /*
          THE BODY SURVIVES THE THROW.

          This used to be `new Error(data.error)` and nothing else, so every
          field a service sent with its refusal — the machine-readable `code`,
          the human `message`, and any context like the terminal a device is
          still bound to — was dropped on the floor. Callers were left matching
          on `err.message`, which is the raw code, and any screen that wanted to
          say WHICH terminal simply could not.

          Attaching them is additive: `message` keeps the exact value it had, so
          existing string comparisons are untouched.
        */
        const err = new Error(data.error || `Request Failed: ${res.statusText}`) as Error & Record<string, any>;
        err.status = res.status;
        err.code = data.error;
        err.data = data;
        if (data && typeof data === 'object') {
            for (const [k, v] of Object.entries(data)) {
                if (k !== 'message' && !(k in err)) err[k] = v;
            }
            if (typeof data.message === 'string') err.serverMessage = data.message;
        }
        throw err;
    }
    console.log('[API Success]', { status: res.status, url: res.url });
    // API Gateway wraps responses in {statusCode, body, headers}
    // Unwrap the body if it exists and is a string
    return parseBody(data);
};

const request = async (url: string, options?: RequestInit & { silent?: boolean; skipSessionExpiredOn401?: boolean }) => {
    // Skip loading spinner if silent mode is enabled
    if (!options?.silent) {
        loadingService.start();
    }
    try {
        // Try to refresh token if needed before making the request
        if (jwtToken || localStorage.getItem('jwtToken')) {
            await refreshTokenIfNeeded();
            // Don't dispatch session-expired here — let the 401 retry handle it
        }

        if (!jwtToken) {
            const stored = (typeof localStorage !== 'undefined') ? localStorage.getItem('jwtToken') : null;
            if (stored) jwtToken = stored;
        }

        const headers = {
            'Content-Type': 'application/json',
            ...(jwtToken && { 'Authorization': `Bearer ${jwtToken}` }),
            ...options?.headers
        } as Record<string, string>;

        console.log('[API Request]', {
            url,
            method: options?.method || 'GET',
            hasToken: !!jwtToken,
            headers: Object.keys(headers)
        });

        const res = await fetch(url, {
            ...options,
            headers
        });
        
        // If we get a 401, try refreshing token and retry once
        if (res.status === 401 && jwtToken) {
            console.log('[API] Got 401, attempting token refresh and retry');
            const refreshed = await refreshTokenIfNeeded();
            if (refreshed) {
                // Retry the request with new token
                const retryHeaders = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${jwtToken}`,
                    ...options?.headers
                } as Record<string, string>;

                const retryRes = await fetch(url, {
                    ...options,
                    headers: retryHeaders
                });
                if (retryRes.status === 401) {
                    if (options?.skipSessionExpiredOn401) {
                        console.warn('[API] 401 after retry — skipSessionExpiredOn401 set, not clearing session');
                        return await handleResponse(retryRes);
                    }
                    console.warn('[API] 401 after retry — session expired');
                    clearTokens();
                    window.dispatchEvent(new CustomEvent('session-expired'));
                }
                return await handleResponse(retryRes);
            }
            // Refresh failed — session expired
            if (options?.skipSessionExpiredOn401) {
                console.warn('[API] Token refresh failed on 401 — skipSessionExpiredOn401 set, not clearing session');
                return await handleResponse(res);
            }
            console.warn('[API] Token refresh failed on 401 — session expired');
            clearTokens();
            window.dispatchEvent(new CustomEvent('session-expired'));
        }

        return await handleResponse(res);
    } finally {
        // Stop loading spinner if not in silent mode
        if (!options?.silent) {
            loadingService.stop();
        }
    }
};

const superAdminRequest = async (path: string, options?: RequestInit) => {
    return request(`${SUPERADMIN_API_BASE}${path}`, options);
};

/**
 * Public wrapper around the internal request() pipeline.
 * Other API service files (aiUsageApi, page-level callers, etc.) MUST use
 * this instead of raw fetch so they get auto token refresh + 401 retry +
 * session-expired dispatch for free. The URL must be a full URL (with
 * scheme + host) since this function does not prepend a base.
 */
export const authedRequest = <T = any>(url: string, options?: RequestInit & { silent?: boolean; skipSessionExpiredOn401?: boolean }): Promise<T> =>
    request(url, options) as Promise<T>;

// Public request (no auth required) - used for support tickets, lead forms, etc.
const publicSuperAdminRequest = async (path: string, options?: RequestInit) => {
    loadingService.start();
    try {
        const res = await fetch(`${SUPERADMIN_API_BASE}${path}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options?.headers
            }
        });
        return await handleResponse(res);
    } finally {
        loadingService.stop();
    }
};

// Public request to super-admin-ext service (support tickets, etc.)
const publicSuperAdminExtRequest = async (path: string, options?: RequestInit) => {
    loadingService.start();
    try {
        const res = await fetch(`${SUPERADMIN_EXT_API_BASE}${path}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options?.headers
            }
        });
        return await handleResponse(res);
    } finally {
        loadingService.stop();
    }
};

// Authenticated request to super-admin-ext service (admin support ticket operations)
const superAdminExtRequest = async (path: string, options?: RequestInit) =>
    request(`${SUPERADMIN_EXT_API_BASE}${path}`, options);

// Authenticated request to super-admin-ext2 service (POS config, registers, gateways, EMV)
const superAdminExt2Request = async (path: string, options?: RequestInit) =>
    request(`${SUPERADMIN_EXT2_API_BASE}${path}`, options);

// --- super-admin-ext3 (payment administration: NMI, POI, gateway, audit) ---
const SUPERADMIN_EXT3_API_BASE = (() => {
    try {
        const viteBase = typeof import.meta !== 'undefined' ? (import.meta as any)?.env?.VITE_SUPERADMIN_EXT3_API_BASE : '';
        if (viteBase) return viteBase;
    } catch (_) { /* ignore */ }
    return SUPERADMIN_EXT2_API_BASE.replace('super-admin-ext2', 'super-admin-ext3');
})();

// Public accessors so UI components don't duplicate hostname-guessing logic.
// Whatever product+stage the admin is currently talking to, the pos-nmi URL
// points at the SAME backend swarm so a webhook the operator registers in
// NMI actually resolves and deliveres back to us.
export function getPosNmiApiBase(): string {
    if (SUPERADMIN_EXT3_API_BASE.startsWith('/')) {
        // localhost dev proxy — NMI can't reach /pos-nmi-api, show the real
        // dev custom domain instead so operators can paste it into NMI.
        return 'https://pos-nmi.api.dev.rubytill.com';
    }
    return SUPERADMIN_EXT3_API_BASE.replace('super-admin-ext3', 'pos-nmi');
}

const superAdminExt3Request = async (path: string, options?: RequestInit) =>
    request(`${SUPERADMIN_EXT3_API_BASE}${path}`, options);

// Authenticated request to devops service (deployment dashboard)
const devopsRequest = async (path: string, options?: RequestInit & { silent?: boolean }) =>
    request(`${DEVOPS_API_BASE}${path}`, options);

// Authenticated request to billing-service (catalog + entitlements)
const billingServiceRequest = async (path: string, options?: RequestInit) =>
    request(`${BILLING_SERVICE_API_BASE}${path}`, options);

// Authenticated request to location-billing service
const locationBillingRequest = async (path: string, options?: RequestInit) =>
    request(`${LOCATION_BILLING_API_BASE}${path}`, options);

// Authenticated request to marketplace-owner service (Phase H)
const marketplaceOwnerRequest = async (path: string, options?: RequestInit) =>
    request(`${MARKETPLACE_OWNER_API_BASE}${path}`, options);

// Authenticated request to locations service
const locationsRequest = async (path: string, options?: RequestInit) =>
    request(`${LOCATIONS_API_BASE}${path}`, options);

// Request options type with silent support
type RequestOptions = RequestInit & { silent?: boolean };

const socialReq = <T,>(url: string, options?: RequestOptions): Promise<T> => request(url, options) as Promise<T>;
const socialAdminBase = (tenantId: string) => `${SOCIAL_API_BASE}/social/admin/tenant/${encodeURIComponent(tenantId)}`;
const authRequest = async (path: string, options?: RequestOptions) => request(`${AUTH_API_BASE}${path}`, options);
const accountsRequest = async (path: string, options?: RequestOptions) => request(`${ACCOUNTS_API_BASE}${path}`, options);
const leadsRequest = async (path: string, options?: RequestOptions) => request(`${LEADS_API_BASE}${path}`, options);
const recipesRequest = async (path: string, options?: RequestOptions) => request(`${RECIPES_API_BASE}${path}`, options);
const inventoryRequest = async (path: string, options?: RequestOptions) => request(`${INVENTORY_API_BASE}${path}`, options);
const schedulingRequest = async (path: string, options?: RequestOptions) => request(`${SCHEDULING_API_BASE}${path}`, options);
const emailRequest = async (path: string, options?: RequestOptions) => request(`${EMAIL_API_BASE}${path}`, options);

// New microservice request helpers (split from accounts-service)
const usersRequest = async (path: string, options?: RequestOptions) => request(`${USERS_API_BASE}${path}`, options);
const tenantsRequest = async (path: string, options?: RequestOptions) => request(`${TENANTS_API_BASE}${path}`, options);
const routinesRequest = async (path: string, options?: RequestOptions) => request(`${ROUTINES_API_BASE}${path}`, options);
const trainingRequest = async (path: string, options?: RequestOptions) => request(`${TRAINING_API_BASE}${path}`, options);
const uploadsRequest = async (path: string, options?: RequestOptions) => request(`${UPLOADS_API_BASE}${path}`, options);
const creatorRequest = async (path: string, options?: RequestOptions) => request(`${CREATOR_API_BASE}${path}`, options);
const affiliatesRequest = async (path: string, options?: RequestOptions) => request(`${AFFILIATES_API_BASE}${path}`, options);
const mediaRequest = async (path: string, options?: RequestOptions) => request(`${MEDIA_API_BASE}${path}`, options);

// Normalize API Gateway style responses where payload may be in `body` as a string
const parseBody = <T>(resp: any): T => {
    if (!resp) return resp as T;
    if (typeof resp.body === 'string') {
        try {
            return JSON.parse(resp.body) as T;
        } catch (_) {
            return resp.body as T;
        }
    }
    return resp as T;
};

const decodeJwt = (token?: string): Record<string, any> => {
    if (!token) return {};
    const parts = token.split('.');
    if (parts.length !== 3) return {};
    try {
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const decoded = typeof atob === 'function'
            ? atob(payload)
            : (globalThis as any).Buffer?.from(payload, 'base64')?.toString('binary');
        if (!decoded) return {};
        return JSON.parse(decoded);
    } catch (_) {
        return {};
    }
};

const mapRole = (roleVal: any) => {
    if (!roleVal) return null;
    const val = String(roleVal).toLowerCase().replace(/\s+/g, '_');
    if (['super_admin', 'superadmin', 'super-admin'].includes(val)) return 'super_admin';
    if (['admin'].includes(val)) return 'admin';
    if (['employee', 'staff'].includes(val)) return 'employee';
    return roleVal;
};

const normalizeUser = (rawResponse: any) => {
    let data: any;
    if (rawResponse && typeof (rawResponse as any).body === 'string') {
        try {
            data = JSON.parse((rawResponse as any).body);
        } catch (_) {
            data = (rawResponse as any).body;
        }
    } else if ((rawResponse as any)?.user) {
        data = rawResponse;
    } else {
        data = (rawResponse as any)?.body || rawResponse;
    }

    const rawUser = (data as any)?.user || data;

    if (!rawUser) return null;

    return {
        ...rawUser,
        role: mapRole(rawUser.role || rawUser.userRole || rawUser.user_type || rawUser.userType) || 'employee',
        name: rawUser.name || rawUser.fullName || (rawUser.email ? rawUser.email.split('@')[0] : 'Unknown user'),
        status: rawUser.status || 'active'
    } as User;
};

    const fetchProfileFromCognito = async (): Promise<Partial<User>> => {
        try {
            const attrs = await cognitoGetUserAttributes();
            const bag: Record<string, string> = {};
            attrs?.forEach((a: any) => {
                if (a?.Name && a?.Value) bag[a.Name] = a.Value;
            });
            return {
                role: mapRole(bag['custom:role']) || undefined,
                tenantId: bag['custom:tenantId'],
                name: bag['name'] || undefined,
                email: bag['email'] || undefined
            };
        } catch (err) {
            console.warn('Failed to fetch Cognito attributes for profile enrichment', err);
            return {};
        }
    };

export const api = {
    auth: {
        login: async (email: string, password: string, tenantId?: string): Promise<any> => {
            // Clear any stale tenant data from previous sessions to prevent orphan tenant creation
            localStorage.removeItem('lastBrandProfile');
            localStorage.removeItem('activeTenantId');

            if (isDemoMode()) {
                const demoUser: User = {
                    id: 'demo_user',
                    tenantId: DEMO_TENANT_ID,
                    name: 'Demo Manager',
                    email: 'demo@trycafemaster.com',
                    role: 'admin' as any,
                    status: 'active',
                    completedTrainingRecipeIds: []
                };
                return { type: 'success', user: demoUser };
            }
            try {
                const cognitoResult = await cognitoLogin(email, password);
                if (cognitoResult.type === 'success') {
                    jwtToken = cognitoResult.idToken;
                    tokenExpiry = decodeJwtExpiry(cognitoResult.idToken);
                    if (jwtToken) localStorage.setItem('jwtToken', jwtToken);
                    if (cognitoResult.refreshToken) localStorage.setItem('refreshToken', cognitoResult.refreshToken);
                    if (tokenExpiry) {
                        localStorage.setItem('tokenExpiry', tokenExpiry.toString());
                    }
                    scheduleBackgroundRefresh();

                    const claims = decodeJwt(cognitoResult.idToken);

                    // Handle multi-tenant: custom:tenantId may be comma-separated
                    const rawTenantId = claims['custom:tenantId'] || tenantId || 'default';
                    console.log('[API:login] Raw tenantId from JWT:', rawTenantId);
                    const allTenantIds = rawTenantId.split(',').map((t: string) => t.trim()).filter(Boolean);
                    console.log('[API:login] Parsed tenant IDs:', allTenantIds);
                    // Use stored active tenant or first one
                    const storedActiveTenant = localStorage.getItem('activeTenantId');
                    const activeTenantId = (storedActiveTenant && allTenantIds.includes(storedActiveTenant)) 
                      ? storedActiveTenant 
                      : allTenantIds[0];
                    console.log('[API:login] Active tenant:', activeTenantId, 'hasMultiple:', allTenantIds.length > 1);

                      const rawResponse = await authRequest(`/auth/login`, {
                          method: 'POST',
                          body: JSON.stringify({
                              email: cognitoResult.user.email,
                              tenantId: activeTenantId
                          })
                      });

                        let normalizedUser = normalizeUser(rawResponse);
                        
                        // If normalizeUser succeeded, add multi-tenant info; otherwise use fallback
                        if (normalizedUser) {
                            normalizedUser = {
                                ...normalizedUser,
                                tenantId: normalizedUser.tenantId || activeTenantId,
                                allTenantIds: allTenantIds // Always set from JWT
                            };
                        } else {
                            normalizedUser = {
                                ...cognitoResult.user,
                                id: cognitoResult.user.userId, // Use Cognito sub as fallback ID
                                role: mapRole(claims['custom:role']) || undefined,
                                status: 'active',
                                tenantId: activeTenantId,
                                allTenantIds: allTenantIds,
                                name: claims['name'] || cognitoResult.user.name || (cognitoResult.user.email ? cognitoResult.user.email.split('@')[0] : 'Unknown user')
                            } as User;
                        }

                      if (!normalizedUser.role || normalizedUser.name?.toLowerCase().includes('unknown')) {
                          const enriched = await fetchProfileFromCognito();
                          normalizedUser = {
                              ...normalizedUser,
                              ...enriched,
                              role: normalizedUser.role || enriched.role || 'employee',
                              tenantId: normalizedUser.tenantId || enriched.tenantId || activeTenantId,
                              allTenantIds: allTenantIds, // Always preserve from JWT
                              name: normalizedUser.name || enriched.name || (normalizedUser.email ? normalizedUser.email.split('@')[0] : undefined) || 'User'
                          } as User;
                      }

                        if (!normalizedUser?.role || normalizedUser.name?.toLowerCase().includes('unknown')) {
                            clearTokens();
                            throw new Error('Your profile was not fully loaded. Please sign in again.');
                        }

                        return { type: 'success', user: normalizedUser };
                } else if (cognitoResult.type === 'newPasswordRequired') {
                    return { type: 'newPasswordRequired', cognitoUser: cognitoResult.cognitoUser, message: cognitoResult.message };
                } else if (cognitoResult.type === 'mfaRequired') {
                    return { type: 'mfaRequired', cognitoUser: cognitoResult.cognitoUser, challengeName: cognitoResult.challengeName, message: cognitoResult.message };
                } else if (cognitoResult.type === 'totpRequired') {
                    return { type: 'totpRequired', cognitoUser: cognitoResult.cognitoUser, message: cognitoResult.message };
                }
                throw new Error('Unknown authentication response type');
            } catch (error: any) {
                console.error('Cognito login failed:', error);
                throw error;
            }
        },
        completeNewPassword: async (cognitoUser: any, newPassword: string): Promise<any> => {
            const result = await cognitoCompleteNewPassword(cognitoUser, newPassword);
            if (result.type === 'success') {
                jwtToken = result.idToken;
                tokenExpiry = decodeJwtExpiry(result.idToken);
                if (jwtToken) localStorage.setItem('jwtToken', jwtToken);
                if (result.refreshToken) localStorage.setItem('refreshToken', result.refreshToken);
                if (tokenExpiry) localStorage.setItem('tokenExpiry', tokenExpiry.toString());
                scheduleBackgroundRefresh();
                const claims = decodeJwt(result.idToken);
                
                // Handle multi-tenant: custom:tenantId may be comma-separated
                const rawTenantId = claims['custom:tenantId'] || 'default';
                const allTenantIds = rawTenantId.split(',').map((t: string) => t.trim()).filter(Boolean);
                // Use stored active tenant or first one
                const storedActiveTenant = localStorage.getItem('activeTenantId');
                const activeTenantId = (storedActiveTenant && allTenantIds.includes(storedActiveTenant)) 
                  ? storedActiveTenant 
                  : allTenantIds[0];
                
                console.log('[Auth] completeNewPassword multi-tenant check', { rawTenantId, allTenantIds, activeTenantId });
                
                try {
                    await authRequest(`/auth/change-password`, {
                        method: 'POST',
                        body: JSON.stringify({ email: result.user.email, newPassword })
                    });
                } catch (syncErr) {
                    console.warn('Backend password sync failed (status update)', syncErr);
                }

                    let normalizedUser: User | null = null;
                try {
                    console.log('[Auth] completeNewPassword fetching user from backend', { email: result.user.email, tenantId: activeTenantId });
                    const rawResponse = await authRequest(`/auth/login`, {
                        method: 'POST',
                        body: JSON.stringify({
                            email: result.user.email,
                            tenantId: activeTenantId
                        })
                    });
                    normalizedUser = normalizeUser(rawResponse);
                    console.log('[Auth] completeNewPassword backend user', { id: normalizedUser?.id, email: normalizedUser?.email, name: normalizedUser?.name });
                } catch (fetchErr) {
                    console.warn('[Auth] Backend login fetch failed after password change', fetchErr);
                }

                const fallbackUser: User = {
                    ...result.user,
                        id: result.user.userId, // Use Cognito sub as fallback ID
                        role: mapRole(claims['custom:role']) || undefined,
                        status: 'active',
                        tenantId: activeTenantId,
                        allTenantIds: allTenantIds, // Always set array
                        name: claims['name'] || result.user.name || (result.user.email ? result.user.email.split('@')[0] : 'Unknown user')
                } as User;

                    let resolvedUser = normalizedUser || fallbackUser;
                    
                    // Always preserve allTenantIds from JWT
                    resolvedUser.allTenantIds = allTenantIds;

                    if (!resolvedUser.role || resolvedUser.name?.toLowerCase().includes('unknown')) {
                        const enriched = await fetchProfileFromCognito();
                        resolvedUser = {
                            ...resolvedUser,
                            ...enriched,
                            role: resolvedUser.role || enriched.role || 'employee',
                            tenantId: resolvedUser.tenantId || enriched.tenantId || activeTenantId,
                            allTenantIds: allTenantIds, // Always preserve from JWT
                            name: resolvedUser.name || enriched.name || (resolvedUser.email ? resolvedUser.email.split('@')[0] : undefined) || 'User'
                        } as User;
                    }

                    if (!resolvedUser?.role || resolvedUser.name?.toLowerCase().includes('unknown')) {
                        clearTokens();
                        throw new Error('Your profile was not fully loaded. Please sign in again.');
                    }

                    console.log('[Auth] completeNewPassword resolved user', { tenantId: resolvedUser.tenantId, allTenantIds: resolvedUser.allTenantIds });
                    return { type: 'success', user: resolvedUser };
            }
            throw new Error('Failed to complete new password challenge');
        },
        sendMFACode: async (cognitoUser: any, mfaCode: string): Promise<any> => {
            const result = await cognitoSendMFACode(cognitoUser, mfaCode);
            if (result.type === 'success') {
                jwtToken = result.idToken;
                tokenExpiry = decodeJwtExpiry(result.idToken);
                if (jwtToken) localStorage.setItem('jwtToken', jwtToken);
                if (result.refreshToken) localStorage.setItem('refreshToken', result.refreshToken);
                if (tokenExpiry) localStorage.setItem('tokenExpiry', tokenExpiry.toString());
                scheduleBackgroundRefresh();

                const claims = decodeJwt(result.idToken);
                const rawTenantId = claims['custom:tenantId'] || 'default';
                const allTenantIds = rawTenantId.split(',').map((t: string) => t.trim()).filter(Boolean);
                const storedActiveTenant = localStorage.getItem('activeTenantId');
                const activeTenantId = (storedActiveTenant && allTenantIds.includes(storedActiveTenant))
                  ? storedActiveTenant
                  : allTenantIds[0];

                // Fetch full user from backend to get DynamoDB user ID
                let normalizedUser: User | null = null;
                try {
                    const rawResponse = await authRequest(`/auth/login`, {
                        method: 'POST',
                        body: JSON.stringify({
                            email: result.user.email,
                            tenantId: activeTenantId
                        })
                    });
                    normalizedUser = normalizeUser(rawResponse);
                    if (normalizedUser) {
                        normalizedUser.allTenantIds = allTenantIds;
                    }
                } catch (fetchErr) {
                    console.warn('Backend login fetch failed after MFA', fetchErr);
                }

                const fallbackUser: User = {
                    ...result.user,
                    id: result.user.userId,
                    role: mapRole(claims['custom:role']) || 'employee',
                    status: 'active',
                    tenantId: activeTenantId,
                    allTenantIds: allTenantIds,
                    name: claims['name'] || result.user.name || (result.user.email ? result.user.email.split('@')[0] : 'Unknown user')
                } as User;

                return { type: 'success', user: normalizedUser || fallbackUser };
            }
            throw new Error('Failed to verify MFA code');
        },
        restoreToken: async (): Promise<void> => {
            const token = localStorage.getItem('jwtToken');
            const storedExpiry = localStorage.getItem('tokenExpiry');

            if (token) {
                jwtToken = token;

                if (storedExpiry) {
                    tokenExpiry = parseInt(storedExpiry, 10);
                } else {
                    tokenExpiry = decodeJwtExpiry(token);
                    if (tokenExpiry) {
                        localStorage.setItem('tokenExpiry', String(tokenExpiry));
                    }
                }

                if (isTokenExpired()) {
                    console.log('[API] Token expired on restore, attempting refresh');
                    const refreshed = await refreshTokenIfNeeded();
                    if (!refreshed) {
                        console.warn('[API] Failed to refresh token on restore — session expired');
                        clearTokens();
                        window.dispatchEvent(new CustomEvent('session-expired'));
                        return;
                    }
                }

                console.log('[API] Token restored, expires in', tokenExpiry ? Math.round((tokenExpiry - Date.now()) / 60000) : 'unknown', 'minutes');
                scheduleBackgroundRefresh();
            }
        },
        getToken: (): string | null => jwtToken,
        setToken: (token: string): void => {
            jwtToken = token;
            tokenExpiry = decodeJwtExpiry(token);
            localStorage.setItem('jwtToken', token);
            if (tokenExpiry) localStorage.setItem('tokenExpiry', tokenExpiry.toString());
            scheduleBackgroundRefresh();
        },
        clearToken: (): void => {
            clearTokens();
        },
        getCognitoAttributes: (): Record<string, string> | null => {
            // Decode JWT token to get Cognito attributes (they're embedded in the ID token)
            const token = jwtToken || localStorage.getItem('jwtToken');
            if (!token) {
                console.warn('[API] getCognitoAttributes: No token available');
                return null;
            }
            
            try {
                const claims = decodeJwt(token);
                console.log('[API] getCognitoAttributes decoded claims:', claims);
                
                // Return relevant custom attributes
                const attributes: Record<string, string> = {};
                for (const key of Object.keys(claims)) {
                    if (key.startsWith('custom:') || ['email', 'sub', 'cognito:username'].includes(key)) {
                        attributes[key] = claims[key];
                    }
                }
                return attributes;
            } catch (err) {
                console.warn('[API] getCognitoAttributes failed to decode token:', err);
                return null;
            }
        }
    },

    admin: {
        resetPassword: async (email: string, newPassword: string): Promise<any> => {
            return authRequest(`/auth/change-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, newPassword })
            });
        },
        getTrialSettings: async (): Promise<{ showTrialOnLandingPage: boolean; showPaidPlanOnLandingPage: boolean; enableAffiliateTrials: boolean; trialDays: number }> => {
            const resp = await superAdminRequest(`/admin/trial-settings`);
            return parseBody(resp);
        },
        updateTrialSettings: async (settings: { showTrialOnLandingPage: boolean; showPaidPlanOnLandingPage: boolean; enableAffiliateTrials: boolean; trialDays: number }): Promise<any> => {
            return superAdminRequest(`/admin/trial-settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings)
            });
        }
    },

    superAdmin: {
        listSubscriptions: async (params?: { status?: string; limit?: number }): Promise<{ items: SubscriptionSummary[]; hasMore?: boolean }> => {
            const search = new URLSearchParams();
            if (params?.status) search.append('status', params.status);
            if (params?.limit) search.append('limit', String(params.limit));
            const resp = await superAdminRequest(`/admin/subscriptions${search.toString() ? `?${search.toString()}` : ''}`);
            return parseBody(resp);
        },
        listPrices: async (): Promise<{ items: PricePlan[] }> => {
            const resp = await superAdminRequest(`/admin/stripe/prices`);
            return parseBody(resp);
        },
        getSubscription: async (subscriptionId: string): Promise<SubscriptionSummary> => {
            const resp = await superAdminRequest(`/admin/subscriptions/${subscriptionId}`);
            return parseBody(resp);
        },
        updatePaymentMethod: async (subscriptionId: string, paymentMethodId: string): Promise<SubscriptionSummary> => {
            const resp = await superAdminRequest(`/admin/subscriptions/${subscriptionId}/payment-method`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ paymentMethodId })
            });
            return parseBody(resp);
        },
        /*
          `cancelSubscription(subscriptionId)` was DELETED 2026-09-07 along with
          the Finance tab's Cancel-subscription control, its only caller. It
          cancelled `tenant.stripeSubscriptionId` — the one tenant-level
          subscription of the pre-per-location billing model, which the
          migration clears and nothing is billed on. Removed rather than left
          dead: an unused method that cancels a subscription is one a future
          reader wires to the wrong subscription.
        */
        setCancelAtPeriodEnd: async (subscriptionId: string, cancelAtPeriodEnd: boolean = true): Promise<SubscriptionSummary> => {
            const resp = await superAdminRequest(`/admin/subscriptions/${subscriptionId}/cancel-at-period-end`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cancelAtPeriodEnd })
            });
            return parseBody(resp);
        },
        upcomingInvoice: async (subscriptionId: string): Promise<UpcomingInvoice> => {
            const resp = await superAdminRequest(`/admin/subscriptions/${subscriptionId}/upcoming`);
            return parseBody(resp);
        },
        listPayments: async (params?: { limit?: number; startingAfter?: string }): Promise<{ items: ChargeSummary[]; hasMore?: boolean }> => {
            const search = new URLSearchParams();
            if (params?.limit) search.append('limit', String(params.limit));
            if (params?.startingAfter) search.append('startingAfter', params.startingAfter);
            const resp = await superAdminRequest(`/admin/payments${search.toString() ? `?${search.toString()}` : ''}`);
            return parseBody(resp);
        },
        createTenantCheckoutSession: async (params: { tenantId: string; successUrl: string; cancelUrl: string; priceId?: string }): Promise<{ url: string }> => {
            const resp = await superAdminRequest('/admin/checkout/create-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params)
            });
            return parseBody(resp);
        },
        refundPayment: async (chargeId: string, amount?: number, reason?: string): Promise<any> => {
            const resp = await superAdminRequest(`/admin/payments/${chargeId}/refund`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...(amount ? { amount } : {}), ...(reason ? { reason } : {}) })
            });
            return parseBody(resp);
        },
        createTicket: async (payload: { tenantId: string; email: string; reason: string; message: string; sender?: string }): Promise<{ ticketId: string }> => {
            // Use super-admin service (not ext) - accessible to any authenticated user
            const resp = await superAdminRequest(`/admin/tickets`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tenantId: payload.tenantId,
                    email: payload.email,
                    reason: payload.reason,
                    message: payload.message,
                    sender: payload.sender || 'tenant'
                })
            });
            return parseBody(resp);
        },
        replyTicket: async (ticketId: string, message: string, sender: 'admin' | 'tenant' = 'admin'): Promise<{ success: boolean }> => {
            // Use super-admin service (not ext) - accessible to any authenticated user
            const resp = await superAdminRequest(`/admin/tickets/${ticketId}/reply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message, sender })
            });
            return parseBody(resp);
        },
        listTickets: async (): Promise<SupportTicket[]> => {
            // Use super-admin service (not ext) - accessible to any authenticated user
            const resp = await superAdminRequest(`/admin/tickets`);
            return parseBody(resp) || [];
        },
        listTicketMessages: async (ticketId: string): Promise<TicketMessage[]> => {
            // Use super-admin service (not ext) - accessible to any authenticated user
            const resp = await superAdminRequest(`/admin/tickets/${ticketId}/messages`);
            return parseBody(resp) || [];
        },
        createCheckoutSessionForSignup: async (payload: { email: string; businessName: string; password?: string; priceId?: string; referralCode?: string }): Promise<{ url: string; id: string }> => {
            const resp = await superAdminRequest(`/admin/stripe/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            return parseBody(resp);
        },
        getTenantStatus: async (tenantId: string): Promise<TenantStatusInfo> => {
            const resp = await superAdminRequest(`/admin/tenants/${tenantId}/status`);
            return parseBody(resp);
        },
        getTenantBilling: async (tenantId: string): Promise<{ events: PaymentEvent[] }> => {
            const resp = await superAdminRequest(`/admin/tenants/${tenantId}/billing`);
            return parseBody(resp);
        },
        getTenant: async (tenantId: string): Promise<Tenant> => {
            const resp = await superAdminRequest(`/admin/tenants/${tenantId}`);
            return parseBody(resp);
        },
        /*
          `changeTenantPlan(tenantId, priceId)` was DELETED 2026-09-07 with the
          Finance tab's "Change plan" dropdown, its only caller. It swapped the
          Stripe price on the tenant-level subscription; a plan is composed per
          LOCATION now, through the Plan assignment card. Removed rather than
          left dead, so nothing can reach for it by name.
        */
        updateTenantStatus: async (tenantId: string, status: string): Promise<{ tenantId: string; status: string }> => {
            const resp = await superAdminRequest(`/admin/tenants/${tenantId}/status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status })
            });
            return parseBody(resp);
        },
        updateTenantFields: async (tenantId: string, fields: { freeLocationQuota?: number }): Promise<{ success: boolean }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(fields)
            });
            return parseBody(resp);
        },
        impersonateTenant: async (tenantId: string): Promise<{ tenantId: string; email: string; tempPassword?: string; impersonationToken?: string; token?: string }> => {
            const resp = await superAdminRequest(`/admin/tenants/${tenantId}/impersonate`, { method: 'POST' });
            return parseBody(resp);
        },
        exchangeImpersonationToken: async (token: string): Promise<{ user: User; tenantId: string; email: string; expiresAt: number; impersonation: boolean }> => {
            const resp = await superAdminRequest(`/admin/impersonation/exchange`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token })
            });
            return parseBody(resp);
        },
        resetTenantPassword: async (tenantId: string): Promise<{ tenantId: string; email: string; tempPassword: string | null; forceChangeNextLogin: boolean; resetEmailSent?: boolean; message?: string }> => {
            const resp = await superAdminRequest(`/admin/tenants/${tenantId}/reset-password`, { method: 'POST' });
            return parseBody(resp);
        },
        resendWelcomeEmail: async (tenantId: string): Promise<{ tenantId: string; email: string; tempPassword: string | null; userAlreadyExisted: boolean; emailSent: boolean; templateUsed: string; resetEmailSent?: boolean; message?: string }> => {
            const resp = await superAdminRequest(`/admin/tenants/${tenantId}/resend-welcome`, { method: 'POST' });
            return parseBody(resp);
        },
        sendTenantEmail: async (
            tenantId: string,
            payload: { subject: string; message?: string; html?: string; to?: string[]; replyTo?: string[]; templateId?: string; templateData?: Record<string, any> }
        ): Promise<{ queued: boolean; tenantId: string; to: string[] }> => {
            const resp = await superAdminRequest(`/admin/tenants/${tenantId}/email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            return parseBody(resp);
        },
        // Bulk Operations
        bulkPause: async (tenantIds: string[], reason?: string): Promise<{ succeeded: string[]; failed: Array<{ tenantId: string; error: string }>; total: number }> => {
            const resp = await superAdminRequest(`/admin/bulk/pause`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tenantIds, reason })
            });
            return parseBody(resp);
        },
        bulkResume: async (tenantIds: string[], reason?: string): Promise<{ succeeded: string[]; failed: Array<{ tenantId: string; error: string }>; total: number }> => {
            const resp = await superAdminRequest(`/admin/bulk/resume`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tenantIds, reason })
            });
            return parseBody(resp);
        },
        bulkSendReminder: async (tenantIds: string[], subject?: string, message?: string): Promise<{ succeeded: string[]; failed: Array<{ tenantId: string; error: string }>; total: number }> => {
            const resp = await superAdminRequest(`/admin/bulk/send-reminder`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tenantIds, subject, message })
            });
            return parseBody(resp);
        },
        getTenantAuditLog: async (tenantId: string, limit?: number): Promise<{ events: Array<{ timestamp: number; action: string; performedBy: string; previousValue?: any; newValue?: any }> }> => {
            const url = limit ? `/admin/tenants/${tenantId}/audit-log?limit=${limit}` : `/admin/tenants/${tenantId}/audit-log`;
            const resp = await superAdminRequest(url);
            return parseBody(resp);
        },
        getTenantInvoices: async (tenantId: string, limit?: number): Promise<{ invoices: Array<{ id: string; number?: string; status: string; amount: number; amountPaid: number; currency: string; periodStart: number; periodEnd: number; hostedInvoiceUrl?: string; invoicePdf?: string;
            /** The subscription that raised it, or null for a one-time invoice. Additive — see `bulkOps.getTenantInvoices`. */
            subscription?: string | null;
            /** Stripe's own reason — `subscription_create`, `manual`, and so on. */
            billingReason?: string | null;
            /** The store, when billing-service raised it. A Stripe renewal carries none — place it by `subscription`. */
            locationId?: string | null;
            /** The invoice's own lines — what it actually charged for. */
            lines?: Array<{ description: string | null; amount: number; quantity: number | null; periodStart: number | null; periodEnd: number | null }> }> }> => {
            const url = limit ? `/admin/tenants/${tenantId}/invoices?limit=${limit}` : `/admin/tenants/${tenantId}/invoices`;
            const resp = await superAdminRequest(url);
            return parseBody(resp);
        },
        applyTenantCredit: async (tenantId: string, amount: number, reason?: string): Promise<{ success: boolean; transaction: { id: string; amount: number; endingBalance: number } }> => {
            const resp = await superAdminRequest(`/admin/tenants/${tenantId}/credit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ amount, reason })
            });
            return parseBody(resp);
        },
        // At-Risk Tenants & Grace Period Management
        getAtRiskTenants: async (params?: { status?: 'grace' | 'suspended' | 'all'; limit?: number }): Promise<{
            tenants: Array<{
                tenantId: string;
                businessName: string;
                ownerEmail: string;
                status: string;
                subscriptionStatus: string;
                subscriptionCost: number;
                graceUntil: number;
                daysRemaining: number;
                lastPaymentAt?: number;
                lastPaymentAmount?: number;
                stripeCustomerId?: string;
            }>;
            summary: {
                totalAtRisk: number;
                totalGrace: number;
                totalSuspended: number;
                totalAmountAtRisk: number;
                expiringThisWeek: number;
            };
        }> => {
            // NOTE: These tenant management endpoints are served by super-admin-ext (support.api.*)
            const search = new URLSearchParams();
            if (params?.status) search.append('status', params.status);
            if (params?.limit) search.append('limit', String(params.limit));
            const resp = await superAdminExtRequest(`/admin/tenants/at-risk${search.toString() ? `?${search.toString()}` : ''}`);
            return parseBody(resp);
        },
        extendGracePeriod: async (tenantId: string, days: number, reason?: string): Promise<{ success: boolean; tenantId: string; previousGraceUntil: number; newGraceUntil: number; daysAdded: number }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/extend-grace`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ days, reason })
            });
            return parseBody(resp);
        },
        sendPaymentReminder: async (tenantId: string): Promise<{ success: boolean; sentTo: string; tenantId: string }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/send-reminder`, { method: 'POST' });
            return parseBody(resp);
        },
        blockTenantAccess: async (tenantId: string, reason?: string): Promise<{ success: boolean; tenantId: string; accessBlocked: boolean; disabledUsers: number; failedUsers: number; message: string }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/block-access`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason })
            });
            return parseBody(resp);
        },
        restoreTenantAccess: async (tenantId: string, reason?: string): Promise<{ success: boolean; tenantId: string; accessBlocked: boolean; enabledUsers: number; failedUsers: number; message: string }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/restore-access`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason })
            });
            return parseBody(resp);
        },
        // Get admin activity for a tenant (impersonation logs, password resets, etc.)
        getTenantAdminActivity: async (tenantId: string, limit?: number): Promise<{ events: Array<{ type: string; admin?: string; performedBy?: string; requestedAt?: number; createdAt?: number; reason?: string; expiresAt?: number }>; count: number; tenantId: string }> => {
            const url = limit ? `/admin/tenants/${tenantId}/admin-activity?limit=${limit}` : `/admin/tenants/${tenantId}/admin-activity`;
            const resp = await superAdminExtRequest(url);
            return parseBody(resp);
        },
        // Cancellation request management (super-admin-ext)
        getCancellationRequests: async (status?: 'pending' | 'all' | 'approved' | 'denied'): Promise<{
            requests: Array<{
                tenantId: string;
                businessName: string;
                ownerEmail: string;
                subscriptionStatus: string;
                currentPeriodEnd?: number;
                cancellationRequestedAt?: number;
                cancellationRequestedBy?: string;
                cancellationRequestReason?: string;
                cancellationRequestStatus?: 'pending' | 'approved' | 'denied';
                cancellationDeniedAt?: number;
                cancellationDeniedReason?: string;
            }>;
            total: number;
            pending: number;
        }> => {
            const search = status ? `?status=${status}` : '';
            const resp = await superAdminExtRequest(`/admin/cancellation-requests${search}`);
            return parseBody(resp);
        },
        approveCancellationRequest: async (tenantId: string): Promise<{ success: boolean; message: string; cancelAtPeriodEnd: boolean; graceUntil: number }> => {
            const resp = await superAdminExtRequest(`/admin/cancellation-requests/${tenantId}/approve`, { method: 'POST' });
            return parseBody(resp);
        },
        denyCancellationRequest: async (tenantId: string, reason?: string): Promise<{ success: boolean; message: string; cancellationRequestStatus: string; denyReason: string }> => {
            const resp = await superAdminExtRequest(`/admin/cancellation-requests/${tenantId}/deny`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason })
            });
            return parseBody(resp);
        },
        // AI Settings
        getAISettings: async (): Promise<{ configured: boolean; provider: string | null; model: string | null; apiKeyMasked: string | null; googleApiKeyMasked: string | null; removeBgApiKeyMasked: string | null; vertexProjectId: string | null; vertexRegion: string | null; vertexServiceAccountKeyMasked: string | null; updatedAt?: number; updatedBy?: string }> => {
            const resp = await superAdminRequest(`/admin/settings/ai`);
            return parseBody(resp);
        },
        saveAISettings: async (data: { provider?: string; googleApiKey?: string; removeBgApiKey?: string; apiKey?: string; model?: string; vertexProjectId?: string; vertexRegion?: string; vertexServiceAccountKey?: string }): Promise<{ success: boolean; message: string; provider: string; googleApiKeyMasked?: string; removeBgApiKeyMasked?: string; vertexProjectId?: string; vertexRegion?: string; vertexServiceAccountKeyMasked?: string }> => {
            const resp = await superAdminRequest(`/admin/settings/ai`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },
        deleteAISettings: async (): Promise<{ success: boolean; message: string }> => {
            const resp = await superAdminRequest(`/admin/settings/ai`, { method: 'DELETE' });
            return parseBody(resp);
        },
        testAISettings: async (): Promise<{ success: boolean; message: string; testImageUrl?: string }> => {
            const resp = await superAdminRequest(`/admin/settings/ai/test`, { method: 'POST' });
            return parseBody(resp);
        },
        getAIProviders: async (): Promise<{ providers: Array<{ id: string; name: string; models: Array<{ id: string; name: string; description: string; costPerImage: string }>; requiresKey: boolean; signupUrl: string }> }> => {
            const resp = await superAdminRequest(`/admin/settings/ai/providers`);
            return parseBody(resp);
        },
        getAIStats: async (): Promise<{
            health: 'healthy' | 'warning' | 'critical' | 'unconfigured';
            healthMessage: string;
            provider: string;
            isConfigured: boolean;
            stats: {
                totalGenerations: number;
                successCount: number;
                failureCount: number;
                apiErrorCount: number;
                successRate: number;
                lastSuccess: number | null;
                lastSuccessProvider: string | null;
                lastFailure: number | null;
                lastFailureProvider: string | null;
                lastError: string | null;
                lastApiError: number | null;
                lastApiErrorMessage: string | null;
            };
            recentErrors: Array<{
                timestamp: number;
                provider: string;
                error: string;
                isApiError: boolean;
            }>;
            updatedAt: number | null;
        }> => {
            const resp = await superAdminRequest(`/admin/settings/ai/stats`);
            return parseBody(resp);
        },
        clearAIStats: async (): Promise<{ success: boolean; message: string }> => {
            const resp = await superAdminRequest(`/admin/settings/ai/stats/clear`, { method: 'POST' });
            return parseBody(resp);
        },

        // Trial Settings (Landing Page Options)
        getTrialSettings: async (): Promise<{
            showTrialOnLandingPage: boolean;
            showPaidPlanOnLandingPage: boolean;
            enableAffiliateTrials: boolean;
            trialDays: number;
        }> => {
            // Public endpoint - no auth required (for landing page)
            const resp = await publicSuperAdminRequest(`/admin/settings/trial`);
            return parseBody(resp);
        },
        updateTrialSettings: async (data: {
            showTrialOnLandingPage?: boolean;
            showPaidPlanOnLandingPage?: boolean;
            enableAffiliateTrials?: boolean;
            trialDays?: number;
        }): Promise<{
            showTrialOnLandingPage: boolean;
            showPaidPlanOnLandingPage: boolean;
            enableAffiliateTrials: boolean;
            trialDays: number;
        }> => {
            const resp = await superAdminRequest(`/admin/settings/trial`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },
        // Trial checkout session for 30-day free trial
        createTrialCheckoutSession: async (payload: { email: string; businessName: string; password?: string; priceId?: string; referralCode?: string }): Promise<{ url: string; id: string; isTrial: boolean }> => {
            const resp = await publicSuperAdminRequest(`/admin/stripe/checkout-trial`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            return parseBody(resp);
        },

        // AI Insights & Usage Tracking
        getAIInsightsOverview: async (params?: { period?: string }): Promise<any> => {
            const search = new URLSearchParams();
            if (params?.period) search.append('period', params.period);
            const resp = await superAdminRequest(`/admin/ai/insights/overview${search.toString() ? `?${search}` : ''}`);
            return parseBody(resp);
        },
        getAIUsageReport: async (params?: { period?: string; startDate?: string; endDate?: string }): Promise<any> => {
            const search = new URLSearchParams();
            if (params?.period) search.append('period', params.period);
            if (params?.startDate) search.append('startDate', params.startDate);
            if (params?.endDate) search.append('endDate', params.endDate);
            const resp = await superAdminRequest(`/admin/ai/insights/usage${search.toString() ? `?${search}` : ''}`);
            return parseBody(resp);
        },
        getTenantUsageReport: async (params?: { period?: string }): Promise<any> => {
            const search = new URLSearchParams();
            if (params?.period) search.append('period', params.period);
            const resp = await superAdminRequest(`/admin/ai/insights/tenants${search.toString() ? `?${search}` : ''}`);
            return parseBody(resp);
        },
        getAPIHealth: async (): Promise<any> => {
            const resp = await superAdminRequest(`/admin/ai/insights/health`);
            return parseBody(resp);
        },
        
        // AI Providers Management (/admin/ai-providers/*) removed 2026-10-05 (Task 27, R49):
        // the routes are gone; the AI Control Center Providers tab (aiControlApi) replaced them.
        
        // AI Dashboard APIs
        getAIDashboardData: async (params?: { 
            period?: 'today' | 'week' | 'month' | 'quarter' | 'year'; 
            startDate?: string; 
            endDate?: string 
        }): Promise<{
            overview: {
                totalCost: number;
                totalOperations: number;
                successRate: number;
                activeTenants: number;
                costChange: number;
                operationsChange: number;
            };
            tenants: Array<{
                tenantId: string;
                businessName: string;
                totalCost: number;
                totalOperations: number;
                successRate: number;
                lastUsed: number;
                byService: Record<string, { operations: number; cost: number; successRate: number }>;
                byProvider: Record<string, { operations: number; cost: number }>;
            }>;
            providers: Array<{
                id: string;
                name: string;
                status: 'healthy' | 'degraded' | 'down' | 'unconfigured';
                successRate: number;
                avgLatencyMs: number;
                totalRequests24h: number;
                costPerRequest: number;
                errorMessage?: string;
                isConfigured: boolean;
            }>;
            providerCosts: Array<{
                providerId: string;
                providerName: string;
                totalCost: number;
                totalRequests: number;
                successfulRequests: number;
                avgCostPerRequest: number;
            }>;
            dailyTrend: Array<{
                date: string;
                operations: number;
                cost: number;
                successRate: number;
            }>;
            downtimeEvents: Array<{
                id: string;
                providerId: string;
                providerName: string;
                startTime: number;
                endTime?: number;
                durationMinutes: number;
                isOngoing: boolean;
                errorMessage: string;
            }>;
            totalDowntimeMinutes: number;
        }> => {
            const search = new URLSearchParams();
            if (params?.period) search.append('period', params.period);
            if (params?.startDate) search.append('startDate', params.startDate);
            if (params?.endDate) search.append('endDate', params.endDate);
            const resp = await superAdminRequest(`/admin/ai/dashboard${search.toString() ? `?${search}` : ''}`);
            return parseBody(resp);
        },
        
        getTenantAIUsageDetail: async (tenantId: string, params?: { period?: string; startDate?: string; endDate?: string }): Promise<{
            tenantId: string;
            businessName: string;
            totalCost: number;
            totalOperations: number;
            successRate: number;
            lastUsed: number;
            byService: Array<{ service: string; operations: number; cost: number; successRate: number }>;
            byProvider: Array<{ provider: string; operations: number; cost: number }>;
            dailyUsage: Array<{ date: string; operations: number; cost: number }>;
            recentEvents: Array<{ timestamp: number; service: string; provider: string; cost: number; success: boolean; error?: string }>;
        }> => {
            const search = new URLSearchParams();
            if (params?.period) search.append('period', params.period);
            if (params?.startDate) search.append('startDate', params.startDate);
            if (params?.endDate) search.append('endDate', params.endDate);
            const resp = await superAdminRequest(`/admin/ai/tenants/${tenantId}${search.toString() ? `?${search}` : ''}`);
            return parseBody(resp);
        },
        
        getProviderHealthDetail: async (providerId: string): Promise<{
            id: string;
            name: string;
            status: 'healthy' | 'degraded' | 'down' | 'unconfigured';
            successRate: number;
            avgLatencyMs: number;
            totalRequests24h: number;
            errorMessage?: string;
            isConfigured: boolean;
            recentErrors: Array<{ timestamp: number; error: string; requestId?: string }>;
            latencyHistory: Array<{ timestamp: number; latencyMs: number }>;
            uptimePercent24h: number;
        }> => {
            const resp = await superAdminRequest(`/admin/ai/providers/${providerId}/health`);
            return parseBody(resp);
        },
        
        testProviderConnection: async (providerId: string): Promise<{
            success: boolean;
            latencyMs?: number;
            message: string;
            errorDetails?: string;
            testedAt: number;
        }> => {
            const resp = await superAdminRequest(`/admin/ai/providers/${providerId}/test`, { method: 'POST' });
            return parseBody(resp);
        },
        
        exportAIUsageReport: async (params?: { period?: string; startDate?: string; endDate?: string; format?: 'csv' | 'json' }): Promise<{ downloadUrl: string } | Blob> => {
            const search = new URLSearchParams();
            if (params?.period) search.append('period', params.period);
            if (params?.startDate) search.append('startDate', params.startDate);
            if (params?.endDate) search.append('endDate', params.endDate);
            if (params?.format) search.append('format', params.format);
            const resp = await superAdminRequest(`/admin/ai/export${search.toString() ? `?${search}` : ''}`);
            if (params?.format === 'csv') {
                return (resp as Response).blob();
            }
            return parseBody(resp as Response);
        },

        // Creator Studio Payment Hub Settings (super-admin-ext)
        getCreatorStudioSettings: async (): Promise<{
            videoPaymentEnabled: boolean;
            videoPriceId: string | null;
            videoPrice: number | null;
            videoProductName: string | null;
            imagePaymentEnabled: boolean;
            imagePriceId: string | null;
            imagePrice: number | null;
            imageProductName: string | null;
            updatedAt: number | null;
            updatedBy: string | null;
        }> => {
            const resp = await superAdminExtRequest(`/admin/creator-studio/settings`);
            return parseBody(resp);
        },
        saveCreatorStudioSettings: async (data: {
            videoPaymentEnabled?: boolean;
            videoPriceId?: string | null;
            imagePaymentEnabled?: boolean;
            imagePriceId?: string | null;
        }): Promise<{
            success: boolean;
            message: string;
            videoPaymentEnabled: boolean;
            videoPriceId: string | null;
            videoPrice: number | null;
            videoProductName: string | null;
            imagePaymentEnabled: boolean;
            imagePriceId: string | null;
            imagePrice: number | null;
            imageProductName: string | null;
        }> => {
            const resp = await superAdminExtRequest(`/admin/creator-studio/settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },
        getCreatorStudioStripeProducts: async (): Promise<{
            products: Array<{
                productId: string;
                productName: string;
                productDescription: string | null;
                prices: Array<{
                    priceId: string;
                    amount: number;
                    currency: string;
                    nickname: string | null;
                }>;
            }>;
            totalProducts: number;
            totalPrices: number;
        }> => {
            const resp = await superAdminExtRequest(`/admin/creator-studio/stripe-products`);
            return parseBody(resp);
        },
        // ============================================
        // CREDIT SYSTEM ADMIN APIs
        // ============================================
        getCreditSettings: async (): Promise<{
            creditSystemEnabled: boolean;
            defaultChargeEnabled: boolean;
            videoPriceCredits: number;
            imagePriceCredits: number;
            compositionPriceCredits: number;
        }> => {
            const resp = await superAdminExtRequest(`/admin/credits/settings`);
            return parseBody(resp);
        },
        saveCreditSettings: async (data: {
            creditSystemEnabled?: boolean;
            defaultChargeEnabled?: boolean;
            videoPriceCredits?: number;
            imagePriceCredits?: number;
            compositionPriceCredits?: number;
        }): Promise<{
            success: boolean;
            message: string;
            creditSystemEnabled: boolean;
            defaultChargeEnabled: boolean;
            videoPriceCredits: number;
            imagePriceCredits: number;
            compositionPriceCredits: number;
        }> => {
            const resp = await superAdminExtRequest(`/admin/credits/settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },
        getTenantCredits: async (tenantId: string): Promise<{
            tenantId: string;
            balance: number;
            totalLoaded: number;
            totalSpent: number;
            chargeEnabled: boolean;
            transactions: Array<{
                transactionId: string;
                type: string;
                amount: number;
                balanceAfter: number;
                status: string;
                createdAt: number;
            }>;
        }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/credits`);
            return parseBody(resp);
        },
        loadTenantCredits: async (tenantId: string, amount: number, note?: string): Promise<{
            success: boolean;
            transactionId: string;
            newBalance: number;
        }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/credits/load`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ amount, note })
            });
            return parseBody(resp);
        },
        toggleTenantCharging: async (tenantId: string, settings: {
            chargeEnabled?: boolean;
            videoPriceCredits?: number;
            imagePriceCredits?: number;
            compositionPriceCredits?: number;
        }): Promise<{
            success: boolean;
            tenantId: string;
            chargeEnabled: boolean;
            videoPriceCredits: number;
            imagePriceCredits: number;
            compositionPriceCredits: number;
            balance: number;
        }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/credits/charging`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings)
            });
            return parseBody(resp);
        },
        getCreditReports: async (period?: 'week' | 'month'): Promise<{
            periodStart: number;
            periodEnd: number;
            summary: {
                totalLoaded: number;
                totalSpent: number;
                netBalance: number;
            };
            byTenant: Array<{
                tenantId: string;
                businessName: string;
                loaded: number;
                spent: number;
                balance: number;
            }>;
            byType: {
                load: number;
                spend: number;
                admin_load: number;
                refund: number;
            };
        }> => {
            const search = period ? `?period=${period}` : '';
            const resp = await superAdminExtRequest(`/admin/credits/reports${search}`);
            return parseBody(resp);
        },
        // Migration: Unlock all content for a tenant (prepaid credit system migration)
        unlockTenantContent: async (tenantId: string): Promise<{
            message: string;
            updated: number;
            total: number;
        }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/credits/unlock-content`, {
                method: 'POST'
            });
            return parseBody(resp);
        },
        // ============================================
        // TENANT PACKAGE MANAGEMENT APIs
        // ============================================
        // Get all packages assigned to a tenant
        getTenantPackages: async (tenantId: string): Promise<{
            tenant: {
                id: string;
                name: string;
                productType?: string;
                enabledPackages?: string[];
                legacyPlan?: boolean;
                dealCode?: string;
            };
            packages: Array<{
                tenantId: string;
                packageId: string;
                status: string;
                assignedAt: number;
                assignedBy: string;
                canceledAt?: number;
            }>;
            packageDetails: Array<{
                id: string;
                name: string;
                description: string;
                features: string[];
                isCore?: boolean;
            }>;
            // All available packages from PACKAGES_TABLE for "Add Package" UI
            allPackages: Array<{
                id: string;
                name: string;
                description: string;
                features: string[];
                isCore?: boolean;
                productType?: string;
                category?: string;
            }>;
        }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/packages`);
            return parseBody(resp);
        },
        // Assign a package to a tenant
        assignTenantPackage: async (tenantId: string, packageId: string): Promise<{
            tenantPackage: {
                tenantId: string;
                packageId: string;
                status: string;
                assignedAt: number;
                assignedBy: string;
            };
        }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/packages`, {
                method: 'POST',
                body: JSON.stringify({ packageId })
            });
            return parseBody(resp);
        },
        // Remove a package from a tenant
        removeTenantPackage: async (tenantId: string, packageId: string): Promise<{
            message: string;
        }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/packages/${packageId}`, {
                method: 'DELETE'
            });
            return parseBody(resp);
        },
        // Update a tenant's product type (or set to null for "No Product")
        updateTenantProduct: async (tenantId: string, productType: string | null): Promise<{
            tenantId: string;
            productType: string | null;
            tenant: {
                id: string;
                name: string;
                productType?: string;
                updatedAt: number;
            };
        }> => {
            const resp = await superAdminExtRequest(`/admin/tenants/${tenantId}/product`, {
                method: 'PUT',
                body: JSON.stringify({ productType })
            });
            return parseBody(resp);
        }
    },

    // Admin self-service billing APIs
    billing: {
        getMySubscription: async (): Promise<{
            hasSubscription: boolean;
            subscriptionId?: string;
            status?: string;
            currentPeriodStart?: number;
            currentPeriodEnd?: number;
            cancelAtPeriodEnd?: boolean;
            plan?: { id: string; nickname?: string; amount?: number; currency: string; interval?: string };
            paymentMethod?: { brand: string; last4: string; expMonth: number; expYear: number };
            // Cancellation request fields
            cancellationRequestStatus?: 'pending' | 'approved' | 'denied' | null;
            cancellationRequestedAt?: number;
            cancellationRequestedBy?: string;
            cancellationRequestReason?: string;
            cancellationDeniedReason?: string;
        }> => {
            const resp = await superAdminRequest(`/billing/subscription`);
            return parseBody(resp);
        },
        getMyInvoices: async (params?: { limit?: number; startingAfter?: string }): Promise<{ invoices: Array<{ id: string; number?: string; status: string; amount: number; amountPaid: number; currency: string; periodStart: number; periodEnd: number; paidAt?: number; hostedInvoiceUrl?: string; invoicePdf?: string }>; hasMore?: boolean }> => {
            const search = new URLSearchParams();
            if (params?.limit) search.append('limit', String(params.limit));
            if (params?.startingAfter) search.append('startingAfter', params.startingAfter);
            const resp = await superAdminRequest(`/billing/invoices${search.toString() ? `?${search}` : ''}`);
            return parseBody(resp);
        },
        getUpcomingInvoice: async (): Promise<{ hasUpcoming: boolean; amount?: number; currency?: string; periodStart?: number; periodEnd?: number; dueDate?: number; lines?: Array<{ description: string; amount: number; quantity: number }> }> => {
            const resp = await superAdminRequest(`/billing/upcoming`);
            return parseBody(resp);
        },
        updatePaymentMethod: async (paymentMethodId: string, tenantId?: string): Promise<{ success: boolean; paymentMethod?: { brand: string; last4: string; expMonth: number; expYear: number } }> => {
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (tenantId) {
                headers['x-tenant-id'] = tenantId;
            }
            const resp = await superAdminRequest(`/billing/payment-method`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ paymentMethodId })
            });
            return parseBody(resp);
        },
        cancelSubscription: async (reason?: string): Promise<{ success: boolean; cancelAtPeriodEnd: boolean; currentPeriodEnd: number; status: string }> => {
            const resp = await superAdminRequest(`/billing/cancel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason })
            });
            return parseBody(resp);
        },
        reactivateSubscription: async (): Promise<{ success: boolean; cancelAtPeriodEnd: boolean; currentPeriodEnd: number; status: string }> => {
            const resp = await superAdminRequest(`/billing/reactivate`, { method: 'POST' });
            return parseBody(resp);
        },
        getBillingPortalUrl: async (returnUrl?: string): Promise<{ url: string }> => {
            const search = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
            const resp = await superAdminRequest(`/billing/portal${search}`);
            return parseBody(resp);
        },
        // Cancellation request workflow (uses super-admin-ext)
        requestCancellation: async (reason?: string): Promise<{ success: boolean; message: string; cancellationRequestStatus: string; cancellationRequestedAt: number }> => {
            const resp = await superAdminExtRequest(`/billing/cancel-request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason })
            });
            return parseBody(resp);
        },
        withdrawCancellationRequest: async (): Promise<{ success: boolean; message: string }> => {
            const resp = await superAdminExtRequest(`/billing/cancel-request`, { method: 'DELETE' });
            return parseBody(resp);
        }
    },

    onboard: {
        createWorkspace: async (data: any) => {
            return tenantsRequest(`/onboard`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
        }
    },

    tenants: {
        getMetadata: async (tid: string, opts?: { silent?: boolean }): Promise<BusinessProfile> => {
            const ensureProfile = (data: any): BusinessProfile => ({
                name: data?.name || 'My Cafe',
                address: data?.address || '',
                phone: data?.phone || '',
                email: data?.email || data?.ownerEmail || '',
                taxId: data?.taxId || '',
                logoUrl: data?.logoUrl || '',
                themeColor: data?.themeColor || 'light',
                notificationEmails: data?.notificationEmails || [],
                ownerName: data?.ownerName || '',
                ownerEmail: data?.ownerEmail || data?.email || '',
                ownerPhone: data?.ownerPhone || '',
                storeHours: data?.storeHours || undefined
            });

            if (isDemoMode()) {
                return ensureProfile(db.settings.getProfile(tid));
            }

            const resp = await tenantsRequest(`/tenant/${tid}/profile`, { silent: opts?.silent });
            const parsed = parseBody<BusinessProfile>(resp);
            return ensureProfile(parsed);
        },
        updateMetadata: async (tid: string, profile: BusinessProfile): Promise<BusinessProfile> => {
            const ensureProfile = (data: any): BusinessProfile => ({
                name: data?.name || profile.name || 'My Cafe',
                address: data?.address ?? profile.address ?? '',
                phone: data?.phone ?? profile.phone ?? '',
                email: data?.email ?? profile.email ?? '',
                taxId: data?.taxId ?? profile.taxId ?? '',
                logoUrl: data?.logoUrl ?? profile.logoUrl ?? '',
                themeColor: data?.themeColor || profile.themeColor || 'light',
                notificationEmails: data?.notificationEmails || profile.notificationEmails || [],
                storeHours: data?.storeHours || profile.storeHours || undefined
            });

            if (isDemoMode()) {
                db.settings.updateProfile(tid, profile);
                return ensureProfile(profile);
            }

            const resp = await tenantsRequest(`/tenant/${tid}/profile`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profile)
            });
            const parsed = parseBody<BusinessProfile>(resp);
            return ensureProfile(parsed);
        },
        getAll: async (): Promise<Tenant[]> => {
            const response: any = await tenantsRequest(`/tenants`);
            if (response.body && typeof response.body === 'string') {
                return JSON.parse(response.body);
            }
            return response as Tenant[];
        },
        // Get summaries for multiple tenants (for business switcher) including user's role
        getSummaries: async (tenantIds: string[], userEmail?: string): Promise<TenantSummary[]> => {
            if (!tenantIds || tenantIds.length === 0) return [];
            
            // Fetch each tenant's basic info and user's role in parallel
            const summaries = await Promise.all(
                tenantIds.map(async (tid) => {
                    try {
                        // Get raw tenant data (not normalized) to access ownerEmail
                        const rawResp = await tenantsRequest(`/tenant/${tid}/profile`);
                        const rawTenant = parseBody<any>(rawResp);
                        
                        const profile = {
                            name: rawTenant?.name || 'My Cafe',
                            logoUrl: rawTenant?.logoUrl || '',
                            email: rawTenant?.email || '',
                            ownerEmail: rawTenant?.ownerEmail || ''
                        };
                        
                        let userRole: string | undefined;
                        
                        // If we have user email, determine their role in this tenant
                        if (userEmail) {
                            const normalizedEmail = userEmail.toLowerCase();
                            
                            // First check if user is the tenant OWNER (from tenant record)
                            // Tenant owners are admins of their own business
                            const isOwner = (profile.ownerEmail && profile.ownerEmail.toLowerCase() === normalizedEmail) ||
                                           (profile.email && profile.email.toLowerCase() === normalizedEmail);
                            
                            if (isOwner) {
                                userRole = 'admin';
                                console.log(`[getSummaries] User ${userEmail} is owner of tenant ${tid} (matched: ownerEmail=${profile.ownerEmail}, email=${profile.email})`);
                            } else {
                                // Not the owner - check Users table for their role as employee/team member
                                try {
                                    const usersResp: any = await usersRequest(`/tenant/${tid}/users`);
                                    const users = Array.isArray(usersResp) ? usersResp : (usersResp.items || usersResp.users || []);
                                    const userRecord = users.find((u: any) => u.email?.toLowerCase() === normalizedEmail);
                                    if (userRecord) {
                                        userRole = userRecord.role;
                                        console.log(`[getSummaries] User ${userEmail} has role ${userRole} in tenant ${tid} (from users table)`);
                                    } else {
                                        console.log(`[getSummaries] User ${userEmail} NOT FOUND in tenant ${tid} users table`);
                                    }
                                } catch (err) {
                                    console.warn(`Failed to fetch user role for tenant ${tid}`, err);
                                }
                            }
                        }
                        
                        return {
                            id: tid,
                            name: profile.name || tid,
                            logoUrl: profile.logoUrl,
                            userRole
                        };
                    } catch (err) {
                        console.warn(`Failed to fetch tenant ${tid}`, err);
                        return { id: tid, name: tid };
                    }
                })
            );
            return summaries;
        },
        update: async (id: string, data: any): Promise<any> => {
            return tenantsRequest(`/tenants/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
        }
    },

    recipes: {
        /**
         * Get all recipes for a tenant
         * By default, returns only PUBLISHED recipes (excludes drafts)
         * Pass { status: 'all' } to include drafts (for Menu Workbench only)
         * Pass { status: 'draft' } to get only drafts
         */
        getAll: async (tid: string, opts?: { status?: 'draft' | 'published' | 'all' }): Promise<Recipe[]> => {
            // Default to 'published' if no status specified (everywhere except Menu Workbench)
            const statusToUse = opts?.status || 'published';
            
            if (isDemoMode()) {
                const list = db.recipes.getAll(tid) as Recipe[];
                if (statusToUse === 'published') return list.filter(r => r.status !== 'draft');
                if (statusToUse === 'draft') return list.filter(r => r.status === 'draft');
                return list; // 'all'
            }
            const query = `?status=${statusToUse}`;
            const resp = await recipesRequest(`/tenant/${tid}/recipes${query}`);
            const parsed = parseBody<Recipe[]>(resp) || [];
            console.log('[Recipes getAll]', { tid, status: statusToUse, count: Array.isArray(parsed) ? parsed.length : 0, sample: parsed?.[0] });
            return parsed;
        },
        /**
         * Get recipes with pagination support - ideal for large datasets (500+ recipes)
         * By default, returns only PUBLISHED recipes (excludes drafts)
         * Pass { status: 'all' } to include drafts (for Menu Workbench only)
         * @param tid Tenant ID
         * @param opts Options including status filter, limit, and cursor
         * @returns Paginated response with items, nextCursor, hasMore, and count
         */
        getPaginated: async (tid: string, opts?: {
            status?: 'draft' | 'published' | 'all';
            limit?: number;
            cursor?: string | null;
            silent?: boolean;
        }): Promise<{ items: Recipe[]; nextCursor: string | null; hasMore: boolean; count: number }> => {
            // Default to 'published' if no status specified
            const statusToUse = opts?.status || 'published';

            if (isDemoMode()) {
                const list = db.recipes.getAll(tid) as Recipe[];
                let filtered = list;
                if (statusToUse === 'published') filtered = list.filter(r => r.status !== 'draft');
                else if (statusToUse === 'draft') filtered = list.filter(r => r.status === 'draft');
                return { items: filtered, nextCursor: null, hasMore: false, count: filtered.length };
            }
            const params = new URLSearchParams();
            params.set('paginate', 'true');
            params.set('status', statusToUse);
            if (opts?.limit) params.set('limit', String(opts.limit));
            if (opts?.cursor) params.set('cursor', opts.cursor);

            const resp = await recipesRequest(`/tenant/${tid}/recipes?${params.toString()}`, { silent: opts?.silent });
            const parsed = parseBody<{ items: Recipe[]; nextCursor: string | null; hasMore: boolean; count: number }>(resp);
            console.log('[Recipes getPaginated]', { tid, status: statusToUse, count: parsed?.count, hasMore: parsed?.hasMore });
            return parsed || { items: [], nextCursor: null, hasMore: false, count: 0 };
        },
        get: async (tid: string, id: string): Promise<Recipe | null> => {
            if (isDemoMode()) return db.recipes.getAll(tid).find(r => r.id === id) || null;
            const resp = await recipesRequest(`/tenant/${tid}/recipes/${id}`);
            return parseBody<Recipe>(resp) || null;
        },
        save: async (tid: string, recipe: Partial<Recipe>, options?: { silent?: boolean }): Promise<Recipe> => {
            if (isDemoMode()) { db.recipes.add(recipe as any); return recipe as Recipe; }
            const method = recipe.id ? 'PUT' : 'POST';
            const path = recipe.id ? `/tenant/${tid}/recipes/${recipe.id}` : `/tenant/${tid}/recipes`;
            return recipesRequest(path, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(recipe),
                silent: options?.silent
            }) as Promise<Recipe>;
        },
        publish: async (tid: string, id: string, notifyTeam: boolean = false): Promise<Recipe> => {
            console.log('[API] recipes.publish', { tid, id, notifyTeam });
            if (isDemoMode()) {
                const existing = db.recipes.getAll(tid).find((r: Recipe) => r.id === id) as any;
                if (existing) {
                    existing.status = 'published';
                    existing.publishedAt = Date.now();
                    db.recipes.update(existing);
                    return existing as Recipe;
                }
                throw new Error('Recipe not found');
            }
            console.log('[API] Sending POST request to publish endpoint', {
                url: `/tenant/${tid}/recipes/${id}/publish`,
                body: { notifyTeam }
            });
            const resp = await recipesRequest(`/tenant/${tid}/recipes/${id}/publish`, { 
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ notifyTeam })
            });
            const result = parseBody<Recipe>(resp);
            console.log('[API] Publish response received', { result });
            return result;
        },
        delete: async (tid: string, id: string): Promise<void> => {
            if (isDemoMode()) { db.recipes.delete(id); return; }
            await recipesRequest(`/tenant/${tid}/recipes/${id}`, { method: 'DELETE' });
        }
    },

    inventory: {
        getAll: async (tid: string): Promise<InventoryItem[]> => {
            if (isDemoMode()) return db.inventory.getAll(tid);
            const resp = await inventoryRequest(`/tenant/${tid}/inventory`);
            return parseBody<InventoryItem[]>(resp) || [];
        },
        create: async (tid: string, item: Partial<InventoryItem>): Promise<InventoryItem> => {
            if (isDemoMode()) { db.inventory.add(item as any); return item as InventoryItem; }
            const resp = await inventoryRequest(`/tenant/${tid}/inventory`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(item)
            });
            return parseBody<InventoryItem>(resp);
        },
        save: async (tid: string, item: Partial<InventoryItem>): Promise<InventoryItem> => {
            if (isDemoMode()) { db.inventory.add(item as any); return item as InventoryItem; }
            return inventoryRequest(`/tenant/${tid}/inventory`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(item)
            }) as Promise<InventoryItem>;
        },
        update: async (tid: string, id: string, item: Partial<InventoryItem>): Promise<InventoryItem> => {
            if (isDemoMode()) { db.inventory.add({ ...item, id } as any); return item as InventoryItem; }
            const resp = await inventoryRequest(`/tenant/${tid}/inventory/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(item)
            });
            return parseBody<InventoryItem>(resp);
        },
        delete: async (tid: string, id: string): Promise<void> => {
            if (isDemoMode()) { db.inventory.delete(id); return; }
            await inventoryRequest(`/tenant/${tid}/inventory/${id}`, { method: 'DELETE' });
        }
    },

    // Supplies (cups, napkins, lids, etc.)
    supplies: {
        getAll: async (tid: string): Promise<Supply[]> => {
            if (isDemoMode()) return db.supplies?.getAll(tid) || [];
            const resp = await inventoryRequest(`/tenant/${tid}/supplies`);
            return parseBody<Supply[]>(resp) || [];
        },
        create: async (tid: string, supply: Partial<Supply>): Promise<Supply> => {
            if (isDemoMode()) { db.supplies?.add(supply as any); return supply as Supply; }
            const resp = await inventoryRequest(`/tenant/${tid}/supplies`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(supply)
            });
            return parseBody<Supply>(resp);
        },
        update: async (tid: string, id: string, supply: Partial<Supply>): Promise<Supply> => {
            if (isDemoMode()) { db.supplies?.update(supply as any); return supply as Supply; }
            const resp = await inventoryRequest(`/tenant/${tid}/supplies/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(supply)
            });
            return parseBody<Supply>(resp);
        },
        delete: async (tid: string, id: string): Promise<void> => {
            if (isDemoMode()) { db.supplies?.delete(id); return; }
            await inventoryRequest(`/tenant/${tid}/supplies/${id}`, { method: 'DELETE' });
        },
        // Update stock for a specific variation (size) within a supply
        updateVariationStock: async (tid: string, supplyId: string, sizeId: string, data: { stockQuantity: number }): Promise<Supply> => {
            if (isDemoMode()) {
                // In demo mode, update the variation's stock quantity
                const supplies = db.supplies?.getAll(tid) || [];
                const supply = supplies.find((s: any) => s.id === supplyId);
                if (supply && supply.sizes) {
                    const sizeIndex = supply.sizes.findIndex((s: any) => s.id === sizeId);
                    if (sizeIndex >= 0) {
                        supply.sizes[sizeIndex].stockQuantity = data.stockQuantity;
                        db.supplies?.update(supply);
                    }
                }
                return supply as Supply;
            }
            const resp = await inventoryRequest(`/tenant/${tid}/supplies/${supplyId}/sizes/${sizeId}/stock`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody<Supply>(resp);
        }
    },

    stockReports: {
        getAll: async (tid: string, status?: string): Promise<StockReport[]> => {
            if (isDemoMode()) return db.stockReports?.getAll(tid) || [];
            const url = status
                ? `/tenant/${tid}/stock-reports?status=${status}`
                : `/tenant/${tid}/stock-reports`;
            const resp = await inventoryRequest(url);
            return parseBody<StockReport[]>(resp) || [];
        },
        create: async (tid: string, report: Partial<StockReport>): Promise<StockReport> => {
            if (isDemoMode()) {
                const newReport = {
                    ...report,
                    reportId: `report_${Date.now()}`,
                    timestamp: Date.now(),
                    status: 'pending' as const
                } as StockReport;
                db.stockReports?.add(newReport);
                return newReport;
            }
            const resp = await inventoryRequest(`/tenant/${tid}/stock-reports`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(report)
            });
            return parseBody<StockReport>(resp);
        },
        update: async (tid: string, reportId: string, updates: Partial<StockReport>): Promise<StockReport> => {
            if (isDemoMode()) {
                db.stockReports?.update({ ...updates, reportId } as any);
                return { ...updates, reportId } as StockReport;
            }
            const resp = await inventoryRequest(`/tenant/${tid}/stock-reports/${reportId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });
            return parseBody<StockReport>(resp);
        },
        delete: async (tid: string, reportId: string): Promise<void> => {
            if (isDemoMode()) { db.stockReports?.delete(reportId); return; }
            await inventoryRequest(`/tenant/${tid}/stock-reports/${reportId}`, { method: 'DELETE' });
        }
    },

    orderLists: {
        getAll: async (tid: string): Promise<OrderList[]> => {
            if (isDemoMode()) return db.orderLists?.getAll(tid) || [];
            const resp = await inventoryRequest(`/tenant/${tid}/order-lists`);
            return parseBody<OrderList[]>(resp) || [];
        },
        create: async (tid: string, list: Partial<OrderList>): Promise<OrderList> => {
            if (isDemoMode()) {
                const newList = {
                    ...list,
                    id: `orderlist_${Date.now()}`,
                    tenantId: tid,
                    items: [],
                    createdAt: Date.now(),
                } as OrderList;
                db.orderLists?.add(newList);
                return newList;
            }
            const resp = await inventoryRequest(`/tenant/${tid}/order-lists`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(list)
            });
            return parseBody<OrderList>(resp);
        },
        update: async (tid: string, listId: string, updates: Partial<OrderList>): Promise<OrderList> => {
            if (isDemoMode()) {
                db.orderLists?.update({ ...updates, id: listId } as any);
                return { ...updates, id: listId } as OrderList;
            }
            const resp = await inventoryRequest(`/tenant/${tid}/order-lists/${listId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });
            return parseBody<OrderList>(resp);
        },
        delete: async (tid: string, listId: string): Promise<void> => {
            if (isDemoMode()) { db.orderLists?.delete(listId); return; }
            await inventoryRequest(`/tenant/${tid}/order-lists/${listId}`, { method: 'DELETE' });
        },
        addItem: async (tid: string, listId: string, item: Partial<OrderListItem>): Promise<OrderList> => {
            if (isDemoMode()) {
                const list = db.orderLists?.get(listId);
                if (list) {
                    const newItem = { ...item, id: `item_${Date.now()}`, addedAt: Date.now() } as OrderListItem;
                    list.items = [...(list.items || []), newItem];
                    db.orderLists?.update(list);
                    return list;
                }
                return {} as OrderList;
            }
            const resp = await inventoryRequest(`/tenant/${tid}/order-lists/${listId}/items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(item)
            });
            return parseBody<OrderList>(resp);
        },
        removeItem: async (tid: string, listId: string, itemId: string): Promise<OrderList> => {
            if (isDemoMode()) {
                const list = db.orderLists?.get(listId);
                if (list) {
                    list.items = (list.items || []).filter(i => i.id !== itemId);
                    db.orderLists?.update(list);
                    return list;
                }
                return {} as OrderList;
            }
            const resp = await inventoryRequest(`/tenant/${tid}/order-lists/${listId}/items/${itemId}`, {
                method: 'DELETE'
            });
            return parseBody<OrderList>(resp);
        },
        toggleItem: async (tid: string, listId: string, itemId: string): Promise<OrderList> => {
            if (isDemoMode()) {
                const list = db.orderLists?.get(listId);
                if (list) {
                    list.items = (list.items || []).map(i =>
                        i.id === itemId ? { ...i, isChecked: !i.isChecked } : i
                    );
                    db.orderLists?.update(list);
                    return list;
                }
                return {} as OrderList;
            }
            const resp = await inventoryRequest(`/tenant/${tid}/order-lists/${listId}/items/${itemId}/toggle`, {
                method: 'PUT'
            });
            return parseBody<OrderList>(resp);
        },
        clearChecked: async (tid: string, listId: string): Promise<OrderList> => {
            if (isDemoMode()) {
                const list = db.orderLists?.get(listId);
                if (list) {
                    list.items = (list.items || []).filter(i => !i.isChecked);
                    db.orderLists?.update(list);
                    return list;
                }
                return {} as OrderList;
            }
            const resp = await inventoryRequest(`/tenant/${tid}/order-lists/${listId}/clear-checked`, {
                method: 'POST'
            });
            return parseBody<OrderList>(resp);
        }
    },

    shifts: {
        getAll: async (tid: string, role?: string): Promise<Shift[]> => {
            if (isDemoMode()) return db.shifts.getAll(tid);
            const url = role ? `/tenant/${tid}/shifts?role=${role}` : `/tenant/${tid}/shifts`;
            const resp = await schedulingRequest(url);
            return parseBody<Shift[]>(resp) || [];
        },
        save: async (tid: string, shift: Partial<Shift> & { notifyUsers?: boolean }): Promise<Shift> => {
            if (isDemoMode()) { db.shifts.add(shift as Shift); return shift as Shift; }
            return schedulingRequest(`/tenant/${tid}/shifts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(shift)
            }) as Promise<Shift>;
        },
        delete: async (tid: string, id: string): Promise<void> => {
            if (isDemoMode()) { db.shifts.delete(id); return; }
            await schedulingRequest(`/tenant/${tid}/shifts/${id}`, { method: 'DELETE' });
        },
        notifyShift: async (tid: string, id: string): Promise<void> => {
            if (isDemoMode()) return;
            await schedulingRequest(`/tenant/${tid}/shifts/${id}/notify`, { method: 'POST' });
        }
    },

    scheduleGroups: {
        getAll: async (tid: string) => {
            if (isDemoMode()) return db.scheduleGroups.getAll(tid);
            const resp = await schedulingRequest(`/tenant/${tid}/schedule-groups`);
            return parseBody(resp) || [];
        },
        create: async (tid: string, group: { name: string; startDate: string; endDate: string; id?: string }) => {
            if (isDemoMode()) { db.scheduleGroups.add(group as any); return group; }
            const resp = await schedulingRequest(`/tenant/${tid}/schedule-groups`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(group)
            });
            return parseBody(resp);
        },
        update: async (tid: string, id: string, updates: Partial<{ name: string; startDate: string; endDate: string }>) => {
            if (isDemoMode()) { db.scheduleGroups.update({ ...(updates as any), id, tenantId: tid }); return { success: true }; }
            const resp = await schedulingRequest(`/tenant/${tid}/schedule-groups/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });
            return parseBody(resp);
        },
        delete: async (tid: string, id: string) => {
            if (isDemoMode()) { db.scheduleGroups.delete(id); return; }
            await schedulingRequest(`/tenant/${tid}/schedule-groups/${id}`, { method: 'DELETE' });
        },
        publish: async (tid: string, id: string, notify: boolean = true) => {
            if (isDemoMode()) { 
                db.scheduleGroups.publish(id); 
                return { success: true, status: 'published' }; 
            }
            const resp = await schedulingRequest(`/tenant/${tid}/schedule-groups/${id}/publish`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ notify })
            });
            return parseBody(resp);
        },
        unpublish: async (tid: string, id: string) => {
            if (isDemoMode()) { 
                db.scheduleGroups.unpublish(id); 
                return { success: true, status: 'draft' }; 
            }
            const resp = await schedulingRequest(`/tenant/${tid}/schedule-groups/${id}/unpublish`, {
                method: 'POST'
            });
            return parseBody(resp);
        }
    },

    scheduleEvents: {
        getAll: async (tid: string) => {
            if (isDemoMode()) return [];
            const resp = await schedulingRequest(`/tenant/${tid}/schedule-events`);
            return parseBody(resp) || [];
        },
        create: async (tid: string, event: {
            eventTypeId: string;
            eventTypeName: string;
            eventTypeColor: string;
            eventTypeIcon?: string;
            date: string;
            startTime?: string;
            endTime?: string;
            allDay?: boolean;
            notes?: string;
        }) => {
            if (isDemoMode()) return event;
            const resp = await schedulingRequest(`/tenant/${tid}/schedule-events`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(event)
            });
            return parseBody(resp);
        },
        update: async (tid: string, id: string, updates: Partial<{
            eventTypeId: string;
            eventTypeName: string;
            eventTypeColor: string;
            eventTypeIcon: string;
            date: string;
            startTime: string;
            endTime: string;
            allDay: boolean;
            notes: string;
        }>) => {
            if (isDemoMode()) return { success: true };
            const resp = await schedulingRequest(`/tenant/${tid}/schedule-events/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });
            return parseBody(resp);
        },
        delete: async (tid: string, id: string) => {
            if (isDemoMode()) return;
            await schedulingRequest(`/tenant/${tid}/schedule-events/${id}`, { method: 'DELETE' });
        }
    },

    availability: {
        getAll: async (tid: string) => {
            if (isDemoMode()) return db.availability.getAll(tid);
            const resp = await schedulingRequest(`/tenant/${tid}/availability`);
            return parseBody(resp) || [];
        },
        update: async (tid: string, availability: { employeeId: string; unavailableDays: number[] }) => {
            if (isDemoMode()) { db.availability.update({ ...availability, tenantId: tid }); return availability; }
            const resp = await schedulingRequest(`/tenant/${tid}/availability`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(availability)
            });
            return parseBody(resp);
        }
    },

    timeOff: {
        getAll: async (tid: string) => {
            if (isDemoMode()) return db.requests.getAll(tid);
            const resp = await schedulingRequest(`/tenant/${tid}/timeoff`);
            return parseBody(resp) || [];
        },
        create: async (tid: string, requestBody: { employeeId: string; startDate: string; endDate: string; reason: string; status?: string }) => {
            if (isDemoMode()) { db.requests.add({ ...requestBody, id: Date.now().toString(), tenantId: tid } as any); return requestBody; }
            const resp = await schedulingRequest(`/tenant/${tid}/timeoff`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });
            return parseBody(resp);
        },
        update: async (tid: string, id: string, updates: Partial<{ startDate: string; endDate: string; reason: string; status: string }>) => {
            if (isDemoMode()) { db.requests.update({ ...(updates as any), id, tenantId: tid } as any); return { success: true }; }
            const resp = await schedulingRequest(`/tenant/${tid}/timeoff/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });
            return parseBody(resp);
        },
        delete: async (tid: string, id: string) => {
            if (isDemoMode()) { db.requests.delete(id); return { success: true }; }
            const resp = await schedulingRequest(`/tenant/${tid}/timeoff/${id}`, {
                method: 'DELETE'
            });
            return parseBody(resp);
        }
    },

    staffNotifications: {
        getAll: async (tid: string): Promise<StaffNotification[]> => {
            if (isDemoMode()) return [];
            const resp = await schedulingRequest(`/tenant/${tid}/staff-notifications`);
            return parseBody<StaffNotification[]>(resp) || [];
        },
        dismiss: async (tid: string, id: string) => {
            if (isDemoMode()) return { success: true };
            const resp = await schedulingRequest(`/tenant/${tid}/staff-notifications/${id}/dismiss`, {
                method: 'POST'
            });
            return parseBody(resp);
        },
        approveTimeOff: async (tid: string, notificationId: string, timeOffId: string) => {
            if (isDemoMode()) return { success: true, action: 'approved' };
            const resp = await schedulingRequest(`/tenant/${tid}/staff-notifications/${notificationId}/approve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ timeOffId })
            });
            return parseBody(resp);
        },
        denyTimeOff: async (tid: string, notificationId: string, timeOffId: string) => {
            if (isDemoMode()) return { success: true, action: 'denied' };
            const resp = await schedulingRequest(`/tenant/${tid}/staff-notifications/${notificationId}/deny`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ timeOffId })
            });
            return parseBody(resp);
        }
    },

    users: {
        getByTenant: async (tid: string): Promise<User[]> => {
            if (isDemoMode()) return db.users.getByTenant(tid);
            const resp = await usersRequest(`/tenant/${tid}/users`);
            return parseBody<User[]>(resp) || [];
        },
        save: async (tid: string, user: Partial<User>): Promise<User> => {
            if (isDemoMode()) { db.users.add(user as any); return user as User; }
            return usersRequest(`/tenant/${tid}/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(user)
            }) as Promise<User>;
        },
        delete: async (tid: string, id: string, email?: string): Promise<void> => {
            if (isDemoMode()) { db.users.delete(id); return; }
            await usersRequest(`/tenant/${tid}/users/${id}`, { 
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: email ? JSON.stringify({ email }) : undefined
            });
        },
        update: async (tid: string, id: string, user: Partial<User>): Promise<any> => {
            if (isDemoMode()) { db.users.update({ ...(user as any), id, tenantId: tid } as any); return user; }
            return usersRequest(`/tenant/${tid}/users/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(user)
            });
        },
        invite: async (tid: string, payload: { email: string; name: string; role: any }) => {
            if (isDemoMode()) {
                const demoUser = {
                    id: `user_${Date.now()}`,
                    tenantId: tid,
                    email: payload.email,
                    name: payload.name,
                    role: mapRole(payload.role) || 'employee',
                    status: 'pending',
                    completedTrainingRecipeIds: []
                } as any;
                db.users.add(demoUser);
                return { success: true, userId: demoUser.id, message: 'Demo mode: invitation created' };
            }
            const resp = await usersRequest(`/tenant/${tid}/users/invite`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            return parseBody(resp);
        },
        manualAdd: async (
            tid: string,
            payload: { email: string; name: string; role: any; defaultPassword?: string }
        ) => {
            if (isDemoMode()) {
                const demoUser: User = {
                    id: `user_${Date.now()}`,
                    tenantId: tid,
                    email: payload.email,
                    name: payload.name,
                    role: mapRole(payload.role) || 'employee',
                    status: 'active',
                    completedTrainingRecipeIds: []
                };
                db.users.add(demoUser as any);
                return { success: true, userId: demoUser.id, status: 'active', message: 'Demo mode: user added' };
            }
            const resp = await usersRequest(`/tenant/${tid}/users/manual-add`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            return parseBody(resp);
        },
        resendWelcome: async (tid: string, userId: string) => {
            if (isDemoMode()) {
                return { success: true, message: 'Demo mode: welcome email resent' };
            }
            const resp = await usersRequest(`/tenant/${tid}/users/${userId}/resend-welcome`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            return parseBody(resp);
        },
        getNotificationPreferences: async (tid: string) => {
            if (isDemoMode()) {
                return {
                    userId: 'demo_user',
                    tenantId: tid,
                    menuUpdates: true,
                    shiftChanges: true,
                    systemAlerts: true,
                    status: 'active',
                    createdAt: Date.now(),
                    updatedAt: Date.now()
                };
            }
            const resp = await usersRequest(`/tenant/${tid}/users/me/notifications`);
            return parseBody(resp);
        },
        updateNotificationPreferences: async (tid: string, preferences: any) => {
            if (isDemoMode()) {
                return { ...preferences, updatedAt: Date.now() };
            }
            const resp = await usersRequest(`/tenant/${tid}/users/me/notifications`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(preferences)
            });
            return parseBody(resp);
        },
        resetPassword: async (tid: string, userId: string, email: string) => {
            if (isDemoMode()) {
                return { temporaryPassword: 'TempPass123!', message: 'Demo mode: password reset' };
            }
            const resp = await usersRequest(`/tenant/${tid}/users/${userId}/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            return parseBody(resp);
        },
        updateStatus: async (tid: string, userId: string, status: 'active' | 'suspended', email?: string) => {
            if (isDemoMode()) {
                return { success: true, message: `Demo mode: user ${status}` };
            }
            const resp = await usersRequest(`/tenant/${tid}/users/${userId}/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status, email })
            });
            return parseBody(resp);
        }
    },

    uploads: {
        sign: async (tid: string, filename: string, contentType: string, folder = 'media'): Promise<{ uploadUrl: string; fileUrl: string; key: string }> => {
            const resp = await uploadsRequest(`/tenant/${tid}/uploads/sign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ filename, contentType, folder })
            });
            return parseBody(resp) as { uploadUrl: string; fileUrl: string; key: string };
        },
        // Get a presigned URL for reading/downloading an existing S3 file (bypasses CORS)
        getPresignedUrl: async (tid: string, fileUrl: string): Promise<{ presignedUrl: string; key: string }> => {
            const resp = await uploadsRequest(`/tenant/${tid}/uploads/presigned`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fileUrl })
            });
            return parseBody(resp) as { presignedUrl: string; key: string };
        },
        delete: async (tid: string, keyOrUrl: string): Promise<void> => {
            await uploadsRequest(`/tenant/${tid}/uploads`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ key: keyOrUrl, fileUrl: keyOrUrl })
            });
        },
        // Proxy upload - bypasses CORS by uploading through backend (for iOS)
        proxyUpload: async (tid: string, base64Data: string, filename: string, contentType: string, folder = 'media'): Promise<{ fileUrl: string; key: string }> => {
            const resp = await uploadsRequest(`/tenant/${tid}/uploads/proxy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ base64Data, filename, contentType, folder })
            });
            return parseBody(resp) as { fileUrl: string; key: string };
        },
        // Ruby pictures come from the Ruby Generation Engine (media-service). The old
        // uploads-service enhance / generate-image / theme / status client methods
        // were removed in Task 24 (2026-10-04) — nothing in this app called them.
        // AI Text Generation (Vertex AI Gemini)
        generateText: async (
            tenantId: string,
            request: {
                type: 'recipe_description' | 'step_description' | 'improve_text' | 'cost_analysis';
                recipeName?: string;
                category?: string;
                stepNumber?: number;
                totalSteps?: number;
                ingredients?: string[];
                previousSteps?: string[];
                text?: string;
                context?: 'description' | 'step' | 'memo';
                ingredientsList?: string;
            }
        ): Promise<{ success: boolean; text: string; type: string; durationMs: number }> => {
            const resp = await uploadsRequest(`/ai/text/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...request, tenantId })
            });
            return parseBody(resp);
        }
    },

    // Media Service v2 - AI Image Generation Pipeline
    media: {
        // Get tenant AI settings for image generation
        getTenantAISettings: async (tenantId: string): Promise<{
            tenantId: string;
            imageSettings: {
                backgroundStyle: string;
                lighting: string;
                surroundings: string;
                cupStyle: string;
                effects: string;
                removeBackground?: boolean;
                customPrompt?: string;
                aspectRatio?: string;
            };
            lastPreviewImage?: string;
            usageLimits?: { monthlyLimit?: number; budgetLimit?: number };
            createdAt: number;
            updatedAt: number;
        }> => {
            const resp = await mediaRequest(`/tenant/${tenantId}/ai-settings`);
            return parseBody(resp);
        },

        // Update tenant AI settings
        updateTenantAISettings: async (tenantId: string, imageSettings: {
            backgroundStyle?: string;
            lighting?: string;
            surroundings?: string;
            cupStyle?: string;
            effects?: string;
            removeBackground?: boolean;
            customPrompt?: string;
            aspectRatio?: string;
        }, lastPreviewImage?: string): Promise<{
            message: string;
            tenantId: string;
            imageSettings: any;
            lastPreviewImage?: string;
            updatedAt: number;
        }> => {
            const resp = await mediaRequest(`/tenant/${tenantId}/ai-settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ imageSettings, lastPreviewImage })
            });
            return parseBody(resp);
        },

        // The old image pipeline client (settings prompt preview, /ai-usage, generate-v2,
        // jobs-v2, tenant limits) was removed with the pipeline (Task 20, 2026-10-04).
        // Ruby pictures are Ruby Generation Engine runs; spend is on the Ruby Token ledger.
    },

    routines: {
        getDefinitions: async (tid: string): Promise<RoutineDefinition[]> => {
            if (isDemoMode()) return db.routines.getDefinitions(tid);
            const resp = await routinesRequest(`/tenant/${tid}/routines/definitions`);
            return parseBody<RoutineDefinition[]>(resp) || [];
        },
        addDefinition: async (tid: string, definition: Partial<RoutineDefinition>): Promise<RoutineDefinition> => {
            if (isDemoMode()) { db.routines.addDefinition(definition as any); return definition as RoutineDefinition; }
            return routinesRequest(`/tenant/${tid}/routines/definitions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(definition)
            }) as Promise<RoutineDefinition>;
        },
        updateDefinition: async (tid: string, id: string, definition: Partial<RoutineDefinition>): Promise<RoutineDefinition> => {
            if (isDemoMode()) { db.routines.updateDefinition({ ...(definition as any), id, tenantId: tid } as any); return definition as RoutineDefinition; }
            return routinesRequest(`/tenant/${tid}/routines/definitions/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(definition)
            }) as Promise<RoutineDefinition>;
        },
        deleteDefinition: async (tid: string, id: string): Promise<void> => {
            if (isDemoMode()) { db.routines.deleteDefinition(id); return; }
            await routinesRequest(`/tenant/${tid}/routines/definitions/${id}`, { method: 'DELETE' });
        },
        getAssignments: async (tid: string): Promise<RoutineAssignment[]> => {
            if (isDemoMode()) return db.routines.getAssignments(tid);
            const resp = await routinesRequest(`/tenant/${tid}/routines/assignments`);
            return parseBody<RoutineAssignment[]>(resp) || [];
        },
        addAssignment: async (tid: string, assignment: Partial<RoutineAssignment>): Promise<RoutineAssignment> => {
            if (isDemoMode()) { db.routines.addAssignment(assignment as any); return assignment as RoutineAssignment; }
            return routinesRequest(`/tenant/${tid}/routines/assignments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(assignment)
            }) as Promise<RoutineAssignment>;
        },
        updateAssignment: async (tid: string, id: string, assignment: Partial<RoutineAssignment>): Promise<RoutineAssignment> => {
            if (isDemoMode()) { db.routines.updateAssignment({ ...(assignment as any), id, tenantId: tid } as any); return assignment as RoutineAssignment; }
            return routinesRequest(`/tenant/${tid}/routines/assignments/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(assignment)
            }) as Promise<RoutineAssignment>;
        },
        deleteAssignment: async (tid: string, id: string): Promise<void> => {
            if (isDemoMode()) { db.routines.deleteAssignment(id); return; }
            await routinesRequest(`/tenant/${tid}/routines/assignments/${id}`, { method: 'DELETE' });
        },
        bulkAssign: async (tid: string, data: { employeeId: string; routineDefinitionId: string; dates: string[] }): Promise<{ success: boolean; created: number; assignments: RoutineAssignment[] }> => {
            if (isDemoMode()) {
                // Mock bulk assign for demo mode
                const assignments: RoutineAssignment[] = data.dates.map(date => ({
                    id: `asg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    tenantId: tid,
                    employeeId: data.employeeId,
                    routineDefinitionId: data.routineDefinitionId,
                    date,
                    completedTaskIds: []
                }));
                assignments.forEach(a => db.routines.addAssignment(a));
                return { success: true, created: assignments.length, assignments };
            }
            return routinesRequest(`/tenant/${tid}/routines/assignments/bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            }) as Promise<{ success: boolean; created: number; assignments: RoutineAssignment[] }>;
        },
        getAssignmentsSummary: async (tid: string, startDate?: string, endDate?: string): Promise<{ summary: Record<string, { dates: string[]; routines: Record<string, string[]> }>; total: number }> => {
            if (isDemoMode()) {
                // Mock summary for demo mode
                const assignments = db.routines.getAssignments(tid);
                const byEmployee: Record<string, { dates: string[]; routines: Record<string, string[]> }> = {};
                for (const item of assignments) {
                    if (!byEmployee[item.employeeId]) {
                        byEmployee[item.employeeId] = { dates: [], routines: {} };
                    }
                    if (!byEmployee[item.employeeId].dates.includes(item.date)) {
                        byEmployee[item.employeeId].dates.push(item.date);
                    }
                    if (!byEmployee[item.employeeId].routines[item.routineDefinitionId]) {
                        byEmployee[item.employeeId].routines[item.routineDefinitionId] = [];
                    }
                    byEmployee[item.employeeId].routines[item.routineDefinitionId].push(item.date);
                }
                return { summary: byEmployee, total: assignments.length };
            }
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            const queryString = params.toString() ? `?${params.toString()}` : '';
            const resp = await routinesRequest(`/tenant/${tid}/routines/assignments/summary${queryString}`);
            return parseBody(resp) || { summary: {}, total: 0 };
        },
        bulkDeleteAssignments: async (tid: string, data: { employeeId?: string; dates?: string[]; routineDefinitionId?: string }): Promise<{ success: boolean; deleted: number }> => {
            if (isDemoMode()) {
                // Mock bulk delete for demo mode
                const assignments = db.routines.getAssignments(tid);
                let deleted = 0;
                for (const item of assignments) {
                    if (data.employeeId && item.employeeId !== data.employeeId) continue;
                    if (data.routineDefinitionId && item.routineDefinitionId !== data.routineDefinitionId) continue;
                    if (data.dates && data.dates.length > 0 && !data.dates.includes(item.date)) continue;
                    db.routines.deleteAssignment(item.id);
                    deleted++;
                }
                return { success: true, deleted };
            }
            return routinesRequest(`/tenant/${tid}/routines/assignments/bulk-delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            }) as Promise<{ success: boolean; deleted: number }>;
        },
        sendNotification: async (tid: string, data: { employeeId: string; date: string; routineNames: string[]; adminId: string }): Promise<{ success: boolean; notifiedEmployee: string; notifiedAdmin: string | null }> => {
            if (isDemoMode()) return { success: true, notifiedEmployee: 'demo@example.com', notifiedAdmin: null };
            const resp = await routinesRequest(`/tenant/${tid}/routines/notifications`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp) || { success: false, notifiedEmployee: '', notifiedAdmin: null };
        }
    },

    training: {
        getAll: async (tid: string): Promise<TrainingProgram[]> => {
            if (isDemoMode()) return db.training.getAll(tid);
            const resp = await trainingRequest(`/tenant/${tid}/training`);
            return parseBody<TrainingProgram[]>(resp) || [];
        },
        create: async (tid: string, program: Partial<TrainingProgram>): Promise<TrainingProgram> => {
            if (isDemoMode()) { db.training.add(program as any); return program as TrainingProgram; }
            return trainingRequest(`/tenant/${tid}/training`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(program)
            }) as Promise<TrainingProgram>;
        },
        update: async (tid: string, id: string, program: Partial<TrainingProgram>): Promise<TrainingProgram> => {
            if (isDemoMode()) { db.training.update({ ...(program as any), id, tenantId: tid } as any); return program as TrainingProgram; }
            return trainingRequest(`/tenant/${tid}/training/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(program)
            }) as Promise<TrainingProgram>;
        },
        delete: async (tid: string, id: string): Promise<void> => {
            if (isDemoMode()) { db.training.delete(id); return; }
            await trainingRequest(`/tenant/${tid}/training/${id}`, { method: 'DELETE' });
        },
        sendNotification: async (tid: string, id: string, recipeTitles: string[]): Promise<{ success: boolean; notifiedEmployee: string; notifiedAdmin: string | null }> => {
            if (isDemoMode()) return { success: true, notifiedEmployee: 'demo@example.com', notifiedAdmin: null };
            const resp = await trainingRequest(`/tenant/${tid}/training/${id}/notify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ recipeTitles })
            });
            return parseBody(resp) || { success: false, notifiedEmployee: '', notifiedAdmin: null };
        }
    },

    categories: {
        getAll: async (tid: string, opts?: { silent?: boolean }): Promise<Category[]> => {
            if (isDemoMode()) return db.categories.getAll(tid);
            const resp = await recipesRequest(`/tenant/${tid}/categories`, { silent: opts?.silent });
            return parseBody<Category[]>(resp) || [];
        },
        create: async (tid: string, category: Partial<Category>): Promise<Category> => {
            if (isDemoMode()) { db.categories.add(category as any); return category as Category; }
            const resp = await recipesRequest(`/tenant/${tid}/categories`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(category)
            });
            return parseBody<Category>(resp);
        },
        update: async (tid: string, id: string, category: Partial<Category>): Promise<Category> => {
            if (isDemoMode()) { return category as Category; }
            const resp = await recipesRequest(`/tenant/${tid}/categories/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(category)
            });
            return parseBody<Category>(resp);
        },
        delete: async (tid: string, id: string): Promise<void> => {
            if (isDemoMode()) { db.categories.delete(id); return; }
            await recipesRequest(`/tenant/${tid}/categories/${id}`, { method: 'DELETE' });
        }
    },

    units: {
        getAll: async (tid: string): Promise<MeasurementUnit[]> => {
            if (isDemoMode()) return db.units.getAll(tid);
            const resp = await recipesRequest(`/tenant/${tid}/units`);
            return parseBody<MeasurementUnit[]>(resp) || [];
        },
        create: async (tid: string, unit: Partial<MeasurementUnit>): Promise<MeasurementUnit> => {
            if (isDemoMode()) { db.units.add(unit as any); return unit as MeasurementUnit; }
            const resp = await recipesRequest(`/tenant/${tid}/units`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(unit)
            });
            return parseBody<MeasurementUnit>(resp);
        },
        update: async (tid: string, id: string, unit: Partial<MeasurementUnit>): Promise<MeasurementUnit> => {
            if (isDemoMode()) { return unit as MeasurementUnit; }
            const resp = await recipesRequest(`/tenant/${tid}/units/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(unit)
            });
            return parseBody<MeasurementUnit>(resp);
        },
        delete: async (tid: string, id: string): Promise<void> => {
            if (isDemoMode()) { db.units.delete(id); return; }
            await recipesRequest(`/tenant/${tid}/units/${id}`, { method: 'DELETE' });
        }
    },

    leads: {
        getAll: async (): Promise<Lead[]> => {
            if (isDemoMode()) {
                console.log('[API] Leads: Demo mode - returning mock data');
                return db.businessRequests.getAll();
            }
            console.log('[API] Leads: Fetching from', `${LEADS_API_BASE}/leads`);
            const result = await leadsRequest(`/leads`);
            console.log('[API] Leads: Response received', result);
            return parseBody<Lead[]>(result);
        },
        create: async (lead: Partial<Lead>): Promise<Lead> => {
            if (isDemoMode()) { await db.businessRequests.add(lead as any); return lead as Lead; }
            const result = await leadsRequest(`/leads`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(lead)
            });
            return parseBody<Lead>(result);
        },
        update: async (id: string, lead: Partial<Lead>): Promise<Lead> => {
            if (isDemoMode()) { await db.businessRequests.update(lead as any); return lead as Lead; }
            const result = await leadsRequest(`/leads/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(lead)
            });
            return parseBody<Lead>(result);
        },
        delete: async (id: string): Promise<void> => {
            const result = await leadsRequest(`/leads/${id}`, { method: 'DELETE' });
            return parseBody<void>(result);
        }
    },

    email: {
        send: async (payload: {
            to: string[];
            cc?: string[];
            bcc?: string[];
            subject?: string;
            text?: string;
            html?: string;
            templateId?: string;
            /** Values are usually scalars; some templates take arrays/objects (e.g. device-unlock-code `devices`). */
            templateData?: Record<string, unknown>;
            replyTo?: string[];
            sourceOverride?: string;
            /** Notification category — email-service's preference gate reads it (e.g. 'security'). */
            category?: string;
        }): Promise<{ status: string }> => {
            const result = await emailRequest(`/email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            return parseBody(result) as { status: string };
        },

        // Stock Report Email Helpers
        sendStockReportSubmitted: async (data: {
            tenantName: string;
            itemName: string;
            itemType: 'ingredient' | 'supply';
            reportType: 'out_of_stock' | 'low_stock' | 'quantity_correction' | 'expired';
            reportedQuantity: number;
            systemQuantity: number;
            unitAbbreviation?: string;
            employeeName: string;
            employeeEmail?: string;
            message?: string;
            adminEmail: string;
            adminName: string;
        }): Promise<{ status: string }> => {
            const reportTypeLabel = {
                out_of_stock: 'Out of Stock',
                low_stock: 'Low Stock',
                quantity_correction: 'Stock Count Update',
                expired: 'EXPIRED - URGENT'
            }[data.reportType];
            const isUrgent = data.reportType === 'expired';
            const itemTypeLabel = data.itemType === 'ingredient' ? 'Ingredient' : 'Supply';
            const dashboardUrl = `${window.location.origin}/inventory`;

            // Send to admin
            await api.email.send({
                to: [data.adminEmail],
                subject: isUrgent ? `🚨 URGENT: ${data.itemName} - ${reportTypeLabel}` : `📦 Stock Report: ${data.itemName} - ${reportTypeLabel}`,
                html: `
                    <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto;">
                        <div style="background: linear-gradient(135deg, ${isUrgent ? '#fecaca 0%, #f87171' : '#fef3c7 0%, #fde68a'} 100%); padding: 24px; border-radius: 12px 12px 0 0;">
                            <h2 style="margin: 0; color: ${isUrgent ? '#991b1b' : '#92400e'};">${isUrgent ? '🚨 URGENT: Expired Item Report' : '📦 New Stock Report Submitted'}</h2>
                            <p style="margin: 8px 0 0; color: ${isUrgent ? '#b91c1c' : '#a16207'};">${data.tenantName}</p>
                        </div>
                        <div style="background-color: #ffffff; padding: 24px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 12px 12px;">
                            <div style="background-color: ${data.reportType === 'out_of_stock' ? '#fef2f2' : data.reportType === 'low_stock' ? '#fffbeb' : '#eff6ff'}; border: 1px solid ${data.reportType === 'out_of_stock' ? '#fecaca' : data.reportType === 'low_stock' ? '#fde68a' : '#bfdbfe'}; border-radius: 8px; padding: 16px; margin-bottom: 20px;">
                                <p style="margin: 0 0 4px; font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em;">${itemTypeLabel}</p>
                                <h3 style="margin: 0 0 8px; color: #111827; font-size: 20px;">${data.itemName}</h3>
                                <span style="display: inline-block; background-color: ${data.reportType === 'out_of_stock' ? '#dc2626' : data.reportType === 'low_stock' ? '#d97706' : '#2563eb'}; color: white; padding: 4px 12px; border-radius: 9999px; font-size: 12px; font-weight: 600;">${reportTypeLabel}</span>
                            </div>
                            <div style="display: flex; gap: 16px; margin-bottom: 20px;">
                                <div style="flex: 1; text-align: center; padding: 16px; background-color: #f3f4f6; border-radius: 8px;">
                                    <p style="margin: 0; font-size: 12px; color: #6b7280; text-transform: uppercase;">System Shows</p>
                                    <p style="margin: 4px 0 0; font-size: 24px; font-weight: 700; color: #9ca3af; text-decoration: line-through;">${data.systemQuantity}</p>
                                    <p style="margin: 0; font-size: 12px; color: #9ca3af;">${data.unitAbbreviation || 'units'}</p>
                                </div>
                                <div style="flex: 1; text-align: center; padding: 16px; background-color: ${data.reportedQuantity === 0 ? '#fef2f2' : '#f0fdf4'}; border-radius: 8px; border: 2px solid ${data.reportedQuantity === 0 ? '#fecaca' : '#bbf7d0'};">
                                    <p style="margin: 0; font-size: 12px; color: #6b7280; text-transform: uppercase;">Actual Count</p>
                                    <p style="margin: 4px 0 0; font-size: 24px; font-weight: 700; color: ${data.reportedQuantity === 0 ? '#dc2626' : '#16a34a'};">${data.reportedQuantity}</p>
                                    <p style="margin: 0; font-size: 12px; color: #6b7280;">${data.unitAbbreviation || 'units'}</p>
                                </div>
                            </div>
                            <div style="background-color: #f9fafb; border-radius: 8px; padding: 16px; margin-bottom: 20px;">
                                <p style="margin: 0 0 4px; font-size: 12px; color: #6b7280;">Reported by:</p>
                                <p style="margin: 0; font-weight: 600; color: #111827;">${data.employeeName}</p>
                                ${data.employeeEmail ? `<p style="margin: 4px 0 0; font-size: 14px; color: #6b7280;">${data.employeeEmail}</p>` : ''}
                            </div>
                            ${data.message ? `
                            <div style="background-color: #f9fafb; border-left: 4px solid #6b7280; padding: 12px 16px; margin-bottom: 20px;">
                                <p style="margin: 0 0 4px; font-size: 12px; color: #6b7280;">Employee Note:</p>
                                <p style="margin: 0; color: #374151;">"${data.message}"</p>
                            </div>
                            ` : ''}
                            <a href="${dashboardUrl}" style="display: block; text-align: center; background-color: #16a34a; color: white; padding: 14px 24px; border-radius: 8px; text-decoration: none; font-weight: 600; margin-top: 20px;">Review & Approve Report →</a>
                        </div>
                    </div>
                `
            });

            // Send confirmation to employee if they have email
            if (data.employeeEmail) {
                await api.email.send({
                    to: [data.employeeEmail],
                    subject: `✅ Stock Report Submitted: ${data.itemName}`,
                    html: `
                        <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto;">
                            <div style="background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%); padding: 24px; border-radius: 12px 12px 0 0;">
                                <h2 style="margin: 0; color: #166534;">✅ Report Submitted Successfully</h2>
                                <p style="margin: 8px 0 0; color: #15803d;">Thank you for helping keep our inventory accurate!</p>
                            </div>
                            <div style="background-color: #ffffff; padding: 24px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 12px 12px;">
                                <p style="margin: 0 0 16px; color: #374151;">Hi ${data.employeeName},</p>
                                <p style="margin: 0 0 20px; color: #374151;">Your stock report has been submitted and is pending admin review.</p>
                                <div style="background-color: #f9fafb; border-radius: 8px; padding: 16px; margin-bottom: 20px;">
                                    <h3 style="margin: 0 0 12px; color: #111827;">${data.itemName}</h3>
                                    <div style="display: flex; justify-content: space-between; align-items: center;">
                                        <div>
                                            <p style="margin: 0; font-size: 12px; color: #6b7280;">Your Count</p>
                                            <p style="margin: 4px 0 0; font-size: 20px; font-weight: 700; color: #111827;">${data.reportedQuantity} ${data.unitAbbreviation || 'units'}</p>
                                        </div>
                                        <span style="background-color: #fef3c7; color: #92400e; padding: 6px 12px; border-radius: 9999px; font-size: 12px; font-weight: 600;">Pending Review</span>
                                    </div>
                                </div>
                                <p style="margin: 0; color: #6b7280; font-size: 14px;">You'll receive an update once ${data.adminName} reviews your report.</p>
                            </div>
                        </div>
                    `
                });
            }

            return { status: 'sent' };
        },

        sendStockReportResolved: async (data: {
            itemName: string;
            status: 'approved' | 'rejected';
            reportedQuantity: number;
            appliedQuantity?: number;
            unitAbbreviation?: string;
            employeeName: string;
            employeeEmail: string;
            adminName: string;
            adminResponse?: string;
        }): Promise<{ status: string }> => {
            if (!data.employeeEmail) {
                return { status: 'skipped' };
            }

            const isApproved = data.status === 'approved';

            await api.email.send({
                to: [data.employeeEmail],
                subject: `${isApproved ? '✅ Approved' : '❌ Rejected'}: Stock Report for ${data.itemName}`,
                html: `
                    <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto;">
                        <div style="background: linear-gradient(135deg, ${isApproved ? '#dcfce7 0%, #bbf7d0' : '#fef2f2 0%, #fecaca'} 100%); padding: 24px; border-radius: 12px 12px 0 0;">
                            <h2 style="margin: 0; color: ${isApproved ? '#166534' : '#991b1b'};">${isApproved ? '✅ Stock Report Approved' : '❌ Stock Report Rejected'}</h2>
                            <p style="margin: 8px 0 0; color: ${isApproved ? '#15803d' : '#b91c1c'};">${data.itemName}</p>
                        </div>
                        <div style="background-color: #ffffff; padding: 24px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 12px 12px;">
                            <p style="margin: 0 0 16px; color: #374151;">Hi ${data.employeeName},</p>
                            <p style="margin: 0 0 20px; color: #374151;">
                                ${isApproved
                                    ? `Your stock report has been approved and the inventory has been updated to <strong>${data.appliedQuantity ?? data.reportedQuantity} ${data.unitAbbreviation || 'units'}</strong>.`
                                    : 'Your stock report has been reviewed but was not approved.'}
                            </p>
                            ${data.adminResponse ? `
                            <div style="background-color: #eff6ff; border-left: 4px solid #3b82f6; padding: 12px 16px; margin-bottom: 20px;">
                                <p style="margin: 0 0 4px; font-size: 12px; color: #1d4ed8; font-weight: 600;">Admin Response:</p>
                                <p style="margin: 0; color: #1e40af;">"${data.adminResponse}"</p>
                                <p style="margin: 8px 0 0; font-size: 12px; color: #6b7280;">— ${data.adminName}</p>
                            </div>
                            ` : ''}
                            <p style="margin: 0; color: #6b7280; font-size: 14px;">Thank you for helping keep our inventory accurate!</p>
                        </div>
                    </div>
                `
            });

            return { status: 'sent' };
        },

        /**
         * Send stock report summary email using centralized email template
         * Template: stock-report-summary (email-service/templates/stock-report-summary.html)
         */
        sendStockReportSummary: async (data: {
            recipientEmail: string;
            recipientName?: string;
            subject: string;
            message: string;
            reports: Array<{
                itemName: string;
                itemType: 'ingredient' | 'supply';
                reportType: string;
                reporter: string;
                date: string;
            }>;
            filterLabel: string;
            senderName: string;
            tenantName: string;
            totalCounts: {
                all: number;
                expired: number;
                out_of_stock: number;
                low_stock: number;
                stock_count: number;
            };
        }): Promise<{ status: string }> => {
            // Helper function to get badge class for report type
            const getTypeBadgeClass = (type: string): string => {
                switch (type) {
                    case 'expired': return 'badge-expired';
                    case 'out_of_stock': return 'badge-out-of-stock';
                    case 'low_stock': return 'badge-low-stock';
                    default: return 'badge-stock-count';
                }
            };

            // Helper function to get label for report type
            const getTypeLabel = (type: string): string => {
                switch (type) {
                    case 'expired': return 'Expired';
                    case 'out_of_stock': return 'Out of Stock';
                    case 'low_stock': return 'Low Stock';
                    default: return 'Stock Count';
                }
            };

            // Pre-generate the reports table HTML for the template
            const reportsHtml = data.reports.map(report => `
                <tr>
                    <td>
                        <strong>${report.itemName}</strong>
                        <div style="font-size: 11px; color: #8d6e63; text-transform: capitalize;">${report.itemType}</div>
                    </td>
                    <td><span class="badge ${getTypeBadgeClass(report.reportType)}">${getTypeLabel(report.reportType)}</span></td>
                    <td>${report.reporter}</td>
                    <td style="white-space: nowrap;">${report.date}</td>
                </tr>
            `).join('');

            // Use centralized email template
            await api.email.send({
                to: [data.recipientEmail],
                subject: data.subject,
                templateId: 'stock-report-summary',
                templateData: {
                    tenantName: data.tenantName,
                    filterLabel: data.filterLabel,
                    senderName: data.senderName,
                    message: data.message || 'Please review the attached stock report summary.',
                    expiredCount: String(data.totalCounts.expired),
                    outOfStockCount: String(data.totalCounts.out_of_stock),
                    lowStockCount: String(data.totalCounts.low_stock),
                    stockCountCount: String(data.totalCounts.stock_count),
                    showExpired: data.totalCounts.expired > 0,
                    showOutOfStock: data.totalCounts.out_of_stock > 0,
                    showLowStock: data.totalCounts.low_stock > 0,
                    showStockCount: data.totalCounts.stock_count > 0,
                    totalReports: String(data.reports.length),
                    reportsHtml: reportsHtml,
                    generatedDate: new Date().toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    }),
                }
            });

            return { status: 'sent' };
        }
    },

    platform: {
        getProfile: async (): Promise<BusinessProfile> => {
            return tenantsRequest(`/platform/profile`) as Promise<BusinessProfile>;
        }
    },

    // ============================================
    // CREATOR STUDIO APIs (Menu Groups, Shares, Boards)
    // ============================================

    menuGroups: {
        getAll: async (tenantId: string, opts?: { silent?: boolean }) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/menu-groups`, { silent: opts?.silent });
            return parseBody<any[]>(resp) || [];
        },
        get: async (tenantId: string, id: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/menu-groups/${id}`);
            return parseBody(resp);
        },
        create: async (tenantId: string, data: { name: string; description?: string; menuItemIds?: string[]; showMenusOnly?: boolean; sortOrder?: number }) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/menu-groups`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },
        update: async (tenantId: string, id: string, data: { name?: string; description?: string; menuItemIds?: string[]; showMenusOnly?: boolean; sortOrder?: number }) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/menu-groups/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },
        delete: async (tenantId: string, id: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/menu-groups/${id}`, {
                method: 'DELETE'
            });
            return parseBody(resp);
        }
    },

    publicShares: {
        getAll: async (tenantId: string, opts?: { silent?: boolean }) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/shares`, { silent: opts?.silent });
            return parseBody<any[]>(resp) || [];
        },
        get: async (tenantId: string, shareId: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/shares/${shareId}`);
            return parseBody(resp);
        },
        create: async (tenantId: string, data: {
            shareType: 'board' | 'social' | 'menu-link';
            title?: string;
            menuItemIds: string[];
            menuGroupId?: string;
            templateConfig?: {
                seasonTheme?: 'spring' | 'summer' | 'autumn' | 'winter' | 'holiday' | 'neutral';
                aspectRatio?: '1:1' | '4:5' | '16:9' | '9:16' | 'custom';
                displayFields?: ('title' | 'price' | 'description' | 'image')[];
            };
            expiresInDays?: number;
        }) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/shares`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },
        delete: async (tenantId: string, shareId: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/shares/${shareId}`, {
                method: 'DELETE'
            });
            return parseBody(resp);
        },
        email: async (tenantId: string, shareId: string, to: string, subject?: string, message?: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/shares/${shareId}/email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ to, subject, message })
            });
            return parseBody(resp);
        },
        // Public endpoint - no auth needed
        getPublic: async (shareId: string) => {
            const resp = await fetch(`${CREATOR_API_BASE}/public/share/${shareId}`);
            return resp.json();
        }
    },

    menuBoards: {
        generate: async (tenantId: string, data: {
            menuItemIds: string[];
            shareType: 'board' | 'social';
            title?: string;
            menuGroupId?: string;
            generationMode?: 'full' | 'background_only';
            templateConfig?: {
                seasonTheme?: 'spring' | 'summer' | 'autumn' | 'winter' | 'holiday' | 'neutral';
                aspectRatio?: '1:1' | '4:5' | '16:9' | '9:16' | 'custom';
                displayFields?: ('title' | 'price' | 'description' | 'image')[];
                layoutStyle?: 'grid' | 'collage' | 'single' | 'carousel';
                textStyle?: 'minimal' | 'bold' | 'elegant' | 'playful';
                bgStyle?: 'solid' | 'gradient' | 'pattern' | 'blur';
                preserveOriginalImages?: boolean;
                backgroundOnly?: boolean;
                backgroundColor?: string;
                referenceImageUrl?: string;
            };
        }) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/menu-boards/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },
        // Public endpoint - no auth needed
        getJobStatus: async (jobId: string) => {
            const resp = await fetch(`${CREATOR_API_BASE}/menu-boards/jobs/${jobId}`);
            return resp.json();
        },
        download: async (tenantId: string, shareId: string, aspectRatio?: string) => {
            const query = aspectRatio ? `?ratio=${aspectRatio}` : '';
            const resp = await creatorRequest(`/tenant/${tenantId}/menu-boards/${shareId}/download${query}`);
            return parseBody(resp);
        }
    },

    wordArt: {
        generate: async (tenantId: string, data: {
            text: string;
            prompt: string;
            width?: number;
            height?: number;
        }) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/word-art/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        }
    },

    // =========================================================================
    // VIDEO GENERATION APIs (via media-service)
    // =========================================================================
    videoGeneration: {
        // Start a new video generation job
        startJob: async (tenantId: string, data: {
            contentType: 'fb_post' | 'fb_post_vertical' | 'tiktok' | 'advertisement' | 'story';
            menuItemId?: string;
            menuItemIds?: string[];
            prompt?: string;
            aspectRatio?: '16:9' | '9:16' | '1:1';
            durationSeconds?: number;
            includeAudio?: boolean;
            style?: string;
            // Video style options
            videoStyle?: string; // appetite, fastfresh, chefstory, localfave, seasonal, celebration
            includeVoice?: boolean;
            voiceScript?: string; // Custom voice-over script
            logoUrl?: string;
            businessName?: string;
            itemName?: string;
            itemPrice?: number; // Cheapest price for text overlay
            itemPriceText?: string; // Formatted price text (e.g., "Starting from $4.99")
            referenceImageUrl?: string;
            thumbnailUrl?: string;
            ingredients?: string[];
            steps?: string[];
            category?: string;
            // Text overlay options
            showTextOverlay?: boolean;
            textDuration?: number; // 4, 6, or 8 seconds
            videoName?: string; // Custom video name
            // Prepaid credit system
            skipWatermark?: boolean; // Skip watermark if credits were spent
            jobId?: string; // Job ID for idempotency
        }) => {
            const resp = await mediaRequest(`/tenant/${tenantId}/media/generate/video`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },
        // Get job status (public - no auth for polling)
        getJobStatus: async (jobId: string) => {
            const resp = await fetch(`${MEDIA_API_BASE}/media/jobs/${jobId}/status`);
            return resp.json();
        },
        // List tenant video jobs
        listJobs: async (tenantId: string, options?: { status?: string; limit?: number }) => {
            const params = new URLSearchParams();
            if (options?.status) params.append('status', options.status);
            if (options?.limit) params.append('limit', options.limit.toString());
            const query = params.toString() ? `?${params}` : '';
            const resp = await mediaRequest(`/tenant/${tenantId}/media/jobs${query}`);
            return parseBody<any[]>(resp) || [];
        },
        // List tenant generated content
        listContent: async (tenantId: string, options?: { contentType?: string; limit?: number }) => {
            const params = new URLSearchParams();
            if (options?.contentType) params.append('contentType', options.contentType);
            if (options?.limit) params.append('limit', options.limit.toString());
            const query = params.toString() ? `?${params}` : '';
            const resp = await mediaRequest(`/tenant/${tenantId}/media/content${query}`);
            return parseBody<any[]>(resp) || [];
        },
        // Delete generated content
        deleteContent: async (tenantId: string, contentId: string) => {
            const resp = await mediaRequest(`/tenant/${tenantId}/media/content/${contentId}`, {
                method: 'DELETE'
            });
            return parseBody(resp);
        },
        // Get tenant usage metrics
        getUsage: async (tenantId: string) => {
            const resp = await mediaRequest(`/tenant/${tenantId}/media/usage`);
            return parseBody(resp);
        },
        // Convenience endpoints for specific content types
        startFbPost: async (tenantId: string, data: { menuItemId?: string; prompt?: string; style?: string }) => {
            const resp = await mediaRequest(`/tenant/${tenantId}/media/generate/fb-post`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },
        startTiktok: async (tenantId: string, data: { menuItemId?: string; prompt?: string; style?: string }) => {
            const resp = await mediaRequest(`/tenant/${tenantId}/media/generate/tiktok`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },
        startAd: async (tenantId: string, data: { menuItemId?: string; prompt?: string; style?: string }) => {
            const resp = await mediaRequest(`/tenant/${tenantId}/media/generate/ad`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        }
    },

    // composition (media-service /composition/*) client removed 2026-10-05 (Task 27): no
    // super-admin page used it; Creator Studio compositions live in platform-app.

    // =========================================================================
    // CREATOR STUDIO CONTENT UNLOCK & PAYMENT APIs
    // =========================================================================
    creatorContent: {
        // Create checkout session for content unlock
        createCheckout: async (tenantId: string, contentId: string, data?: { successUrl?: string; cancelUrl?: string }) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/creator/content/${contentId}/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data || {})
            });
            return parseBody(resp);
        },
        // Verify checkout session (public)
        verifyCheckout: async (sessionId: string) => {
            const resp = await fetch(`${CREATOR_API_BASE}/creator/checkout/${sessionId}/verify`);
            return resp.json();
        },
        // Get download URL for unlocked content
        getDownloadUrl: async (tenantId: string, contentId: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/creator/content/${contentId}/download`);
            return parseBody(resp);
        }
    },

    // =========================================================================
    // CREATOR STUDIO SUBSCRIPTION APIs
    // =========================================================================
    creatorSubscriptions: {
        // Get available subscription plans (public)
        getPlans: async () => {
            const resp = await fetch(`${CREATOR_API_BASE}/creator/subscriptions/plans`);
            return resp.json();
        },
        // Create subscription checkout session
        createCheckout: async (tenantId: string, data: { planId: string; successUrl?: string; cancelUrl?: string }) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/creator/subscriptions/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },
        // Get current subscription
        get: async (tenantId: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/creator/subscriptions`);
            return parseBody(resp);
        },
        // Cancel subscription
        cancel: async (tenantId: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/creator/subscriptions/cancel`, {
                method: 'POST'
            });
            return parseBody(resp);
        },
        // Reactivate cancelled subscription
        reactivate: async (tenantId: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/creator/subscriptions/reactivate`, {
                method: 'POST'
            });
            return parseBody(resp);
        },
        // Create billing portal session
        createPortalSession: async (tenantId: string, returnUrl?: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/creator/subscriptions/portal`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ returnUrl })
            });
            return parseBody(resp);
        }
    },

    // =========================================================================
    // CREATOR STUDIO TRANSACTION APIs
    // =========================================================================
    creatorTransactions: {
        // Get tenant transactions
        list: async (tenantId: string, options?: { type?: string; limit?: number; startDate?: string; endDate?: string }) => {
            const params = new URLSearchParams();
            if (options?.type) params.append('type', options.type);
            if (options?.limit) params.append('limit', options.limit.toString());
            if (options?.startDate) params.append('startDate', options.startDate);
            if (options?.endDate) params.append('endDate', options.endDate);
            const query = params.toString() ? `?${params}` : '';
            const resp = await creatorRequest(`/tenant/${tenantId}/creator/transactions${query}`);
            return parseBody<any[]>(resp) || [];
        },
        // Get single transaction
        get: async (tenantId: string, transactionId: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/creator/transactions/${transactionId}`);
            return parseBody(resp);
        },
        // Get transaction summary
        getSummary: async (tenantId: string, options?: { startDate?: string; endDate?: string }) => {
            const params = new URLSearchParams();
            if (options?.startDate) params.append('startDate', options.startDate);
            if (options?.endDate) params.append('endDate', options.endDate);
            const query = params.toString() ? `?${params}` : '';
            const resp = await creatorRequest(`/tenant/${tenantId}/creator/transactions/summary${query}`);
            return parseBody(resp);
        }
    },

    // =========================================================================
    // CREATOR STUDIO CREDIT SYSTEM APIs
    // =========================================================================
    credits: {
        // Get credit balance and pricing
        getBalance: async (tenantId: string, opts?: { silent?: boolean }) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/credits/balance`, { silent: opts?.silent });
            return parseBody<{
                balance: number;
                chargeEnabled: boolean;
                totalLoaded: number;
                totalSpent: number;
                lastLoadedAt: number | null;
                lastSpentAt: number | null;
                pricing: {
                    video: number;
                    image: number;
                    composition: number;
                };
                creditSystemEnabled: boolean;
            }>(resp);
        },
        // Get credit transactions
        getTransactions: async (tenantId: string, options?: { type?: string; limit?: number }) => {
            const params = new URLSearchParams();
            if (options?.type) params.append('type', options.type);
            if (options?.limit) params.append('limit', options.limit.toString());
            const query = params.toString() ? `?${params}` : '';
            const resp = await creatorRequest(`/tenant/${tenantId}/credits/transactions${query}`);
            return parseBody<{
                transactions: Array<{
                    transactionId: string;
                    type: string;
                    amount: number;
                    balanceBefore: number;
                    balanceAfter: number;
                    status: string;
                    paymentSource?: string;
                    description?: string;
                    jobId?: string;
                    jobType?: string;
                    createdAt: number;
                }>;
                totals: {
                    totalLoaded: number;
                    totalSpent: number;
                    totalRefunded: number;
                    loadCount: number;
                    spendCount: number;
                    refundCount: number;
                };
                count: number;
            }>(resp);
        },
        // Create Stripe Payment Intent for credit load (web)
        createLoadSession: async (tenantId: string, amount: number) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/credits/load`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ amount })
            });
            return parseBody<{
                clientSecret: string;
                paymentIntentId: string;
                amount: number;
                currentBalance: number;
                newBalance: number;
            }>(resp);
        },
        // Confirm credit load after successful payment
        confirmCreditLoad: async (tenantId: string, paymentIntentId: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/credits/confirm-load`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ paymentIntentId })
            });
            return parseBody<{
                success: boolean;
                newBalance: number;
                amount: number;
                transactionId?: string;
                message: string;
                alreadyProcessed?: boolean;
            }>(resp);
        },
        // Verify Apple IAP purchase (iOS)
        verifyApplePurchase: async (tenantId: string, data: {
            receiptData: string;
            productId: string;
            transactionId: string;
        }) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/credits/load/apple`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody<{
                success: boolean;
                amount?: number;
                transactionId?: string;
                alreadyProcessed?: boolean;
            }>(resp);
        },
        // Verify load session (fallback for Stripe checkout - legacy)
        verifyLoadSession: async (sessionId: string) => {
            const resp = await fetch(`${CREATOR_API_BASE}/credits/load/${sessionId}/verify`);
            return resp.json();
        },
        // Spend credits for content generation (videos, images, compositions)
        spendCredits: async (tenantId: string, jobType: 'video' | 'image' | 'composition', jobId: string) => {
            const resp = await creatorRequest(`/tenant/${tenantId}/credits/spend`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ jobType, jobId })
            });
            return parseBody<{
                success: boolean;
                freeTier?: boolean;
                transactionId?: string;
                amount?: number;
                balanceAfter?: number;
                balance?: number;
                error?: string;
                required?: number;
                jobType?: string;
                message?: string;
                alreadySpent?: boolean;
            }>(resp);
        }
    },

    // =========================================================================
    // AFFILIATE PROGRAM APIs
    // =========================================================================
    affiliates: {
        // ---------- PUBLIC ENDPOINTS (no auth) ----------
        
        // Validate a referral/trial code (public)
        validateCode: async (code: string) => {
            const baseUrl = getAffiliatesApiBase();
            const resp = await fetch(`${baseUrl}/public/validate-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code })
            });
            return resp.json();
        },

        // Register as new affiliate (public)
        register: async (data: {
            email: string;
            name: string;
            company?: string;
            website?: string;
        }) => {
            const baseUrl = getAffiliatesApiBase();
            const resp = await fetch(`${baseUrl}/affiliates/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return resp.json();
        },

        // Login with email/password (returns Cognito tokens)
        login: async (email: string, password: string) => {
            const baseUrl = getAffiliatesApiBase();
            const resp = await fetch(`${baseUrl}/affiliates/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });
            return resp.json();
        },

        // Complete NEW_PASSWORD_REQUIRED challenge
        completeNewPassword: async (email: string, newPassword: string, session: string) => {
            const baseUrl = getAffiliatesApiBase();
            const resp = await fetch(`${baseUrl}/affiliates/auth/complete-new-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, newPassword, session })
            });
            return resp.json();
        },

        // Request password reset
        forgotPassword: async (email: string) => {
            const baseUrl = getAffiliatesApiBase();
            const resp = await fetch(`${baseUrl}/affiliates/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            return resp.json();
        },

        // Confirm password reset with code
        confirmForgotPassword: async (email: string, code: string, newPassword: string) => {
            const baseUrl = getAffiliatesApiBase();
            const resp = await fetch(`${baseUrl}/affiliates/auth/confirm-forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code, newPassword })
            });
            return resp.json();
        },

        // ---------- AFFILIATE PORTAL ENDPOINTS (affiliate token auth) ----------

        // Get affiliate profile
        getProfile: async (token: string) => {
            const baseUrl = getAffiliatesApiBase();
            const resp = await fetch(`${baseUrl}/affiliates/me`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return resp.json();
        },

        // Update affiliate profile
        updateProfile: async (token: string, data: {
            name?: string;
            company?: string;
            website?: string;
            paymentMethod?: 'paypal' | 'bank_transfer';
            paymentDetails?: {
                paypalEmail?: string;
                bankName?: string;
                accountNumber?: string;
                routingNumber?: string;
            };
        }) => {
            const baseUrl = getAffiliatesApiBase();
            const resp = await fetch(`${baseUrl}/affiliates/me`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            return resp.json();
        },

        // Get affiliate stats
        getStats: async (token: string) => {
            const baseUrl = getAffiliatesApiBase();
            const resp = await fetch(`${baseUrl}/affiliates/me/stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return resp.json();
        },

        // Get affiliate referral link
        getLink: async (token: string) => {
            const baseUrl = getAffiliatesApiBase();
            const resp = await fetch(`${baseUrl}/affiliates/me/link`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return resp.json();
        },

        // Get affiliate referrals list
        getReferrals: async (token: string, page?: number, status?: string) => {
            const baseUrl = getAffiliatesApiBase();
            const params = new URLSearchParams();
            if (page) params.set('page', String(page));
            if (status) params.set('status', status);
            const query = params.toString() ? `?${params.toString()}` : '';
            const resp = await fetch(`${baseUrl}/affiliates/me/referrals${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return resp.json();
        },

        // Get affiliate payouts list
        getPayouts: async (token: string, page?: number, status?: string) => {
            const baseUrl = getAffiliatesApiBase();
            const params = new URLSearchParams();
            if (page) params.set('page', String(page));
            if (status) params.set('status', status);
            const query = params.toString() ? `?${params.toString()}` : '';
            const resp = await fetch(`${baseUrl}/affiliates/me/payouts${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return resp.json();
        },

        // Request a payout
        requestPayout: async (token: string) => {
            const baseUrl = getAffiliatesApiBase();
            const resp = await fetch(`${baseUrl}/affiliates/me/payouts/request`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return resp.json();
        },

        // ---------- SUPER ADMIN ENDPOINTS (Cognito auth) ----------
        admin: {
            // List all affiliates
            list: async (page?: number, status?: string, search?: string) => {
                const params = new URLSearchParams();
                if (page) params.set('page', String(page));
                if (status) params.set('status', status);
                if (search) params.set('search', search);
                const query = params.toString() ? `?${params.toString()}` : '';
                const resp = await affiliatesRequest(`/admin/affiliates${query}`);
                return parseBody(resp);
            },

            // Get affiliate details
            getAffiliate: async (affiliateId: string) => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}`);
                return parseBody(resp);
            },

            // Update affiliate status (approve/suspend/reject)
            updateStatus: async (affiliateId: string, data: {
                status: 'approved' | 'suspended' | 'rejected';
                reason?: string;
            }) => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/status`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data)
                });
                return parseBody(resp);
            },

            /**
             * Every document this partner has signed, newest first. Each row
             * carries the artifact of the version THEY signed, not the one in
             * force today.
             */
            getSignedDocuments: async (affiliateId: string): Promise<{
                /** What they are held to right now, each with its signature state. */
                attached: Array<{
                    documentRef: string;
                    documentId: string;
                    slug: string;
                    title: string;
                    versionNo: number;
                    effectiveAt: string | null;
                    changeSummary: string | null;
                    artifactUrl: string | null;
                    accepted: boolean;
                    acceptedVersionNo: number | null;
                }>;
                signed: Array<{
                    documentId: string;
                    slug: string | null;
                    title: string;
                    versionNo: number;
                    acceptedAt: string | null;
                    method: string | null;
                    artifactUrl: string | null;
                    effectiveAt: string | null;
                    retired: boolean;
                }>;
                degraded?: boolean;
            }> => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/documents`);
                return parseBody(resp);
            },

            /**
             * Attach (or detach) the agreement/policy this partner must sign
             * before the portal opens. `documentRef` is a documents-service
             * slug (or `doc_<slug>`); pass null to detach.
             *
             * The server refuses to approve a partner with nothing attached,
             * so this is the step BEFORE approval, not an optional extra.
             */
            setAgreement: async (affiliateId: string, documentRefs: string[]) => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/agreement`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ documentRefs })
                });
                return parseBody(resp);
            },

            /**
             * PARTNER DEAL LINKS — a partner may market many deals, each with
             * its own code, seat cap and expiry. Replaces the single
             * `assignedDealId`, which could not express a limited offer.
             */
            listLinks: async (affiliateId: string) => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/links`);
                return parseBody(resp);
            },
            createLink: async (affiliateId: string, data: {
                dealId: string;
                dealCode?: string;
                label?: string;
                /** null = unlimited. 0 legitimately means no seats left. */
                maxUses?: number | null;
                expiresAt?: number;
                commissionPlanId?: string;
            }) => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/links`, {
                    method: 'POST',
                    body: JSON.stringify(data),
                });
                return parseBody(resp);
            },
            /** `usedCount` is deliberately not editable — raise `maxUses` to grant seats. */
            updateLink: async (affiliateId: string, linkId: string, data: Record<string, any>) => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/links/${linkId}`, {
                    method: 'PUT',
                    body: JSON.stringify(data),
                });
                return parseBody(resp);
            },
            /** Refused once the link has conversions — pause instead, so the trail survives. */
            deleteLink: async (affiliateId: string, linkId: string) => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/links/${linkId}`, {
                    method: 'DELETE',
                });
                return parseBody(resp);
            },

            /** Commission ledger for one partner. Half-open [from, to) epoch ms. */
            getCommissions: async (affiliateId: string, range?: { fromMs?: number; toMs?: number }) => {
                const qs = new URLSearchParams();
                if (typeof range?.fromMs === 'number') qs.set('from', String(range.fromMs));
                if (typeof range?.toMs === 'number') qs.set('to', String(range.toMs));
                const suffix = qs.toString() ? `?${qs.toString()}` : '';
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/commissions${suffix}`);
                return parseBody(resp);
            },

            /**
             * Mark a partner as a RubyTill internal ISO — the toggle that lets
             * merchant-processing orders be assigned to them. Server refuses it
             * for a partner who is not approved.
             */
            /**
             * WHO ALREADY HOLDS THIS DEAL — one call for the whole partner
             * list, so the "Send to partners" popup does not have to ask
             * `listLinks` once per partner to draw its ticks.
             */
            listDealOffers: async (dealId: string): Promise<{
                dealId: string;
                offers: Array<{
                    partnerId: string; linkId: string; code: string; label?: string;
                    status: string; displayStatus: string;
                    remainingUses: number | null; maxUses: number | null;
                    usedCount: number; signupCount: number;
                    expiresAt?: number; createdAt?: number; shareUrl: string;
                }>;
            }> => {
                const resp = await affiliatesRequest(`/admin/deals/${encodeURIComponent(dealId)}/offers`);
                return parseBody(resp);
            },

            /**
             * GRANT AND WITHDRAW IN ONE CALL.
             *
             * Deliberately not a loop of `createLink` in the browser: that has
             * no atomicity, re-reads the deal per partner, and leaves a
             * half-finished state nobody can see when one call fails. The
             * server reports an outcome PER PARTNER
             * (created/resumed/unchanged/revoked/skipped/failed) and never
             * aborts the batch, so the popup can name exactly what did not take.
             *
             * Granting is idempotent — a partner who already holds a live link
             * is `unchanged` and is NOT re-emailed.
             */
            sendDealOffers: async (dealId: string, data: { grant?: string[]; revoke?: string[] }): Promise<{
                dealId: string;
                results: Array<{ partnerId: string; outcome: string; code?: string; reason?: string }>;
                tally: Record<string, number>;
            }> => {
                const resp = await affiliatesRequest(`/admin/deals/${encodeURIComponent(dealId)}/offers`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data)
                });
                return parseBody(resp);
            },

            setIsoStatus: async (affiliateId: string, data: {
                isInternalIso: boolean;
                isoCapabilities?: {
                    mccCodes?: string[];
                    states?: string[];
                    acceptingOrders?: boolean;
                    note?: string;
                };
            }) => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/iso`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data)
                });
                return parseBody(resp);
            },

            // Update affiliate commission.
            //
            // The body MUST be `commissionMode`/`flatFee`/`percentageRate` —
            // those are the only three keys affiliate-service's updateCommission
            // reads. This used to declare `commissionType`/`commissionRate`,
            // which the handler parses out of the body, finds undefined, and
            // writes nothing for: it returned 200 "Commission settings updated"
            // having updated only `updatedAt`. It had no callers, so the silent
            // no-op was never seen.
            //
            // `flatFee` is INTEGER CENTS and the server caps it at 100000 ($1000);
            // `percentageRate` is a percent, 0-100.
            updateCommission: async (affiliateId: string, data: {
                commissionMode: 'flat' | 'percentage' | 'both';
                flatFee?: number;
                percentageRate?: number;
            }) => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/commission`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data)
                });
                return parseBody(resp);
            },

            // Get affiliate program settings
            getSettings: async () => {
                const resp = await affiliatesRequest(`/admin/affiliate-settings`);
                return parseBody(resp);
            },

            // Update affiliate program settings
            updateSettings: async (settings: {
                defaultCommissionType?: 'flat' | 'percentage';
                defaultCommissionRate?: number;
                minPayoutAmount?: number;
                autoApproveAffiliates?: boolean;
                trialDuration?: number;
                cookieDuration?: number;
            }) => {
                const resp = await affiliatesRequest(`/admin/affiliate-settings`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(settings)
                });
                return parseBody(resp);
            },

            // Create trial code
            createTrialCode: async (data: {
                code?: string;
                affiliateId?: string;
                maxUses?: number;
                validDays?: number;
            }) => {
                const resp = await affiliatesRequest(`/admin/trial-codes`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data)
                });
                return parseBody(resp);
            },

            // List trial codes
            listTrialCodes: async (page?: number) => {
                const query = page ? `?page=${page}` : '';
                const resp = await affiliatesRequest(`/admin/trial-codes${query}`);
                return parseBody(resp);
            },

            // Delete trial code
            deleteTrialCode: async (codeId: string) => {
                const resp = await affiliatesRequest(`/admin/trial-codes/${codeId}`, {
                    method: 'DELETE'
                });
                return parseBody(resp);
            },

            // Process payout
            processPayout: async (payoutId: string, data: {
                status: 'completed' | 'failed';
                paymentReference?: string;
                notes?: string;
            }) => {
                const resp = await affiliatesRequest(`/admin/payouts/${payoutId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data)
                });
                return parseBody(resp);
            },

            // Get analytics
            getAnalytics: async (period?: 'week' | 'month' | 'year') => {
                const query = period ? `?period=${period}` : '';
                const resp = await affiliatesRequest(`/admin/affiliate-analytics${query}`);
                return parseBody(resp);
            },

            // Get affiliate referrals (admin view)
            getAffiliateReferrals: async (affiliateId: string, page?: number) => {
                const query = page ? `?page=${page}` : '';
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/referrals${query}`);
                return parseBody(resp);
            },

            // Get affiliate payouts (admin view)
            getAffiliatePayouts: async (affiliateId: string, page?: number) => {
                const query = page ? `?page=${page}` : '';
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/payouts${query}`);
                return parseBody(resp);
            },

            // List all pending payouts (admin)
            listPayouts: async (status?: string) => {
                const query = status ? `?status=${status}` : '';
                const resp = await affiliatesRequest(`/admin/payouts${query}`);
                return parseBody(resp);
            },

            // Reset affiliate password
            resetPassword: async (affiliateId: string) => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/reset-password`, {
                    method: 'POST'
                });
                return parseBody(resp);
            },

            // Delete affiliate
            deleteAffiliate: async (affiliateId: string) => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}`, {
                    method: 'DELETE'
                });
                return parseBody(resp);
            },

            // Assign deal to affiliate
            assignDeal: async (affiliateId: string, dealId: string | null) => {
                const resp = await affiliatesRequest(`/admin/affiliates/${affiliateId}/deal`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ dealId })
                });
                return parseBody(resp);
            }
        },

        // ---------- TENANT ENDPOINTS (for tenant to share affiliate code) ----------
        tenant: {
            // Get affiliate info for tenant
            getAffiliateInfo: async (tenantId: string) => {
                const resp = await affiliatesRequest(`/tenant/${tenantId}/affiliate`);
                return parseBody(resp);
            }
        }
    },

    // ---------- PUBLIC SUPPORT API (no auth required) ----------
    // Uses super-admin-ext service for scalability
    support: {
        // Submit a public support ticket or contact inquiry (guests and authenticated users)
        submitTicket: async (data: {
            name: string;
            email: string;
            phone?: string;
            subject: string;
            message: string;
            category?: 'general' | 'billing' | 'technical' | 'feature_request' | 'bug_report';
            type?: 'support' | 'contact'; // 'support' for support requests, 'contact' for contact us inquiries
            tenantId?: string;
            businessName?: string;
        }): Promise<{ success: boolean; message: string; ticket: { id: string; ticketNumber: string; status: string } }> => {
            const resp = await publicSuperAdminExtRequest(`/support/tickets`, {
                method: 'POST',
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        }
    },

    // ============================================
    // DEALS MANAGEMENT (super-admin-ext service)
    // ============================================
    deals: {
        // List all deals
        list: async (isActive?: string): Promise<{ deals: any[]; count: number }> => {
            const query = isActive ? `?isActive=${isActive}` : '';
            const resp = await superAdminExtRequest(`/admin/deals${query}`);
            return parseBody(resp);
        },

        // Get a single deal
        get: async (dealId: string): Promise<{ deal: any }> => {
            const resp = await superAdminExtRequest(`/admin/deals/${dealId}`);
            return parseBody(resp);
        },

        /**
         * COPY A DEAL INTO AN INDEPENDENT VARIANT.
         *
         * Hosted on super-admin-ext3, not ext: ext sits at 438/500
         * CloudFormation resources and a platform that mints variants in bulk
         * should not be the thing that pushes it over. The copy is a whole new
         * deal — lineage is recorded for DISPLAY only and nothing resolves
         * through it.
         *
         * Omit `code` to let the server suffix the source's (`X` -> `X-2`).
         * SEND one and it is honoured exactly or refused with 409 — never
         * silently altered, because an operator who typed a code will hand
         * that code to partners.
         */
        duplicate: async (dealId: string, data?: { name?: string; code?: string }): Promise<{ deal: any }> => {
            const resp = await superAdminExt3Request(`/admin/deals/${encodeURIComponent(dealId)}/duplicate`, {
                method: 'POST',
                body: JSON.stringify(data || {}),
            });
            return parseBody(resp);
        },

        /**
         * THE DEAL TAG REGISTRY — the colours and labels the deals list groups
         * by. One platform-settings row; `saveTags` replaces it whole.
         *
         * Deleting a tag here does NOT rewrite the deals carrying it — a reader
         * resolves a deal's tag ids against this list and ignores ids it cannot
         * find. See `deal-tags-put.ts` for why a cosmetic edit must never
         * become a thousand-row write.
         */
        listTags: async (): Promise<{ tags: { id: string; label: string; color: string }[] }> => {
            const resp = await superAdminExt3Request('/admin/deal-tags');
            return parseBody(resp);
        },

        saveTags: async (tags: { id: string; label: string; color: string }[]): Promise<{ tags: any[] }> => {
            const resp = await superAdminExt3Request('/admin/deal-tags', {
                method: 'PUT',
                body: JSON.stringify({ tags }),
            });
            return parseBody(resp);
        },

        // Create a new deal
        //
        // A deal now carries its OWN price: the API accepts a name plus EITHER
        // a `stripePriceId` (the legacy path) OR `priceCents` (the builder).
        // `stripePriceId` and `monthlyAmount` are deprecated and never removed.
        create: async (data: {
            name: string;
            code?: string;
            stripePriceId?: string;
            /** @deprecated superseded by `priceCents`; the API mirrors priceCents into it. */
            monthlyAmount?: number;
            /** The deal's own monthly price in integer cents — authoritative at signup. */
            priceCents?: number;
            /** packageId -> the discount given on that package. Same shape as a PlanAssignment's. */
            packageDiscounts?: Record<string, { percentOff?: number; amountOffCents?: number; reason?: string }>;
            /** Device SKUs offered with this deal. Stored under `deviceSkuIds`; send ONE name, or both with identical contents. */
            devicePackageIds?: string[];
            deviceSkuIds?: string[];
            trialDays?: number;
            commissionMode?: 'flat' | 'percentage' | 'both';
            flatFee?: number;
            percentageRate?: number;
            showOnLandingPage?: boolean;
            landingPageLabel?: string;
            landingPageDescription?: string;
            isDefaultDeal?: boolean;
            maxUses?: number;
            expiresAt?: number;
            productId?: string;
            packageIds?: string[];
            /** REQUIRED (2026-08-26 Plan & Packages spec): locations included free with this deal. */
            freeLocationQuota?: number;
            /** REQUIRED (2026-08-26): monthly price per location beyond the quota, integer cents. */
            additionalLocationPriceCents?: number;
        }): Promise<{ deal: any }> => {
            const resp = await superAdminExtRequest(`/admin/deals`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },

        // Update a deal. Partial — only the fields present are written.
        update: async (dealId: string, data: {
            name?: string;
            code?: string;
            /** Send '' to CLEAR it; omitting it leaves the stored value in force. */
            stripePriceId?: string;
            /** @deprecated superseded by `priceCents`; the API mirrors priceCents into it. */
            monthlyAmount?: number;
            /** The deal's own monthly price in integer cents — takes precedence on every read. */
            priceCents?: number;
            packageDiscounts?: Record<string, { percentOff?: number; amountOffCents?: number; reason?: string }>;
            devicePackageIds?: string[];
            deviceSkuIds?: string[];
            trialDays?: number;
            commissionMode?: 'flat' | 'percentage' | 'both';
            flatFee?: number;
            percentageRate?: number;
            showOnLandingPage?: boolean;
            landingPageLabel?: string;
            landingPageDescription?: string;
            isDefaultDeal?: boolean;
            maxUses?: number;
            expiresAt?: number;
            isActive?: boolean;
            productId?: string;
            packageIds?: string[];
            /** Required on any pricing-relevant update once the deal is edited (2026-08-26 spec). */
            freeLocationQuota?: number;
            additionalLocationPriceCents?: number;
        }): Promise<{ deal: any }> => {
            const resp = await superAdminExtRequest(`/admin/deals/${dealId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },

        // Delete (deactivate) a deal
        delete: async (dealId: string): Promise<{ message: string }> => {
            const resp = await superAdminExtRequest(`/admin/deals/${dealId}`, {
                method: 'DELETE'
            });
            return parseBody(resp);
        },

        // Get the default deal
        getDefault: async (): Promise<{ deal: any }> => {
            const resp = await superAdminExtRequest(`/admin/deals/default`);
            return parseBody(resp);
        },

        // Get deal statistics
        getStats: async (): Promise<{ stats: any }> => {
            const resp = await superAdminExtRequest(`/admin/deals/stats`);
            return parseBody(resp);
        },

        // Get Stripe prices for selection
        getStripePrices: async (): Promise<{ prices: any[] }> => {
            const resp = await superAdminExtRequest(`/admin/stripe/prices`);
            return parseBody(resp);
        },

        // Public: Get deal by code (for landing page validation)
        getDealByCode: async (code: string): Promise<{ valid: boolean; type: string; deal?: any; affiliateId?: string }> => {
            const resp = await publicSuperAdminExtRequest(`/public/deals/${code}`);
            return parseBody(resp);
        },

        // Public: Get deals to show on landing page
        getLandingPageDeals: async (): Promise<{ deals: any[] }> => {
            const resp = await publicSuperAdminExtRequest(`/public/deals/landing-page`);
            return parseBody(resp);
        }
    },

    // ==========================================
    // PACKAGES & PRODUCTS
    // ==========================================
    // Package families / tiers (super-admin-ext2, spec 2026-09-26 + 2026-09-27).
    // Tier ids are always read from the family record — never constants.
    packageFamilies: {
        list: async (): Promise<{ families: any[] }> => {
            const resp = await superAdminExt2Request(`/admin/package-families`);
            return parseBody(resp);
        },
        create: async (body: Record<string, unknown>): Promise<{ family: any }> => {
            const resp = await superAdminExt2Request(`/admin/package-families`, { method: 'POST', body: JSON.stringify(body) });
            return parseBody(resp);
        },
        get: async (familyId: string): Promise<{
            family: any; tiers: any[]; addons: any[]; missingPackageIds: string[];
        }> => {
            const resp = await superAdminExt2Request(`/admin/package-families/${encodeURIComponent(familyId)}`);
            return parseBody(resp);
        },
        /** Partial update; 409 on a concurrent change. */
        update: async (familyId: string, patch: Record<string, unknown>): Promise<{ family: any }> => {
            const resp = await superAdminExt2Request(`/admin/package-families/${encodeURIComponent(familyId)}`, {
                method: 'PUT',
                body: JSON.stringify(patch),
            });
            return parseBody(resp);
        },
    },

    packages: {
        // List all packages
        list: async (): Promise<{ packages: any[] }> => {
            const resp = await superAdminExtRequest(`/admin/packages`);
            return parseBody(resp);
        },

        /** Like list(), minus Basic/Pro tier rows — pickers show one row per package. */
        listVisible: async (): Promise<{ packages: any[] }> => {
            const [p, f] = await Promise.all([
                superAdminExtRequest(`/admin/packages`).then(parseBody),
                superAdminExt2Request(`/admin/package-families`).then(parseBody).catch(() => ({ families: [] })),
            ]);
            return { packages: visiblePackages((p as any).packages || [], (f as any).families || []) };
        },

        // Get a single package
        get: async (packageId: string): Promise<{ package: any }> => {
            const resp = await superAdminExtRequest(`/admin/packages/${packageId}`);
            return parseBody(resp);
        },

        // Create a new package
        create: async (data: {
            name: string;
            description: string;
            productType?: string | null;
            category?: 'shared' | 'product-specific';
            features?: string[];
            services?: string[];
            basePrice?: number;
            displayOrder?: number;
            isCore?: boolean;
            /** On the open shelf — any tenant may buy it. Refused without a basePrice. */
            marketplace?: boolean;
            dependencies?: string[];
        }): Promise<{ package: any }> => {
            const resp = await superAdminExtRequest(`/admin/packages`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },

        // Update a package
        update: async (packageId: string, data: {
            name?: string;
            description?: string;
            productType?: string | null;
            category?: 'shared' | 'product-specific';
            status?: 'active' | 'deprecated' | 'coming_soon';
            features?: string[];
            services?: string[];
            basePrice?: number;
            displayOrder?: number;
            isCore?: boolean;
            /** On the open shelf — any tenant may buy it. Refused without a basePrice. */
            marketplace?: boolean;
            dependencies?: string[];
            menuGroupId?: string;
            /** Package-tier entitlement rules (spec 2026-09-26). Replaces the whole array. */
            entitlements?: unknown[];
        }): Promise<{ package: any }> => {
            const resp = await superAdminExtRequest(`/admin/packages/${packageId}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },

        // Delete a package
        // Set force=true to cancel all tenant assignments before deleting
        delete: async (packageId: string, force?: boolean): Promise<{ message: string; assignmentsCanceled?: number }> => {
            const url = force
                ? `/admin/packages/${packageId}?force=true`
                : `/admin/packages/${packageId}`;
            const resp = await superAdminExtRequest(url, {
                method: 'DELETE',
            });
            return parseBody(resp);
        },

        // List all products
        listProducts: async (): Promise<{ products: any[] }> => {
            const resp = await superAdminExtRequest(`/admin/products`);
            return parseBody(resp);
        },

        // Get a single product
        getProduct: async (productId: string): Promise<{ product: any }> => {
            const resp = await superAdminExtRequest(`/admin/products/${productId}`);
            return parseBody(resp);
        },

        // Create a new product
        createProduct: async (data: {
            id: string;
            name: string;
            description?: string;
            domain: string;
            devDomain?: string;
            appDomain?: string;
            devAppDomain?: string;
            defaultPackages?: string[];
            availablePackages?: string[];
            theme?: { primaryColor: string; secondaryColor: string };
            status?: 'active' | 'coming_soon' | 'deprecated';
            defaultRoute?: string;
            features?: string[];
        }): Promise<{ product: any }> => {
            const resp = await superAdminExtRequest(`/admin/products`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },

        // Update a product
        updateProduct: async (productId: string, data: {
            name?: string;
            description?: string;
            domain?: string;
            devDomain?: string;
            appDomain?: string;
            devAppDomain?: string;
            defaultPackages?: string[];
            availablePackages?: string[];
            theme?: { primaryColor: string; secondaryColor: string };
            status?: 'active' | 'coming_soon' | 'deprecated';
            defaultRoute?: string;
            features?: string[];
            syncTenants?: boolean; // Auto-sync packages to existing tenants
        }): Promise<{ product: any; tenantsSynced?: number }> => {
            const resp = await superAdminExtRequest(`/admin/products/${productId}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },

        // Delete a product
        deleteProduct: async (productId: string): Promise<{ message: string }> => {
            const resp = await superAdminExtRequest(`/admin/products/${productId}`, {
                method: 'DELETE',
            });
            return parseBody(resp);
        }
    },

    // ==========================================
    // FEATURES
    // ==========================================
    features: {
        // List all features
        list: async (params?: { category?: string; productType?: string }): Promise<{ features: any[]; count: number }> => {
            const queryParams = new URLSearchParams();
            if (params?.category) queryParams.append('category', params.category);
            if (params?.productType) queryParams.append('productType', params.productType);
            const query = queryParams.toString();
            const resp = await superAdminExtRequest(`/admin/features${query ? `?${query}` : ''}`);
            return parseBody(resp);
        },

        // Get a single feature
        get: async (featureId: string): Promise<{ feature: any }> => {
            const resp = await superAdminExtRequest(`/admin/features/${featureId}`);
            return parseBody(resp);
        },

        // Create a new feature
        create: async (data: {
            id: string;
            name: string;
            description?: string;
            category?: string;
            productType?: string;
            status?: 'active' | 'deprecated';
            displayOrder?: number;
            route?: string;
            icon?: string;
            menuLabel?: string;
            roles?: string[];
            nativeHidden?: boolean;
            menuStyle?: any;
        }): Promise<{ feature: any }> => {
            const resp = await superAdminExtRequest(`/admin/features`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },

        // Update a feature
        update: async (featureId: string, data: {
            name?: string;
            description?: string;
            category?: string;
            productType?: string;
            status?: 'active' | 'deprecated';
            displayOrder?: number;
            route?: string;
            icon?: string;
            menuLabel?: string;
            roles?: string[];
            nativeHidden?: boolean;
            menuStyle?: any;
        }): Promise<{ feature: any }> => {
            const resp = await superAdminExtRequest(`/admin/features/${featureId}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },

        // Delete a feature
        delete: async (featureId: string): Promise<{ message: string }> => {
            const resp = await superAdminExtRequest(`/admin/features/${featureId}`, {
                method: 'DELETE',
            });
            return parseBody(resp);
        },

        // Get public features (no auth)
        listPublic: async (productType?: string): Promise<{ features: any[]; byCategory: Record<string, any[]>; count: number }> => {
            const query = productType ? `?productType=${productType}` : '';
            const resp = await superAdminExtRequest(`/public/features${query}`);
            return parseBody(resp);
        }
    },

    // ==========================================
    // SIDEBAR CONFIGURATION
    // ==========================================
    sidebar: {
        // Get sidebar config for a product
        get: async (productId: string, line?: 'rubyestate'): Promise<{ productId: string; productName: string; sidebarConfig: any }> => {
            const resp = await superAdminExtRequest(`/admin/products/${productId}/sidebar${line ? '?line=' + line : ''}`);
            return parseBody(resp);
        },

        // Save sidebar config for a product
        save: async (productId: string, data: { groups: any[]; routeRegistry?: any[]; updatedBy: string; version?: number }, line?: 'rubyestate'): Promise<{ productId: string; sidebarConfig: any }> => {
            const resp = await superAdminExtRequest(`/admin/products/${productId}/sidebar${line ? '?line=' + line : ''}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },

        // Reset sidebar config to defaults
        reset: async (productId: string, line?: 'rubyestate'): Promise<{ message: string }> => {
            const resp = await superAdminExtRequest(`/admin/products/${productId}/sidebar${line ? '?line=' + line : ''}`, {
                method: 'DELETE',
            });
            return parseBody(resp);
        },

        // Get sidebar version history
        getHistory: async (productId: string, line?: 'rubyestate'): Promise<{ productId: string; history: any[] }> => {
            const resp = await superAdminExtRequest(`/admin/products/${productId}/sidebar/history${line ? '?line=' + line : ''}`);
            return parseBody(resp);
        },

        // Rollback sidebar config to a previous version
        rollback: async (productId: string, version: number, line?: 'rubyestate'): Promise<{ productId: string; sidebarConfig: any; message: string }> => {
            const resp = await superAdminExtRequest(`/admin/products/${productId}/sidebar/rollback${line ? '?line=' + line : ''}`, {
                method: 'POST',
                body: JSON.stringify({ version }),
            });
            return parseBody(resp);
        },

    },

    // =========================================================================
    // DEVOPS / DEPLOYMENT DASHBOARD APIs (devops-service)
    // =========================================================================
    devops: {
        // Get GitHub config (PAT, org)
        getConfig: async () => {
            const resp = await devopsRequest('/admin/devops/config');
            return parseBody(resp);
        },

        // Save GitHub config
        saveConfig: async (data: { pat: string; org: string }) => {
            const resp = await devopsRequest('/admin/devops/config', {
                method: 'PUT',
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },

        // Get repo registry
        getRegistry: async () => {
            const resp = await devopsRequest('/admin/devops/registry', { silent: true } as any);
            return parseBody(resp);
        },

        // Save repo registry
        saveRegistry: async (data: any) => {
            const resp = await devopsRequest('/admin/devops/registry', {
                method: 'PUT',
                body: JSON.stringify(data),
                silent: true
            } as any);
            return parseBody(resp);
        },

        // Get dashboard stats
        getDashboardStats: async (product?: string) => {
            const query = product ? `?product=${product}` : '';
            const resp = await devopsRequest(`/admin/devops/dashboard${query}`);
            return parseBody(resp);
        },

        // List repos
        listRepos: async (params?: { product?: string; category?: string; page?: number; pageSize?: number }) => {
            const searchParams = new URLSearchParams();
            if (params?.product) searchParams.set('product', params.product);
            if (params?.category) searchParams.set('category', params.category);
            if (params?.page) searchParams.set('page', String(params.page));
            if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
            const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
            const resp = await devopsRequest(`/admin/devops/repos${query}`);
            return parseBody(resp);
        },

        // Get repo detail
        getRepoDetail: async (owner: string, repo: string) => {
            const resp = await devopsRequest(`/admin/devops/repos/${owner}/${repo}`);
            return parseBody(resp);
        },

        // List branches
        listBranches: async (owner: string, repo: string) => {
            const resp = await devopsRequest(`/admin/devops/repos/${owner}/${repo}/branches`);
            return parseBody(resp);
        },

        // Get branch graph
        getBranchGraph: async (owner: string, repo: string) => {
            const resp = await devopsRequest(`/admin/devops/repos/${owner}/${repo}/graph`);
            return parseBody(resp);
        },

        // Get run details
        getRunDetails: async (owner: string, repo: string, runId: string | number) => {
            const resp = await devopsRequest(`/admin/devops/repos/${owner}/${repo}/runs/${runId}`);
            return parseBody(resp);
        },

        // Trigger deployment
        triggerDeploy: async (owner: string, repo: string, data: { product: string; environment: string }) => {
            const resp = await devopsRequest(`/admin/devops/repos/${owner}/${repo}/deploy`, {
                method: 'POST',
                body: JSON.stringify(data)
            });
            return parseBody(resp);
        },

        // Refresh cached GitHub data for a single repo
        refreshRepo: async (owner: string, repo: string) => {
            const resp = await devopsRequest(`/admin/devops/repos/${owner}/${repo}/refresh`, {
                method: 'POST'
            });
            return parseBody(resp);
        },

        // Cancel an in-progress GitHub Actions run (Deploy All per-repo "Stop")
        cancelRun: async (owner: string, repo: string, runId: number) => {
            const resp = await devopsRequest(`/admin/devops/repos/${owner}/${repo}/runs/${runId}/cancel`, {
                method: 'POST', silent: true
            } as any);
            return parseBody(resp);
        },

        // Silent versions (no GlobalLoader — dashboard uses skeleton loading instead)
        getConfigSilent: async () => {
            const resp = await devopsRequest('/admin/devops/config', { silent: true } as any);
            return parseBody(resp);
        },
        getDashboardStatsSilent: async (product?: string) => {
            const query = product ? `?product=${product}` : '';
            const resp = await devopsRequest(`/admin/devops/dashboard${query}`, { silent: true } as any);
            return parseBody(resp);
        },
        listReposSilent: async (params?: { product?: string; category?: string }) => {
            const searchParams = new URLSearchParams();
            if (params?.product) searchParams.set('product', params.product);
            if (params?.category) searchParams.set('category', params.category);
            const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
            const resp = await devopsRequest(`/admin/devops/repos${query}`, { silent: true } as any);
            return parseBody(resp);
        },

        // Deploy Lock (silent — no GlobalLoader)
        toggleDeployLock: async (data: { repoName: string; locked: boolean; confirmation: string }) => {
            const resp = await devopsRequest('/admin/devops/deploy-lock', {
                method: 'PUT', body: JSON.stringify(data), silent: true
            } as any);
            return parseBody(resp);
        },

        // Deploy All — get phased plan (no side effects)
        getDeployPlan: async (environment: 'dev' | 'prod', product?: string) => {
            const params = new URLSearchParams({ environment });
            if (product) params.set('product', product);
            const resp = await devopsRequest(`/admin/devops/deploy-plan?${params.toString()}`);
            return parseBody(resp);
        }
    },

    // Location Billing Service
    billingCatalog: {
        listSkus: async (): Promise<{ items: any[]; count: number; invalid: string[] }> =>
            billingServiceRequest('/catalog/skus') as Promise<{ items: any[]; count: number; invalid: string[] }>,
        listMeters: async (): Promise<{ items: any[]; count: number }> =>
            billingServiceRequest('/catalog/meters') as Promise<{ items: any[]; count: number }>,
        getEntitlements: async (tenantId: string): Promise<any> =>
            billingServiceRequest(`/entitlements/${tenantId}`),
        upsertSku: async (sku: Record<string, any>): Promise<{ sku: any }> =>
            billingServiceRequest('/catalog/skus', { method: 'POST', body: JSON.stringify(sku) }) as Promise<{ sku: any }>,
        upsertMeter: async (meter: Record<string, any>): Promise<{ meter: any }> =>
            billingServiceRequest('/catalog/meters', { method: 'POST', body: JSON.stringify(meter) }) as Promise<{ meter: any }>,
        /**
         * Runs a named action block server-side (`POST /tenants/{tenantId}/actions`).
         * `activate_packages` reads what to grant off the tenant's ACTIVE
         * PlanAssignment and writes the UNION of that with `enabledPackages` —
         * additive only, it can never remove a package. See
         * services/shared/billing-service/src/lib/actionBlocks.ts.
         */
        runAction: async (tenantId: string, blockId: string): Promise<{
            runId: string;
            blockId: string;
            intent: 'grant' | 'revoke' | 'none';
            source: 'manual' | 'policy' | 'onboarding';
            actor: string;
            reason?: string;
            status: 'ok' | 'partial' | 'failed';
            steps: Array<{ step: string; ok: boolean; detail?: string; error?: string; degraded?: string }>;
            orderId?: string;
            at: number;
        }> =>
            billingServiceRequest(`/tenants/${tenantId}/actions`, {
                method: 'POST', body: JSON.stringify({ blockId })
            }) as Promise<any>,
    },

    locationBilling: {
        listAllRequests: async (status?: string) => {
            const query = status ? `?status=${status}` : '';
            return locationBillingRequest(`/location-billing/admin/requests${query}`);
        },
        listTenantRequests: async (tenantId: string) => {
            return locationBillingRequest(`/location-billing/admin/tenant/${tenantId}/requests`);
        },
        updateRequest: async (requestId: string, data: { tenantId: string } & Record<string, any>) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        approveRequest: async (requestId: string, data: {
            tenantId: string;
            /** LEGACY: explicit Stripe price id. Omit to approve at the tenant's deal/catalog default price (2026-08-26 spec). */
            priceId?: string;
            isFree?: boolean;
            /** Attributable admin discount off the default price — exactly one of percentOff/amountOffCents. */
            discount?: { percentOff?: number; amountOffCents?: number; reason?: string };
        }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/approve`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        rejectRequest: async (requestId: string, data: { tenantId: string; reason: string }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/reject`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        requestInfo: async (requestId: string, data: { tenantId: string; message: string }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/request-info`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        compRequest: async (requestId: string, data: { tenantId: string }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/comp`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        deactivateRequest: async (requestId: string, data: { tenantId: string }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/deactivate`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        reactivateRequest: async (requestId: string, data: { tenantId: string }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/reactivate`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        changePriceRequest: async (requestId: string, data: {
            tenantId: string;
            /** LEGACY: explicit Stripe price id. Omit to re-derive from the tenant's deal/catalog default (2026-08-26 spec). */
            priceId?: string;
            /** Attributable admin discount off the default price; omit both fields entirely to reset to the default. */
            discount?: { percentOff?: number; amountOffCents?: number; reason?: string };
        }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/change-price`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        makeFreeRequest: async (requestId: string, data: { tenantId: string }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/make-free`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        transferRequest: async (requestId: string, data: { fromTenantId: string; toTenantId: string }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/transfer`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        getDocumentViewUrl: async (requestId: string, tenantId: string, documentId: string) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/document-view`, {
                method: 'POST',
                body: JSON.stringify({ tenantId, documentId }),
            });
        },
        documentAction: async (requestId: string, data: { tenantId: string; documentId: string; action: 'approve' | 'reject'; comment?: string }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/document-action`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        /**
         * UPLOAD A COMPLIANCE DOCUMENT ON THE MERCHANT'S BEHALF.
         *
         * Three steps because S3 takes the bytes directly: sign, PUT, record.
         * The tenant's own uploader is closed to admins twice over — uploads-service
         * signs against the USER pool, and the tenant record route reads tenantId
         * from the JWT — so these are the admin counterparts, not a second store.
         *
         * Lands as `uploaded`, never `verified`: the approve step stays, and the
         * timeline names the admin rather than claiming the merchant sent it.
         */
        uploadDocumentForTenant: async (
            requestId: string,
            data: { tenantId: string; documentId: string; file: File },
        ) => {
            const signed = await locationBillingRequest(
                `/location-billing/admin/requests/${requestId}/document-upload-url`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        tenantId: data.tenantId,
                        documentId: data.documentId,
                        fileName: data.file.name,
                        contentType: data.file.type || 'application/octet-stream',
                        fileSize: data.file.size,
                    }),
                },
            ) as any;
            const uploadUrl = signed?.uploadUrl;
            const fileKey = signed?.fileKey;
            if (!uploadUrl || !fileKey) throw new Error('Could not get an upload URL for that document.');

            // Straight to S3 — never through the API, which has a 10MB payload cap
            // a scanned licence clears without trying.
            const put = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': data.file.type || 'application/octet-stream' },
                body: data.file,
            });
            if (!put.ok) throw new Error(`Upload failed (${put.status}).`);

            return locationBillingRequest(
                `/location-billing/admin/requests/${requestId}/document-uploaded`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        tenantId: data.tenantId,
                        documentId: data.documentId,
                        fileKey,
                        fileName: data.file.name,
                        contentType: data.file.type || 'application/octet-stream',
                    }),
                },
            );
        },
        addDocument: async (requestId: string, data: { tenantId: string; name: string; documentId?: string; comment?: string }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/documents`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        removeDocument: async (requestId: string, documentId: string, tenantId: string) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/documents/${documentId}?tenantId=${encodeURIComponent(tenantId)}`, {
                method: 'DELETE',
            });
        },
        /** Takes the file off a document (back to `requested`); the requirement stays. */
        clearDocumentFile: async (requestId: string, documentId: string, tenantId: string) => {
            return locationBillingRequest(
                `/location-billing/admin/requests/${requestId}/documents/${documentId}/clear-file`,
                { method: 'POST', body: JSON.stringify({ tenantId }) },
            );
        },
        listPrices: async (): Promise<{ items: any[] }> => {
            return await locationBillingRequest(`/location-billing/admin/prices`) as { items: any[] };
        },
        requestRegistrationDoc: async (requestId: string, data: { tenantId: string; docType: string; name: string; comment?: string }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/registration-docs/request`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
        registrationDocAction: async (requestId: string, docId: string, data: { tenantId: string; action: 'approve' | 'reject'; comment?: string }) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/registration-docs/${docId}/action`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
        },
    },

    // Marketplace Owner Onboarding (Phase H)
    marketplaceRequest: {
        listAllRequests: async (status?: string) => {
            const query = status ? `?status=${status}` : '';
            return marketplaceOwnerRequest(`/marketplace-owner/admin/requests${query}`);
        },
        getRequest: async (requestId: string, tenantId: string) =>
            marketplaceOwnerRequest(`/marketplace-owner/admin/requests/${requestId}?tenantId=${encodeURIComponent(tenantId)}`),
        approveRequest: async (requestId: string, data: { tenantId: string; ownerLocationId: string; priceId: string }) =>
            marketplaceOwnerRequest(`/marketplace-owner/admin/requests/${requestId}/approve`, { method: 'PATCH', body: JSON.stringify(data) }),
        rejectRequest: async (requestId: string, data: { tenantId: string; reason: string }) =>
            marketplaceOwnerRequest(`/marketplace-owner/admin/requests/${requestId}/reject`, { method: 'PATCH', body: JSON.stringify(data) }),
        requestInfo: async (requestId: string, data: { tenantId: string; message: string }) =>
            marketplaceOwnerRequest(`/marketplace-owner/admin/requests/${requestId}/request-info`, { method: 'PATCH', body: JSON.stringify(data) }),
        documentAction: async (requestId: string, data: { tenantId: string; documentId: string; action: 'approve' | 'reject'; comment?: string }) =>
            marketplaceOwnerRequest(`/marketplace-owner/admin/requests/${requestId}/document-action`, { method: 'POST', body: JSON.stringify(data) }),
    },

    // ============================================
    // LOCATIONS SERVICE (routed through location-billing admin endpoints for auth)
    // ============================================
    locations: {
        getFullLocation: async (requestId: string, tenantId: string) => {
            return locationBillingRequest(`/location-billing/admin/requests/${requestId}/location-full?tenantId=${tenantId}`);
        },
    },

    // ============================================
    // SOCIAL PUBLISHING (social-publish-service, admin mirror of the merchant Online Presence panel)
    // Tenant is named in the route; the AdminAccess authorizer + handler require super_admin.
    // Errors carry the service code on `err.data.code` (FEATURE_OFF, SETUP_PENDING, NOT_YOUR_CONNECTION…).
    // ============================================
    social: {
        channels: (tenantId: string, locationId: string): Promise<SocialChannelsResponse> =>
            socialReq(`${socialAdminBase(tenantId)}/channels?locationId=${encodeURIComponent(locationId)}`, { silent: true }),
        /** Credentialed: the response sets the HttpOnly cookie that binds the OAuth round to this browser. */
        startOAuth: (tenantId: string, provider: 'google' | 'meta', locationId: string, requestId: string): Promise<{ authUrl: string }> =>
            socialReq(`${socialAdminBase(tenantId)}/oauth/${provider}/start`, {
                method: 'POST', credentials: 'include',
                body: JSON.stringify({ locationId, requestId, returnTo: window.location.origin }),
            }),
        connection: (tenantId: string, id: string): Promise<{ connection: SocialConnectionView }> =>
            socialReq(`${socialAdminBase(tenantId)}/connections/${encodeURIComponent(id)}`, { silent: true }),
        select: (tenantId: string, id: string, accounts: SocialAccount[], locationIds: string[]): Promise<{ connection: SocialConnectionView }> =>
            socialReq(`${socialAdminBase(tenantId)}/connections/${encodeURIComponent(id)}/select`, { method: 'POST', body: JSON.stringify({ accounts, locationIds }) }),
        disconnect: (tenantId: string, id: string): Promise<{ status: string }> =>
            socialReq(`${socialAdminBase(tenantId)}/connections/${encodeURIComponent(id)}`, { method: 'DELETE' }),
        sandbox: (tenantId: string, locationId: string, channels: SocialChannel[]): Promise<{ connection: SocialConnectionView }> =>
            socialReq(`${socialAdminBase(tenantId)}/connections/sandbox`, { method: 'POST', body: JSON.stringify({ locationId, channels }) }),
        policy: (tenantId: string): Promise<SocialPolicyResponse> =>
            socialReq(`${socialAdminBase(tenantId)}/policy`, { silent: true }),
        setPolicy: (tenantId: string, categoryId: string, blocked: SocialChannel[]): Promise<{ categoryId: string; blocked: SocialChannel[] }> =>
            socialReq(`${socialAdminBase(tenantId)}/policy`, { method: 'PUT', body: JSON.stringify({ categoryId, blocked }) }),
        recentPosts: (tenantId: string, locationId: string, limit = 5): Promise<{ posts: SocialPost[]; complete?: boolean }> =>
            socialReq(`${socialAdminBase(tenantId)}/posts?locationId=${encodeURIComponent(locationId)}&limit=${limit}`, { silent: true }),
        /** RubyTill's own Google/Meta app keys for THIS stage. Secrets are write-only — never returned. */
        appKeys: (): Promise<SocialAppKeysResponse> =>
            socialReq(`${SOCIAL_API_BASE}/social/admin/app-keys`, { silent: true }),
        saveAppKeys: (provider: 'google' | 'meta', body: { clientId?: string; secret?: string; accessStatus?: string }): Promise<{ provider: SocialAppKeysProviderView }> =>
            socialReq(`${SOCIAL_API_BASE}/social/admin/app-keys/${provider}`, { method: 'PUT', body: JSON.stringify(body) }),
        testAppKeys: (provider: 'google' | 'meta'): Promise<SocialAppKeysTestResponse> =>
            socialReq(`${SOCIAL_API_BASE}/social/admin/app-keys/${provider}/test`, { method: 'POST' }),
    },

    // ============================================
    // POS DEFAULTS (super-admin-ext2) — the GLOBAL RubyTill layout + receipt
    // default every business falls back to until it saves its own.
    // ============================================
    posDefaults: {
        getLayout: async (): Promise<{
            layout: { pinned: string[]; keypadSlots: Record<string, { action: string; label?: string } | null>; buttonCustomizations: Record<string, any>; headerButtons?: Record<string, any>[] };
            customerDisplayConfig: any;
            theme: 'light' | 'dark';
            updatedAt: number | null;
            updatedBy: string | null;
            source: 'stored' | 'code';
        }> => {
            const resp = await superAdminExt2Request(`/admin/pos-defaults/layout`);
            return parseBody(resp);
        },
        putLayout: async (body: {
            layout: { pinned: string[]; keypadSlots: Record<string, { action: string; label?: string } | null>; buttonCustomizations: Record<string, any>; headerButtons?: Record<string, any>[] };
            customerDisplayConfig: any;
            theme: 'light' | 'dark';
        }): Promise<any> => {
            const resp = await superAdminExt2Request(`/admin/pos-defaults/layout`, { method: 'PUT', body: JSON.stringify(body) });
            return parseBody(resp);
        },
        getReceipt: async (): Promise<{ config: any; updatedAt: number | null; updatedBy: string | null; source: 'stored' | 'code' }> => {
            const resp = await superAdminExt2Request(`/admin/pos-defaults/receipt`);
            return parseBody(resp);
        },
        putReceipt: async (config: any): Promise<any> => {
            const resp = await superAdminExt2Request(`/admin/pos-defaults/receipt`, { method: 'PUT', body: JSON.stringify({ config }) });
            return parseBody(resp);
        },
    },

    // ============================================
    // POS CONFIG (super-admin-ext2 service)
    // ============================================
    posConfig: {
        /**
         * The location's TIP CHOICES, SURCHARGE RATE and CARD MINIMUM — the same
         * write the merchant makes (ext2 PUT /pos/locations/{loc}/nmi-cloud-config,
         * super admin acting for the named tenant). Changed keys only: absent =
         * keep. Writes the location's gateway row; the server pushes its tills.
         */
        putLocationPaymentValues: async (tenantId: string, locationId: string, body: {
            tenantTipConfig?: { percentages?: number[]; allowCustom?: boolean; allowSkip?: boolean };
            tenantSurchargeConfig?: { rateOverride?: number | null; flatOverride?: number | null };
            minCardAmount?: number;
        }): Promise<any> => {
            const resp = await superAdminExt2Request(`/pos/locations/${encodeURIComponent(locationId)}/nmi-cloud-config?tenantId=${encodeURIComponent(tenantId)}`, {
                method: 'PUT',
                headers: { 'x-tenant-id': tenantId },
                body: JSON.stringify(body),
            });
            return parseBody(resp);
        },
        // --- Registers ---
        listRegisters: async (tenantId?: string): Promise<any> => {
            const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
            const resp = await superAdminExt2Request(`/pos/registers${qs}`);
            return parseBody(resp);
        },
        /**
         * Registers behind the fleet's deviceIds, across every tenant.
         * `listRegisters` cannot serve the fleet — it requires a tenantId.
         *
         * PAGED, not capped. The server's schema takes at most 200 ids per
         * call, but the status endpoint returns up to 2000 devices: a
         * `slice(0, 200)` here left devices 201+ with no link AND in neither
         * `missing` nor `unresolved`, so the fleet's own honesty banner never
         * mentioned them — "we could not look but said nothing", at fleet
         * scale, invisible on a small dev fleet.
         *
         * Every requested id comes back in EXACTLY ONE of links / missing /
         * unresolved. A batch that fails (network, 5xx, or a response that
         * does not match the shared schema) puts its ids in `unresolved` —
         * never `missing`, which is a definite "no register" answer — and
         * reports why in `error`, so one bad batch costs that batch only.
         */
        getRegisterLinks: async (deviceIds: string[]): Promise<FleetRegisterLinkResponse & { error?: string }> => {
            const ids = Array.from(new Set(deviceIds.filter(Boolean)));
            if (ids.length === 0) return { links: [], missing: [], unresolved: [] };
            const BATCH = 200; // the server's `FleetRegisterLinkLookupSchema` max
            const out: FleetRegisterLinkResponse & { error?: string } = { links: [], missing: [], unresolved: [] };
            for (let i = 0; i < ids.length; i += BATCH) {
                const batch = ids.slice(i, i + BATCH);
                try {
                    const resp = await superAdminExt2Request('/pos/registers/by-device', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ deviceIds: batch }),
                    });
                    // VALIDATED, not assumed. `super-admin-ext2` hand-mirrors
                    // this schema (it carries no shared-types dependency), and
                    // the ruling that accepted that mirror rested on the client
                    // validating against the shared schema — which it did not:
                    // the only reference here was a TYPE-only import, so a
                    // drift would have been silent bad data.
                    const parsed = FleetRegisterLinkResponseSchema.safeParse(parseBody(resp));
                    if (!parsed.success) {
                        out.unresolved.push(...batch);
                        out.error = out.error
                            || 'Register links unavailable — the lookup returned a response that did not match the expected shape.';
                        continue;
                    }
                    out.links.push(...parsed.data.links);
                    out.missing.push(...parsed.data.missing);
                    out.unresolved.push(...parsed.data.unresolved);
                } catch (e: any) {
                    out.unresolved.push(...batch);
                    out.error = out.error || 'Register links unavailable — the lookup could not be read.';
                }
            }
            return out;
        },
        getRegister: async (registerId: string, tenantId?: string): Promise<any> => {
            const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
            const resp = await superAdminExt2Request(`/pos/registers/${registerId}${qs}`);
            return parseBody(resp);
        },
        createRegister: async (data: any): Promise<any> => {
            const resp = await superAdminExt2Request('/pos/registers', {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },
        updateRegister: async (registerId: string, data: any): Promise<any> => {
            const tid = data?.tenantId ? `?tenantId=${encodeURIComponent(data.tenantId)}` : '';
            const resp = await superAdminExt2Request(`/pos/registers/${registerId}${tid}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },
        /** "Set up POS" — installs the default layout on the buttons already enabled. */
        setupRegister: async (registerId: string, tenantId: string): Promise<any> => {
            const resp = await superAdminExt2Request(
                `/pos/registers/${registerId}/setup?tenantId=${encodeURIComponent(tenantId)}`,
                { method: 'POST', body: '{}' },
            );
            return parseBody(resp);
        },
        deleteRegister: async (registerId: string): Promise<any> => {
            const resp = await superAdminExt2Request(`/pos/registers/${registerId}`, {
                method: 'DELETE',
            });
            return parseBody(resp);
        },

        // --- Payment Gateways ---
        getGateway: async (locationId: string): Promise<any> => {
            const resp = await superAdminExt2Request(`/pos/gateways/${locationId}`);
            return parseBody(resp);
        },
        saveGateway: async (locationId: string, data: any): Promise<any> => {
            const resp = await superAdminExt2Request(`/pos/gateways/${locationId}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },

        // --- EMV Devices ---
        listEmvDevices: async (): Promise<any[]> => {
            const resp = await superAdminExt2Request('/pos/emv-devices');
            return parseBody(resp);
        },
        registerEmvDevice: async (data: any): Promise<any> => {
            const resp = await superAdminExt2Request('/pos/emv-devices', {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },
        deleteEmvDevice: async (deviceId: string): Promise<any> => {
            const resp = await superAdminExt2Request(`/pos/emv-devices/${deviceId}`, {
                method: 'DELETE',
            });
            return parseBody(resp);
        },

        // --- Button Layout ---
        getButtonLayout: async (tenantId: string): Promise<any> => {
            const resp = await superAdminExt2Request(`/pos/button-layout/${tenantId}`);
            return parseBody(resp);
        },
        saveButtonLayout: async (tenantId: string, data: any): Promise<any> => {
            const resp = await superAdminExt2Request(`/pos/button-layout/${tenantId}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },
    },

    // ============================================
    // POS PAYMENT TRANSACTIONS (pos-payments-service)
    // ============================================
    posPayments: {
        // Admin-facing list + timeline live on super-admin-ext3 (Cognito auth) —
        // matching every other admin endpoint. pos-payments-service's /payments
        // also exists for POS terminal (POS JWT) consumers; the admin UI never
        // calls it so we don't hit 401s from the wrong auth.
        list: async (filters: { status?: string; brand?: string; terminalId?: string; cashier?: string; search?: string; from?: string; to?: string; limit?: number; cursor?: string; } = {}): Promise<any> => {
            const tenantId = sessionStorage.getItem('activeTenantId') || localStorage.getItem('activeTenantId') || '';
            if (!tenantId) return { items: [], kpi: null };
            const qs = new URLSearchParams();
            for (const [k, v] of Object.entries(filters)) if (v) qs.set(k, String(v));
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/payments?${qs.toString()}`);
            return parseBody(resp);
        },
        timeline: async (transactionId: string): Promise<any> => {
            const tenantId = sessionStorage.getItem('activeTenantId') || localStorage.getItem('activeTenantId') || '';
            if (!tenantId) return { events: [], payment: null };
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/payments/${encodeURIComponent(transactionId)}/timeline`);
            return parseBody(resp);
        },
        refund: async (body: { transactionId: string; amount: number; reason: string; }): Promise<any> => {
            const base = SUPERADMIN_EXT3_API_BASE.replace('super-admin-ext3', 'pos-payments');
            const tenantId = sessionStorage.getItem('activeTenantId') || localStorage.getItem('activeTenantId') || '';
            return request(`${base}/payments/refund`, {
                method: 'POST',
                headers: { 'x-tenant-id': tenantId },
                body: JSON.stringify(body),
            });
        },
        void: async (body: { transactionId: string; reason: string; }): Promise<any> => {
            const base = SUPERADMIN_EXT3_API_BASE.replace('super-admin-ext3', 'pos-payments');
            const tenantId = sessionStorage.getItem('activeTenantId') || localStorage.getItem('activeTenantId') || '';
            return request(`${base}/payments/void`, {
                method: 'POST',
                headers: { 'x-tenant-id': tenantId },
                body: JSON.stringify(body),
            });
        },
    },

    // ============================================
    // POS NMI / Payment Administration (super-admin-ext3)
    // ============================================
    posNmi: {
        // --- Payment Types (with server-side mutex) ---
        getPaymentTypes: async (tenantId: string, terminalId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/payment-types`);
            return parseBody(resp);
        },
        /**
         * CHANGED FIELDS ONLY. The handler treats every absent field as KEEP, so a
         * caller sends what it changed and nothing else — re-sending a field
         * seeded from the GET is how `overrideActive` and "follows location"
         * surcharge used to be clobbered. `surchargeApply: null` returns the
         * register to following the location.
         */
        putPaymentTypes: async (tenantId: string, terminalId: string, body: { assigned?: string[]; enabled?: string[]; overrideActive?: boolean; tipConfig?: unknown; surchargeApply?: boolean | null; debitEnabled?: boolean | null; emvDeviceId?: string | null; signatureRequired?: Record<string, boolean>; /** SUNMI T3 only, sent alone; null clears. */ cardSetup?: unknown; }): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/payment-types`, {
                method: 'PUT',
                body: JSON.stringify(body),
            });
            return parseBody(resp);
        },
        /**
         * SUNMI card setup — point a register at another catalog model (the
         * Single/Dual variant switch, and the Details tab's "till reports a
         * different model" fix). Spec: docs/agenticpos-rubytill/sunmi-card-setup.md.
         */
        /** The register's catalog model, what the till reports, and whether they disagree. */
        getTerminalDeviceModel: async (tenantId: string, terminalId: string): Promise<{
            deviceModelId: string | null;
            model: { modelId: string; modelName: string; brand?: string; buildTarget?: string | null; hardwareIds?: string[]; cardSetupPolicy?: unknown } | null;
            reported: { hardwareModel?: string | null; hardwareBrand?: string | null; buildTarget?: string | null; appVersion?: string | null; updatedAt?: unknown } | null;
            mismatch: boolean;
            suggestedModels: Array<{ modelId: string; modelName: string; buildTarget?: string | null }>;
        }> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/device-model`);
            return parseBody(resp);
        },
        setTerminalDeviceModel: async (tenantId: string, terminalId: string, deviceModelId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/device-model`, {
                method: 'PUT',
                body: JSON.stringify({ deviceModelId }),
            });
            return parseBody(resp);
        },
        /**
         * COPY THIS TILL'S CHANGED REGISTER FIELDS TO OTHER TILLS.
         * `dryRun` answers what each other till would change without writing;
         * the server pushes each changed till itself.
         */
        bulkPaymentTypes: async (tenantId: string, body: {
            scope: 'location' | 'tenant';
            locationId?: string;
            excludeTerminalId: string;
            dryRun?: boolean;
            fields: {
                cardEnabled?: boolean;
                signatureRequired?: { cash?: boolean; credit?: boolean; debit?: boolean };
                surchargeApply?: boolean | null;
                debitEnabled?: boolean | null;
            };
        }): Promise<{ results: Array<{
            terminalId: string; registerName?: string; locationId?: string; locationName?: string;
            changed: Array<{ field: string; from: unknown; to: unknown }>; skipped?: string | boolean;
        }> }> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/payment-types/bulk`, {
                method: 'POST',
                body: JSON.stringify(body),
            });
            return parseBody(resp);
        },

        // --- EMV Device ---
        getEmvDevice: async (tenantId: string, terminalId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/emv-device`);
            return parseBody(resp);
        },
        /**
         * Assign an existing pool device to a terminal. `registerEmvDeviceToLocation`
         * is the registration call; this one only binds an already-registered
         * device.
         */
        assignEmvDevice: async (tenantId: string, terminalId: string, body: { deviceId: string; force?: boolean; }): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/emv-device`, {
                method: 'POST',
                body: JSON.stringify(body),
            });
            return parseBody(resp);
        },
        unassignEmvDevice: async (tenantId: string, terminalId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/emv-device`, {
                method: 'DELETE',
            });
            return parseBody(resp);
        },

        // --- Location-scoped device pool ---
        listEmvDevicesAtLocation: async (tenantId: string, locationId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/emv-devices`);
            return parseBody(resp);
        },
        /** `paymentConfigId` is the device's chosen lane and outranks `model`, which
         *  the service otherwise resolves by exact NAME match (defaulting anything
         *  unrecognised to NMI cloud). Send it whenever the catalog row has one. */
        registerEmvDeviceToLocation: async (tenantId: string, locationId: string, body: { deviceName: string; model?: string; paymentConfigId?: string; serialNumber?: string; registrationCode?: string; connectionType?: string; spinTpn?: string; spinRegisterId?: string; spinAuthKey?: string; spinBaseUrl?: string; iposApiKey?: string; iposSecretKey?: string; rtpayMode?: 'charge_only' | 'customer_display'; rtpayPin?: string; rtpayCardEngine?: 'dvpaylite' | 'tap_to_pay' | 'sunbay'; rtpayMerchantCode?: string; }): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/emv-devices`, {
                method: 'POST',
                body: JSON.stringify(body),
            });
            return parseBody(resp);
        },

        // --- RubyTill Pay (our app on a P-series terminal) — docs/agenticpos-rubytill/rubytill-pay.md ---
        /** `{rtpay (hasPin, never the hash), status: {paired, online, lastSeenAt, appVersion}, pairing?: {expiresAt}}` */
        getRtpay: async (tenantId: string, deviceId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/emv-devices/${encodeURIComponent(deviceId)}/rtpay`);
            return parseBody(resp);
        },
        /** CHANGED FIELDS ONLY — the server pushes the new config to the terminal. */
        putRtpay: async (tenantId: string, deviceId: string, body: { mode?: 'charge_only' | 'customer_display'; enabled?: boolean; settings?: Record<string, unknown>; pin?: string; merchantCode?: string; }): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/emv-devices/${encodeURIComponent(deviceId)}/rtpay`, {
                method: 'PUT',
                body: JSON.stringify(body),
            });
            return parseBody(resp);
        },
        /** `{code, expiresAt}` — 6 digits, 10 minutes, single use; replaces any earlier code. */
        newRtpayPairingCode: async (tenantId: string, deviceId: string): Promise<{ code: string; expiresAt: string | number }> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/emv-devices/${encodeURIComponent(deviceId)}/rtpay/pairing-code`, {
                method: 'POST',
            });
            return parseBody(resp);
        },
        /** Unpair — bumps the token version, so the terminal's old token stops working. */
        unpairRtpay: async (tenantId: string, deviceId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/emv-devices/${encodeURIComponent(deviceId)}/rtpay/pairing`, {
                method: 'DELETE',
            });
            return parseBody(resp);
        },

        // --- Online Payment Processors (Dejavoo Cloud TPNs — separate from physical EMV) ---
        /** Register a Dejavoo Cloud processor (TPN + Auth Token) to a home location. */
        registerCloudProcessor: async (tenantId: string, locationId: string, body: { deviceName: string; spinTpn: string; iposAuthToken: string; iposApiKey?: string; iposSecretKey?: string; }): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/emv-devices`, {
                method: 'POST',
                // The config id, not the magic model name, is what decides the lane.
                // `model` stays for the audit row and for older service builds.
                body: JSON.stringify({ ...body, model: 'Dejavoo Cloud POS', paymentConfigId: 'dejavoo_cloud' }),
            });
            return parseBody(resp);
        },
        /** Tenant-wide list of Dejavoo Cloud processors (all locations). */
        listOnlineProcessors: async (tenantId: string): Promise<{ processors: Array<{ deviceId: string; deviceName: string; locationId: string; tpnMasked?: string | null; hasCreds?: boolean; hasAuthToken?: boolean; status: string; kind?: 'dejavoo' | 'magtek'; readiness?: { unigate: 'location' | 'platform' | null; teconnect: 'location' | 'platform' | null } }> }> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/online-processors`);
            return parseBody(resp);
        },
        /** Delete a cloud processor (mistaken-add cleanup). 409 if still assigned to a terminal. */
        deleteProcessor: async (tenantId: string, deviceId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/online-processors/${encodeURIComponent(deviceId)}`, {
                method: 'DELETE',
            });
            return parseBody(resp);
        },

        // --- Online Terminals (virtual "online_sales" terminal, no provisioning code) ---
        // Pass emvDeviceId to reference an existing processor, OR newProcessor to
        // create a Dejavoo Cloud processor inline (combined one-step flow).
        createOnlineTerminal: async (tenantId: string, body: { locationId: string; locationName?: string; name?: string; emvDeviceId?: string; newProcessor?: { kind?: 'magtek'; deviceName?: string; spinTpn?: string; iposAuthToken?: string; iposApiKey?: string; iposSecretKey?: string } }): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/online-terminals`, {
                method: 'POST',
                body: JSON.stringify(body),
            });
            return parseBody(resp);
        },
        /** Change ({emvDeviceId}) or remove ({emvDeviceId:null}) an online terminal's processor. */
        updateOnlineTerminal: async (tenantId: string, registerId: string, body: { emvDeviceId?: string | null; name?: string; status?: 'active' | 'inactive' | 'archived' }): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/online-terminals/${encodeURIComponent(registerId)}`, {
                method: 'PATCH',
                body: JSON.stringify(body),
            });
            return parseBody(resp);
        },
        /** Hard-delete an online terminal (its referenced processor is left intact). */
        deleteOnlineTerminal: async (tenantId: string, registerId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/online-terminals/${encodeURIComponent(registerId)}`, {
                method: 'DELETE',
            });
            return parseBody(resp);
        },
        updateEmvDevice: async (tenantId: string, locationId: string, deviceId: string, body: {
            deviceName?: string;
            active?: boolean;
            /** One of the on-terminal configuration steps, ticked or unticked.
             *  The service enforces their order — see `EMV_CONFIG_STEPS`. */
            configStep?: 'appInstalled' | 'paramsSetup' | 'paramsDownloaded';
            configStepDone?: boolean;
            /** MagTek rows only: 'MagTek DynaFlex II PED' | 'MagTek DynaFlex II Go' (refused while bound to a till). */
            magtekModel?: string;
            /** SUNBAY rows only: where Tapro runs relative to the till. */
            sunbayConnection?: 'sub_screen' | 'on_device' | 'cable_aoa' | 'cable_vsp';
            /** The narrow first version of `deviceConfig`; still accepted. */
            spinCredentials?: { spinRegisterId: string; spinAuthKey: string };
            /**
             * Edit the device row after registration. A PARTIAL — only the keys
             * sent are written, and a secret left out keeps its stored value
             * (we are only ever given a mask, so absent can never mean "clear").
             */
            deviceConfig?: {
                serialNumber?: string;
                spinRegisterId?: string;
                /** Cloud lane only — USB is issued no Auth Key. */
                spinAuthKey?: string;
                spinTpn?: string;
                iposApiKey?: string;
                iposSecretKey?: string;
            };
        }): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/emv-devices/${encodeURIComponent(deviceId)}`, {
                method: 'PATCH',
                body: JSON.stringify(body),
            });
            return parseBody(resp);
        },
        /**
         * `force` detaches a device that is still bound to a terminal: the
         * service releases the binding and strips the terminal's card lane
         * before deregistering, instead of refusing with DEVICE_IN_USE. Only
         * ever sent after the operator has confirmed the terminal it names.
         */
        deregisterEmvDevice: async (tenantId: string, locationId: string, deviceId: string, force = false): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/emv-devices/${encodeURIComponent(deviceId)}${force ? '?force=true' : ''}`, {
                method: 'DELETE',
            });
            return parseBody(resp);
        },
        replaceEmvDevice: async (tenantId: string, terminalId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/emv-device/replace`, {
                method: 'POST',
            });
            return parseBody(resp);
        },
        pingEmvDevice: async (tenantId: string, terminalId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/emv-device/ping`, {
                method: 'POST',
            });
            return parseBody(resp);
        },

        // --- MagTek UniGate credentials (write-only; responses carry status, never a value) ---
        /** Platform default (SSM). → {set, environment, baseUrlHost, updatedAt, updatedBy} */
        getMagtekUnigatePlatform: async (): Promise<any> => parseBody(await superAdminExt3Request('/platform/payment-processors/magtek-unigate')),
        /** A UniGate PUT, a TEConnect-only PUT ({teconnect}), or both — what the body omits is kept. */
        putMagtekUnigatePlatform: async (body: { customerCode?: string; username?: string; password?: string; environment?: 'pilot' | 'live'; baseUrl?: string; confirmHost?: boolean; teconnect?: { publicKey: string; googleMerchantId?: string; googleGatewayId?: string; appleMerchantId?: string; environment: 'sandbox' | 'production' } }): Promise<any> =>
            parseBody(await superAdminExt3Request('/platform/payment-processors/magtek-unigate', { method: 'PUT', body: JSON.stringify(body) })),
        deleteMagtekUnigatePlatform: async (): Promise<any> =>
            parseBody(await superAdminExt3Request('/platform/payment-processors/magtek-unigate', { method: 'DELETE' })),
        /** A location's own UniGate account. → {source: 'location'|'platform', set} */
        getLocationMagtekCreds: async (tenantId: string, locationId: string): Promise<any> =>
            parseBody(await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway/magtek`)),
        putLocationMagtekCreds: async (tenantId: string, locationId: string, body: { customerCode?: string; username?: string; password?: string; environment?: 'pilot' | 'live'; baseUrl?: string; confirmHost?: boolean; teconnect?: { publicKey: string; googleMerchantId?: string; googleGatewayId?: string; appleMerchantId?: string; environment: 'sandbox' | 'production' } }): Promise<any> =>
            parseBody(await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway/magtek`, { method: 'PUT', body: JSON.stringify(body) })),
        // --- SUNBAY Nexus (write-only; responses carry status, never a secret) ---
        getSunbayNexusPlatform: async (): Promise<any> => parseBody(await superAdminExt3Request('/platform/payment-processors/sunbay-nexus')),
        putSunbayNexusPlatform: async (body: { appId: string; apiKey: string; secretKey: string; webhookSecret: string; baseUrl: string; environment: 'sandbox' | 'production'; confirmHost?: boolean }): Promise<any> =>
            parseBody(await superAdminExt3Request('/platform/payment-processors/sunbay-nexus', { method: 'PUT', body: JSON.stringify(body) })),
        deleteSunbayNexusPlatform: async (): Promise<any> =>
            parseBody(await superAdminExt3Request('/platform/payment-processors/sunbay-nexus', { method: 'DELETE' })),
        getLocationSunbay: async (tenantId: string, locationId: string): Promise<any> =>
            parseBody(await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway/sunbay`)),
        putLocationSunbay: async (tenantId: string, locationId: string, body: { merchantId: string; storeId?: string }): Promise<any> =>
            parseBody(await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway/sunbay`, { method: 'PUT', body: JSON.stringify(body) })),
        deleteLocationSunbay: async (tenantId: string, locationId: string): Promise<any> =>
            parseBody(await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway/sunbay`, { method: 'DELETE' })),
        deleteLocationMagtekCreds: async (tenantId: string, locationId: string): Promise<any> =>
            parseBody(await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway/magtek`, { method: 'DELETE' })),

        // --- Payment Gateway ---
        getPaymentGateway: async (tenantId: string, locationId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway`);
            return parseBody(resp);
        },
        putPaymentGateway: async (tenantId: string, locationId: string, body: Record<string, unknown>): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway`, {
                method: 'PUT',
                body: JSON.stringify(body),
            });
            return parseBody(resp);
        },
        testPaymentGateway: async (tenantId: string, locationId: string, body?: { terminalIdNmi?: string }): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway/test-connection`, {
                method: 'POST',
                body: JSON.stringify(body || {}),
            });
            return parseBody(resp);
        },

        // --- Surcharge Agreements ---
        presignAgreementUpload: async (
            tenantId: string,
            locationId: string,
            body: { fileName: string; contentType?: string; sizeBytes?: number },
        ): Promise<{ agreementId: string; s3Key: string; uploadUrl: string; contentType: string; expiresInSec: number }> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway/agreements/presign`, {
                method: 'POST',
                body: JSON.stringify(body),
            });
            return parseBody(resp);
        },
        confirmAgreementUpload: async (
            tenantId: string,
            locationId: string,
            body: { agreementId: string; s3Key: string; fileName: string; agreementName: string; maxRate: number; maxFlat: number; contentType?: string; sizeBytes?: number; effectiveDate?: string; version?: string; notes?: string; setActive?: boolean },
        ): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway/agreements/confirm`, {
                method: 'POST',
                body: JSON.stringify(body),
            });
            return parseBody(resp);
        },
        setActiveAgreement: async (tenantId: string, locationId: string, agreementId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway/agreements/${encodeURIComponent(agreementId)}/set-active`, {
                method: 'PATCH',
            });
            return parseBody(resp);
        },
        getAgreementDownloadUrl: async (tenantId: string, locationId: string, agreementId: string): Promise<{ agreementId: string; fileName: string; downloadUrl: string; expiresInSec: number }> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway/agreements/${encodeURIComponent(agreementId)}/download-url`);
            return parseBody(resp);
        },
        deleteAgreement: async (tenantId: string, locationId: string, agreementId: string): Promise<any> => {
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/payment-gateway/agreements/${encodeURIComponent(agreementId)}`, {
                method: 'DELETE',
            });
            return parseBody(resp);
        },

        // --- Audit Log ---
        getAuditLog: async (tenantId: string, entity?: string, limit = 50): Promise<any> => {
            const qs = `?limit=${limit}${entity ? `&entity=${encodeURIComponent(entity)}` : ''}`;
            const resp = await superAdminExt3Request(`/tenants/${encodeURIComponent(tenantId)}/audit-log/payment-config${qs}`);
            return parseBody(resp);
        },
    },

    // ============================================
    // POS TERMINAL PROVISIONING (super-admin-ext2)
    // ============================================
    /**
     * PREPARING A TILL FOR THE MERCHANT — the admin counterpart to the
     * merchant-pool routes in pos-auth-service, which a super admin cannot call.
     * Returns the new PIN ONCE; it is never readable from here again.
     */
    posAuth: {
        /** What was already handed over, or `null`. Lets a refresh restore the
         *  PIN instead of pushing the operator into issuing a new one over a
         *  card that is already printed. */
        getHandover: async (tenantId: string, who: { userId?: string; ownerEmail?: string }): Promise<{
            handover: null | {
                at: number;
                pin: string | null;
                pinViewableLater: boolean;
                owner: { userId: string; email: string | null; name: string | null };
                role: { roleGroupId: string | null; name: string | null; created: boolean; resolvedPosButtons: string[] };
            };
        }> => {
            const qs = new URLSearchParams(
                who.userId ? { userId: who.userId } : { ownerEmail: who.ownerEmail || '' },
            ).toString();
            const resp = await request(
                `${resolveServiceBase('pos-auth')}/admin/tenants/${encodeURIComponent(tenantId)}/pos-handover?${qs}`,
            );
            return parseBody(resp) as any;
        },

        /**
         * `locationId` is what turns the owner's Location Access on for the
         * site the till is going to — without it they get a role and a PIN and
         * a business with every location toggled off.
         *
         * `platformMenuAccess` is sent from here because the menu registry is
         * the per-product sidebar config, which this app owns and pos-auth
         * does not. See `collectMenuIds` in TillHandoverPanel.
         */
        handover: async (tenantId: string, who: {
            userId?: string;
            ownerEmail?: string;
            locationId?: string;
            platformMenuAccess?: string[];
            /** Mint a NEW pin, invalidating the one already printed and
             *  emailed. Omitted, a re-run returns the PIN they already have —
             *  which is what makes this safe to press once per till on a
             *  multi-machine order. */
            reissuePin?: boolean;
        }): Promise<{
            pin: string;
            pinReused: boolean;
            pinViewableLater: boolean;
            owner: { userId: string; email: string | null; name: string | null };
            role: {
                roleGroupId: string; name: string; created: boolean; resolvedPosButtons: string[];
                posButtonAccess: string[]; platformMenuAccess: string[];
            };
            locationAccess: null | { locationId: string; role: string; hourlyRate: number; created: boolean };
            at: number;
        }> => {
            const resp = await request(
                `${resolveServiceBase('pos-auth')}/admin/tenants/${encodeURIComponent(tenantId)}/pos-handover`,
                { method: 'POST', body: JSON.stringify(who) },
            );
            return parseBody(resp) as any;
        },
    },

    posTerminals: {
        /** Soft deregister: activated → pending. The SAME code can be re-entered
         *  on the customer's device, and the register — with everything
         *  configured during testing — survives. Distinct from `release`, which
         *  kills the code for good. */
        deregister: async (
            terminalId: string,
            tenantId: string,
            /** Replace wizard: also hand the register to another device (`null` clears the link). */
            opts?: { deviceUnitId?: string | null },
        ): Promise<any> => {
            const resp = await superAdminExt2Request(
                `/pos/terminals/${encodeURIComponent(terminalId)}/deregister?tenantId=${encodeURIComponent(tenantId)}`,
                opts && 'deviceUnitId' in opts
                    ? { method: 'POST', body: JSON.stringify({ deviceUnitId: opts.deviceUnitId ?? null }) }
                    : { method: 'POST' },
            );
            return parseBody(resp);
        },
        /** Super Admin: a fresh single-use activation code (14-day expiry) for an
         *  EXISTING till — same register, location and settings. The old code
         *  dies and the old device is signed out (till token version bump). */
        newCode: async (terminalId: string, tenantId: string): Promise<{
            terminalId: string; registerId: string; registerName?: string; locationId: string;
            code: string; expiresAt: string; reopened?: boolean; message?: string;
        }> => {
            const resp = await superAdminExt2Request(
                `/pos/terminals/${encodeURIComponent(terminalId)}/new-code?tenantId=${encodeURIComponent(tenantId)}`,
                { method: 'POST' },
            );
            return parseBody(resp) as any;
        },
        list: async (tenantId: string): Promise<any> => {
            const resp = await superAdminExt2Request(`/pos/terminals?tenantId=${encodeURIComponent(tenantId)}`);
            return parseBody(resp);
        },
        /* `deviceModelId`/`deviceUnitId` are additive and optional — the
           handler has always written them when present. They are what ties a
           PROVISION# row back to the physical box it was minted for. */
        provision: async (data: { tenantId: string; locationId: string; locationName?: string; registerName: string; terminalType: string; deviceModelId?: string; deviceUnitId?: string }): Promise<any> => {
            const resp = await superAdminExt2Request('/pos/terminals/provision', {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return parseBody(resp);
        },
        events: async (terminalId: string, limit = 50): Promise<any> => {
            const resp = await superAdminExt2Request(`/pos/terminals/${encodeURIComponent(terminalId)}/events?limit=${limit}`);
            return parseBody(resp);
        },
    },

    // ============================================
    // KIOSKS (kiosks-service) — generic kiosk framework, all types
    // ============================================
    kiosks: {
        provision: async (data: {
            tenantId: string;
            locationId: string;
            kioskType: string;
            name: string;
            description?: string;
            idlePlaylistId?: string | null;
            idleAfterSeconds?: number;
            resumeWindowMinutes?: number;
            resolution?: 'tablet' | '1080p' | '4k';
            orientation?: 'landscape' | 'portrait';
            screenSizeInches?: number;
            config?: Record<string, unknown>;
        }): Promise<any> => {
            return authedRequest(`${kiosksBaseUrl()}/kiosks/provision`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-tenant-id': data.tenantId },
                body: JSON.stringify(data),
            });
        },
        list: async (tenantId: string, opts?: { locationId?: string; kioskType?: string }): Promise<any> => {
            const qs = new URLSearchParams();
            if (opts?.locationId) qs.set('locationId', opts.locationId);
            if (opts?.kioskType) qs.set('kioskType', opts.kioskType);
            const url = `${kiosksBaseUrl()}/kiosks${qs.toString() ? `?${qs}` : ''}`;
            return authedRequest(url, { headers: { 'x-tenant-id': tenantId } });
        },
        get: async (kioskId: string, tenantId: string): Promise<any> => {
            return authedRequest(`${kiosksBaseUrl()}/kiosks/${encodeURIComponent(kioskId)}`, {
                headers: { 'x-tenant-id': tenantId },
            });
        },
        patch: async (kioskId: string, tenantId: string, patch: Record<string, unknown>): Promise<any> => {
            return authedRequest(`${kiosksBaseUrl()}/kiosks/${encodeURIComponent(kioskId)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'x-tenant-id': tenantId },
                body: JSON.stringify(patch),
            });
        },
        deregister: async (kioskId: string, tenantId: string): Promise<any> => {
            return authedRequest(`${kiosksBaseUrl()}/kiosks/${encodeURIComponent(kioskId)}/deregister`, {
                method: 'POST',
                headers: { 'x-tenant-id': tenantId },
            });
        },
        release: async (kioskId: string, tenantId: string): Promise<any> => {
            return authedRequest(`${kiosksBaseUrl()}/kiosks/${encodeURIComponent(kioskId)}/release`, {
                method: 'POST',
                headers: { 'x-tenant-id': tenantId },
            });
        },
        remove: async (kioskId: string, tenantId: string): Promise<any> => {
            return authedRequest(`${kiosksBaseUrl()}/kiosks/${encodeURIComponent(kioskId)}`, {
                method: 'DELETE',
                headers: { 'x-tenant-id': tenantId },
            });
        },
        broadcast: async (
            tenantId: string,
            data: { playlistId: string | null; scope: { all?: boolean; locationId?: string; kioskType?: string } },
        ): Promise<any> => {
            return authedRequest(`${kiosksBaseUrl()}/kiosks/broadcast`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-tenant-id': tenantId },
                body: JSON.stringify(data),
            });
        },
    },

    // ============================================
    // MARKETING DISPLAYS / ADVERTISING (pos-advertising-service)
    // ============================================
    advertising: {
        provisionDisplay: async (data: {
            tenantId: string;
            locationId: string;
            name: string;
            description?: string;
            resolution?: '1080p' | '4k';
            orientation?: 'landscape' | 'portrait';
        }): Promise<any> => {
            return authedRequest(`${advertisingBaseUrl()}/marketing-displays/provision`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-tenant-id': data.tenantId },
                body: JSON.stringify(data),
            });
        },
        listDisplays: async (tenantId: string): Promise<any> => {
            return authedRequest(`${advertisingBaseUrl()}/marketing-displays?tenantId=${encodeURIComponent(tenantId)}`, {
                headers: { 'x-tenant-id': tenantId },
            });
        },
        getDisplay: async (displayId: string, tenantId: string): Promise<any> => {
            return authedRequest(`${advertisingBaseUrl()}/marketing-displays/${encodeURIComponent(displayId)}?tenantId=${encodeURIComponent(tenantId)}`, {
                headers: { 'x-tenant-id': tenantId },
            });
        },
        updateDisplay: async (displayId: string, tenantId: string, patch: any): Promise<any> => {
            return authedRequest(`${advertisingBaseUrl()}/marketing-displays/${encodeURIComponent(displayId)}?tenantId=${encodeURIComponent(tenantId)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'x-tenant-id': tenantId },
                body: JSON.stringify(patch),
            });
        },
        releaseDisplay: async (displayId: string, tenantId: string): Promise<any> => {
            return authedRequest(`${advertisingBaseUrl()}/marketing-displays/${encodeURIComponent(displayId)}/release?tenantId=${encodeURIComponent(tenantId)}`, {
                method: 'POST',
                headers: { 'x-tenant-id': tenantId },
            });
        },
        deleteDisplay: async (displayId: string, tenantId: string): Promise<any> => {
            return authedRequest(`${advertisingBaseUrl()}/marketing-displays/${encodeURIComponent(displayId)}?tenantId=${encodeURIComponent(tenantId)}`, {
                method: 'DELETE',
                headers: { 'x-tenant-id': tenantId },
            });
        },
    },
};

// ---------------------------------------------------------------------------
// Advertising (pos-advertising-service) base URL helper
// ---------------------------------------------------------------------------
function advertisingBaseUrl(): string {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const base = hostname.includes('dev.') || hostname === 'localhost' ? 'dev.rubytill.com' : 'rubytill.com';
    return `https://advertising.api.${base}`;
}

// ---------------------------------------------------------------------------
// Kiosks (kiosks-service) base URL helper
// ---------------------------------------------------------------------------
function kiosksBaseUrl(): string {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const base = hostname.includes('dev.') || hostname === 'localhost' ? 'dev.rubytill.com' : 'rubytill.com';
    return `https://kiosks.api.${base}`;
}
